import React, { lazy, Suspense, useMemo, useRef, useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { toast, Toaster } from 'react-hot-toast';
import Cookies from 'js-cookie';

// Import theme context
import { ThemeProvider, ThemeToggle, useTheme } from './contexts/ThemeContext';

// Import shadcn components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Separator } from './components/ui/separator';

// Icons
import { 
  Sparkles, Target, MessageSquare, Calendar, CheckCircle, CheckCircle2, Plus, Brain, Zap, Users, TrendingUp,
  ArrowRight, Menu, X, Lock, User, Building2, Crown, Shield, UserCheck, RefreshCw, Clock,
  LogOut, Settings, Mail, Eye, EyeOff, BarChart3, Moon, Sun, Monitor, Bell, Send, Phone, MapPin, ShieldCheck, Key,
  FolderOpen, Trash2, Edit3, Search, UserPlus, Users2, ChevronDown, ChevronUp, AlertTriangle, AlertCircle,
  Upload, Download, Copy
} from 'lucide-react';

// Import notifications panel
import NotificationsPanel from './components/NotificationsPanel';
import PresenceBar from './components/PresenceBar';
import SyncStateBadge from './components/SyncStateBadge';
import TypingIndicator from './components/TypingIndicator';
import ModernKanban from './components/ModernKanban';
import AICopilotPanel from './components/AICopilotPanel';
import GlobalCommandPalette from './components/GlobalCommandPalette';
import ActivityFeed from './components/ActivityFeed';
import AppShell from './components/layout/AppShell';
import AppErrorBoundary from './components/AppErrorBoundary';
import TaskDetailModal from './components/TaskDetailModal';
import TaskListView from './components/TaskListView';
import SprintPanel from './components/SprintPanel';
import GanttView from './components/GanttView';
import { EmptyState } from './components/ui/empty-state';
import { Skeleton } from './components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './components/ui/sheet';
import { useRealtimeProject } from './hooks/useRealtimeProject';
import { uiMotion } from './lib/ui-motion';

import { getApiUrl, getBackendUrl } from './utils/config';

const AnalyticsPage = lazy(() => import('./components/AnalyticsPage'));
const UserSettings = lazy(() => import('./components/UserSettings'));
const EnterpriseConsole = lazy(() => import('./components/EnterpriseConsole'));
const IntegrationsPage = lazy(() => import('./components/IntegrationsPage'));


// ==================== AUTH CONTEXT ====================
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const getApiErrorMessage = (error, fallback = 'Request failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return first.msg;
  }
  if (typeof detail === 'object' && detail?.message) {
    return detail.message;
  }
  return fallback;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());

  // Setup axios interceptor for auth
  useEffect(() => {
    // Single-flight refresh guard — prevents the infinite /auth/refresh loop
    let isRefreshing = false;
    let refreshSubscribers = [];           // queued requests waiting for a new token
    const onRefreshed = (token) => { refreshSubscribers.forEach((cb) => cb(token)); refreshSubscribers = []; };
    const onRefreshFailed = () => { refreshSubscribers.forEach((cb) => cb(null)); refreshSubscribers = []; };

    const isAuthEndpoint = (url = '') =>
      url.includes('/auth/refresh') || url.includes('/auth/login') ||
      url.includes('/auth/register') || url.includes('/auth/verify');

    // Request interceptor: Add token to every request
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = Cookies.get('access_token');
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const status = error.response?.status;

        // Render can occasionally return a gateway error (cold start) without app CORS headers.
        // Retry safe GETs and the one-shot signup/login steps once or twice before surfacing an error,
        // since each of these checks existing state before mutating (safe to retry on a transient failure).
        const RETRYABLE_POST_PATHS = [
          '/auth/login', '/auth/register', '/auth/verify-email', '/auth/resend-verification-otp',
          '/auth/create-workspace', '/auth/verify-2fa', '/auth/forgot-password', '/auth/reset-password',
        ];
        const transientRetries = originalRequest._transientRetries || 0;
        const canRetryTransient =
          originalRequest?.method === 'get' ||
          (originalRequest?.method === 'post' && RETRYABLE_POST_PATHS.some(p => originalRequest.url?.includes(p)));
        const isCanceled = error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
        const isTransient = !isCanceled && (!error.response || status >= 500);
        if (canRetryTransient && isTransient && transientRetries < 2) {
          originalRequest._transientRetries = transientRetries + 1;
          await new Promise((resolve) => setTimeout(resolve, 450 * originalRequest._transientRetries));
          return axios(originalRequest);
        }

        // 401 on an auth endpoint itself (refresh/login/etc) → never loop, just fail/logout
        if (status === 401 && isAuthEndpoint(originalRequest.url)) {
          if (originalRequest.url?.includes('/auth/refresh')) logout();
          return Promise.reject(error);
        }

        // 401 on a normal request → try a single shared refresh
        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = Cookies.get('refresh_token');
          if (!refreshToken) { logout(); return Promise.reject(error); }

          // If a refresh is already in flight, queue this request until it resolves
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              refreshSubscribers.push((newToken) => {
                if (!newToken) { reject(error); return; }
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(axios(originalRequest));
              });
            });
          }

          isRefreshing = true;
          try {
            const response = await axios.post(getApiUrl('/auth/refresh'),
              { refresh_token: refreshToken },
              { headers: { Authorization: undefined } }
            );
            const { access_token, refresh_token: newRefreshToken } = response.data;
            Cookies.set('access_token', access_token, { expires: 1, secure: true, sameSite: 'strict' });
            if (newRefreshToken) Cookies.set('refresh_token', newRefreshToken, { expires: 7, secure: true, sameSite: 'strict' });
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            onRefreshed(access_token);
            return axios(originalRequest);
          } catch (refreshError) {
            onRefreshFailed();
            logout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    const token = Cookies.get('access_token');
    if (token) {
      // Set initial auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
    // Note: fetchUserInfo is intentionally not in deps - should run once on mount
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = Cookies.get('access_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(getApiUrl('/me'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(response.data.user);
      setOrganizations(response.data.organizations);
      if (response.data.organizations.length > 0) {
        setCurrentOrg(prev => {
          const same = response.data.organizations.find(o => o.id === prev?.id);
          return same || response.data.organizations[0];
        });
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 404) {
        logout();
      } else {
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Expose a lightweight refresh so components can call it after role changes
  const checkAuth = fetchUserInfo;

  // Personal WebSocket channel — role/org changes + workspace-level presence
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let attempts = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const token = Cookies.get('access_token');
      const userId = user?.id;
      if (!token || !userId) return;
      const wsBase = getBackendUrl().replace(/\/+$/, '').replace(/^http/, 'ws');
      ws = new WebSocket(`${wsBase}/ws/user/${userId}?token=${token}`);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.event_type === 'role.changed') {
            fetchUserInfo();
            toast(`Your role was updated to ${data.new_role.replace(/_/g, ' ').toLowerCase()}`, { icon: '🔔' });
          } else if (data.event_type === 'user.online') {
            setOnlineUserIds(prev => new Set([...prev, data.user_id]));
          } else if (data.event_type === 'user.offline') {
            setOnlineUserIds(prev => { const next = new Set(prev); next.delete(data.user_id); return next; });
          }
        } catch (_) {}
      };
      ws.onerror = () => {};
      ws.onopen = () => { attempts = 0; };
      ws.onclose = (e) => {
        // 4001 = bad token, 4003 = forbidden, 1000 = intentional close — don't reconnect
        if (stopped || e.code === 4001 || e.code === 4003 || e.code === 1000) return;
        attempts++;
        const delay = Math.min(1000 * Math.pow(2, attempts - 1), 30000);
        reconnectTimer = setTimeout(connect, delay);
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      try { ws?.close(1000); } catch (_) {}
    };
  }, [user?.id]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(getApiUrl('/auth/login'), { email, password });
      const data = response.data;

      // 2FA required — backend sends OTP, we need the second step
      if (data.requires_2fa) {
        return { requires2fa: true, email: data.email };
      }

      const { access_token, refresh_token, user, organizations } = data;
      Cookies.set('access_token', access_token, { expires: 1, secure: true, sameSite: 'strict' });
      Cookies.set('refresh_token', refresh_token, { expires: 7, secure: true, sameSite: 'strict' });
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setOrganizations(organizations);
      if (organizations.length > 0) setCurrentOrg(organizations[0]);
      toast.success('Login successful!');
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'EMAIL_NOT_VERIFIED') {
        return { requiresVerification: true, email };
      }
      toast.error(getApiErrorMessage(error, 'Login failed'));
      return false;
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await axios.post(getApiUrl('/auth/register'), { email, password, full_name: fullName });
      // Backend returns requires_verification=false when SKIP_EMAIL_VERIFICATION=true
      // (dev/E2E only — always true in production, see render.yaml, since that
      // env var is never set there). Previously hardcoded to true, which broke
      // registration entirely in any environment with email verification skipped.
      return { requiresVerification: response.data?.requires_verification !== false, email };
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Registration failed'));
      return false;
    }
  };

  const verifyEmail = async (email, otp) => {
    try {
      const response = await axios.post(getApiUrl('/auth/verify-email'), { email, otp });
      const { access_token, refresh_token, user, organizations, needs_workspace } = response.data;
      Cookies.set('access_token', access_token, { expires: 1, secure: true, sameSite: 'strict' });
      Cookies.set('refresh_token', refresh_token, { expires: 7, secure: true, sameSite: 'strict' });
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setOrganizations(organizations);
      if (organizations.length > 0) setCurrentOrg(organizations[0]);
      toast.success('Email verified! Welcome to SyncWork 🎉');
      return { ok: true, needs_workspace };
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Invalid or expired code'));
      return false;
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(getApiUrl('/auth/forgot-password'), { email });
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send reset code'));
      return false;
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    try {
      await axios.post(getApiUrl('/auth/reset-password'), { email, otp, new_password: newPassword });
      toast.success('Password reset successfully! You can now log in.');
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Invalid or expired code'));
      return false;
    }
  };

  const updateUser = async (userData) => {
    try {
      const response = await axios.patch(getApiUrl('/me'), userData);
      setUser(response.data);
      toast.success('Profile updated successfully');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      return false;
    }
  };

  const updateOrganization = (orgId, updatedFields) => {
    setOrganizations(prev =>
      prev.map(org => org.id === orgId ? { ...org, ...updatedFields } : org)
    );
    setCurrentOrg(prev =>
      prev?.id === orgId ? { ...prev, ...updatedFields } : prev
    );
  };

  const logout = async () => {
    const refreshToken = Cookies.get('refresh_token');
    try {
      await axios.post(getApiUrl('/auth/logout'), { refresh_token: refreshToken || null });
    } catch (_) { /* ignore — always clear local state */ }
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setOrganizations([]);
    setCurrentOrg(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    organizations,
    setOrganizations,
    currentOrg,
    setCurrentOrg,
    login,
    register,
    verifyEmail,
    forgotPassword,
    resetPassword,
    updateUser,
    updateOrganization,
    logout,
    loading,
    checkAuth,
    onlineUserIds,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ==================== PROTECTED ROUTE COMPONENT ====================
const ProtectedRoute = ({ children }) => {
  const { user, organizations, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-3">
          <Skeleton variant="title" />
          <Skeleton variant="text" />
          <Skeleton variant="card" className="h-24" />
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Logged in but no workspace yet → must create one first
  if (organizations && organizations.length === 0) {
    return <Navigate to="/create-workspace" replace />;
  }

  return children;
};

const RouteFallback = () => (
  <div className="min-h-screen px-4 py-10">
    <div className="mx-auto max-w-5xl space-y-4">
      <Skeleton variant="title" className="max-w-xs" />
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton variant="card" />
        <Skeleton variant="card" />
      </div>
    </div>
  </div>
);

const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <AppShell
      header={<DashboardHeader />}
    >
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <EmptyState
          icon={Target}
          title="Page not found"
          description="The page you requested does not exist or may have moved."
          actionLabel="Back to dashboard"
          onAction={() => navigate("/dashboard")}
        />
      </div>
    </AppShell>
  );
};

// ==================== AUTH PAGE SHARED COMPONENTS ====================
// Defined OUTSIDE AuthPage so React doesn't recreate them on every keystroke.

const AuthLogo = () => (
  <div className="text-center mb-8">
    <div className="flex justify-center mb-4">
      <img src="/syncwork.svg" alt="SyncWork" className="h-20 w-auto object-contain max-w-[320px] mx-auto" />
    </div>
    <p className="text-gray-600">Transform your project planning with AI</p>
  </div>
);

const AuthSpinner = ({ label }) => (
  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />{label}</>
);

const PasswordInput = ({ value, onChange, placeholder = 'Enter password', required = true, show, onToggle }) => (
  <div className="relative">
    <Input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="pr-10"
      required={required}
    />
    <button type="button" aria-label={show ? 'Hide password' : 'Show password'} className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={onToggle}>
      {show ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
    </button>
  </div>
);

// ==================== AUTH PAGE (Login / Register / OTP / Forgot Password) ====================
const AuthPage = () => {
  // step: 'login' | 'login-2fa' | 'register' | 'verify-email' | 'forgot-email' | 'forgot-otp' | 'forgot-newpw'
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const [step, setStep] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [fullName, setFullName]     = useState('');
  const [otp, setOtp]               = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]       = useState(false);

  const { login, register, verifyEmail, forgotPassword, resetPassword, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result === true) {
      navigate('/dashboard');
    } else if (result?.requiresVerification) {
      setEmail(result.email);
      setStep('verify-email');
    } else if (result?.requires2fa) {
      setEmail(result.email);
      setOtp('');
      setStep('login-2fa');
    }
  };

  // ── 2FA OTP verify (login) ─────────────────────────────────────────────────
  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl('/auth/verify-2fa'), { email, otp });
      const { access_token, refresh_token, user: u, organizations } = res.data;
      Cookies.set('access_token', access_token, { expires: 1, secure: true, sameSite: 'strict' });
      Cookies.set('refresh_token', refresh_token, { expires: 7, secure: true, sameSite: 'strict' });
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      // update auth context via a page reload — cleanest approach
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    }
    setLoading(false);
  };

  // ── Register ───────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(email, password, fullName);
    if (result?.requiresVerification) {
      setLoading(false);
      setStep('verify-email');
    } else if (result) {
      // SKIP_EMAIL_VERIFICATION=true (dev/E2E only, see register()'s comment) —
      // the account is already active but /auth/register issues no tokens, so
      // log in immediately to complete the flow instead of leaving the user
      // stuck on the register form with no navigation at all.
      const loginResult = await login(email, password);
      setLoading(false);
      if (loginResult === true) {
        navigate('/dashboard');
      }
    } else {
      setLoading(false);
    }
  };

  // ── Verify email OTP ───────────────────────────────────────────────────────
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await verifyEmail(email, otp);
    setLoading(false);
    if (result?.ok) {
      if (result.needs_workspace) {
        navigate('/create-workspace');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    await axios.post(getApiUrl('/auth/resend-verification-otp'), { email }).catch(() => {});
    toast.success('New code sent! Check your inbox.');
    setLoading(false);
  };

  // ── Forgot password ────────────────────────────────────────────────────────
  const handleForgotEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await forgotPassword(email);
    setLoading(false);
    if (ok) { toast.success('Reset code sent! Check your inbox.'); setStep('forgot-otp'); }
  };

  const handleForgotOtp = async (e) => {
    e.preventDefault();
    // Just move to new-password screen — OTP is verified when we submit the new password
    setStep('forgot-newpw');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    const ok = await resetPassword(email, otp, newPassword);
    setLoading(false);
    if (ok) setStep('login');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AuthLogo />

        {/* ── LOGIN ── */}
        {step === 'login' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Welcome Back</CardTitle>
              <CardDescription>Sign in to your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Password</label>
                    <button type="button" onClick={() => setStep('forgot-email')}
                      className="text-xs text-[#4B1E87] hover:text-[#693CB4]">Forgot password?</button>
                  </div>
                  <PasswordInput value={password} onChange={e => setPassword(e.target.value)} show={showPassword} onToggle={() => setShowPassword(p => !p)} />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={loading}>
                  {loading ? <AuthSpinner label="Signing In…" /> : <><Lock className="w-4 h-4 mr-2" />Sign In</>}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button type="button" onClick={() => setStep('register')} className="text-[#4B1E87] hover:text-[#693CB4] text-sm">
                  Don't have an account? Sign up
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── REGISTER ── */}
        {step === 'register' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Create Account</CardTitle>
              <CardDescription>Sign up — we'll email you a verification code</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <Input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Smith" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" show={showPassword} onToggle={() => setShowPassword(p => !p)} />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={loading}>
                  {loading ? <AuthSpinner label="Creating Account…" /> : <><Mail className="w-4 h-4 mr-2" />Create Account</>}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button type="button" onClick={() => setStep('login')} className="text-[#4B1E87] hover:text-[#693CB4] text-sm">
                  Already have an account? Sign in
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── 2FA OTP STEP ── */}
        {step === 'login-2fa' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Two-Factor Verification</CardTitle>
              <CardDescription>Enter the 6-digit code sent to <strong>{email}</strong></CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify2fa} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                  <Input
                    type="text" inputMode="numeric" autoFocus
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6} required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4]" disabled={loading || otp.length !== 6}>
                  {loading ? <AuthSpinner label="Verifying…" /> : <><Lock className="w-4 h-4 mr-2" />Verify & Sign In</>}
                </Button>
              </form>
              <p className="text-center text-xs text-gray-400 mt-4">Didn't receive it? Check your spam folder.</p>
              <div className="mt-4 text-center">
                <button type="button" onClick={() => setStep('login')} className="text-[#4B1E87] text-sm hover:underline">
                  ← Back to sign in
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── VERIFY EMAIL OTP ── */}
        {step === 'verify-email' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Verify Your Email</CardTitle>
              <CardDescription>Enter the 6-digit code we sent to <strong>{email}</strong></CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyEmail} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="text-center text-2xl tracking-widest font-bold"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={loading || otp.length !== 6}>
                  {loading ? <AuthSpinner label="Verifying…" /> : <><ShieldCheck className="w-4 h-4 mr-2" />Verify &amp; Continue</>}
                </Button>
              </form>
              <div className="mt-4 text-center space-y-2">
                <button type="button" onClick={handleResendOtp} disabled={loading} className="text-[#4B1E87] hover:text-[#693CB4] text-sm">
                  Didn't receive it? Resend code
                </button>
                <br />
                <button type="button" onClick={() => setStep('login')} className="text-gray-500 hover:text-gray-700 text-xs">
                  ← Back to login
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── FORGOT PASSWORD — enter email ── */}
        {step === 'forgot-email' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>We'll send a reset code to your email</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotEmail} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={loading}>
                  {loading ? <AuthSpinner label="Sending Code…" /> : <><Send className="w-4 h-4 mr-2" />Send Reset Code</>}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button type="button" onClick={() => setStep('login')} className="text-[#4B1E87] hover:text-[#693CB4] text-sm">
                  ← Back to login
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── FORGOT PASSWORD — enter OTP ── */}
        {step === 'forgot-otp' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Enter Reset Code</CardTitle>
              <CardDescription>Check <strong>{email}</strong> for your 6-digit code</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reset Code</label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="text-center text-2xl tracking-widest font-bold"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={otp.length !== 6}>
                  Next →
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button type="button" onClick={() => { setStep('forgot-email'); setOtp(''); }} className="text-[#4B1E87] hover:text-[#693CB4] text-sm">
                  Resend code
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── FORGOT PASSWORD — new password ── */}
        {step === 'forgot-newpw' && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>Choose a strong password for your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <PasswordInput value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" show={showPassword} onToggle={() => setShowPassword(p => !p)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" required />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]" disabled={loading}>
                  {loading ? <AuthSpinner label="Resetting…" /> : <><Key className="w-4 h-4 mr-2" />Reset Password</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ==================== LANDING PAGE ====================
// ── Shared nav component used by all marketing pages ─────────────────────────
const MarketingNav = ({ activePage = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const links = [
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/demo', label: 'Contact' },
  ];
  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img src="/syncwork.svg" alt="SyncWork" className="h-20 w-auto object-contain max-w-[300px]" />
          </Link>
          <div className="hidden md:flex items-center space-x-6">
            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`transition-colors text-sm font-medium ${activePage === l.to ? 'text-[#4B1E87]' : 'text-gray-600 hover:text-[#4B1E87]'}`}>
                {l.label}
              </Link>
            ))}
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4]">Dashboard</Button>
            ) : (
              <>
                <Button onClick={() => navigate('/auth')} variant="outline" size="sm">Sign In</Button>
                <Button onClick={() => navigate('/auth?tab=register')} size="sm" className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4]">Get Started</Button>
              </>
            )}
          </div>
          <Button variant="ghost" size="sm" className="md:hidden text-gray-900 hover:bg-gray-100" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t px-4 py-3 space-y-2">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-gray-700 py-1">{l.label}</Link>
          ))}
          <Button onClick={() => navigate('/auth?tab=register')} className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] mt-2">Get Started</Button>
        </div>
      )}
    </nav>
  );
};

// ==================== SHARED LEGAL PAGE SHELL ====================
const LegalPage = ({ title, lastUpdated, children }) => (
  <div className="min-h-screen bg-gray-50">
    {/* Nav */}
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
        <Link to="/" className="flex items-center">
          <img src="/syncwork.svg" alt="SyncWork" className="h-10 w-auto object-contain max-w-[180px]" />
        </Link>
        <span className="text-gray-300">|</span>
        <span className="text-gray-600 text-sm">{title}</span>
      </div>
    </nav>

    {/* Content */}
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
      <p className="text-sm text-gray-400 mb-10">Last updated: {lastUpdated}</p>
      <div className="space-y-8 text-gray-700 text-sm leading-relaxed">{children}</div>
    </div>

    {/* Footer */}
    <footer className="border-t border-gray-200 bg-white mt-16 py-8">
      <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
        <span>© 2026 SyncWork. All rights reserved.</span>
        <div className="flex gap-6">
          <Link to="/privacy" className="hover:text-gray-700 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-700 transition-colors">Terms of Service</Link>
          <a href="mailto:info@syncwork.pro" className="hover:text-gray-700 transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  </div>
);

const LegalSection = ({ title, children }) => (
  <div>
    <h2 className="text-base font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);

// ==================== PRIVACY POLICY PAGE ====================
const PrivacyPolicyPage = () => (
  <LegalPage title="Privacy Policy" lastUpdated="June 23, 2026">
    <LegalSection title="1. Introduction">
      <p>SyncWork ("we", "us", or "our") operates syncwork.pro (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. Please read it carefully.</p>
      <p>By using SyncWork, you agree to the collection and use of information in accordance with this policy.</p>
    </LegalSection>

    <LegalSection title="2. Information We Collect">
      <p><strong>Account Information:</strong> When you register, we collect your full name, email address, and password (stored as a bcrypt hash — never in plain text).</p>
      <p><strong>Profile Data:</strong> Optionally, you may upload a profile photo, which is stored as a base64 image in our database.</p>
      <p><strong>Usage Data:</strong> We collect information on how you interact with the Service — pages visited, actions taken (task created, sprint started, etc.), timestamps, and IP addresses. This is recorded in our Audit Log.</p>
      <p><strong>Content You Create:</strong> Tasks, comments, descriptions, file attachments, sprint plans, and other content you create are stored and associated with your account and organization.</p>
      <p><strong>Communication Data:</strong> If you contact us via the support form, we store your name, email, and message content.</p>
    </LegalSection>

    <LegalSection title="3. How We Use Your Information">
      <p>We use collected information to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Provide, maintain, and improve the Service</li>
        <li>Authenticate your identity and secure your account</li>
        <li>Send transactional emails (verification, password reset, 2FA codes, task notifications)</li>
        <li>Send product updates and newsletters (you can opt out at any time)</li>
        <li>Respond to support requests</li>
        <li>Monitor for security incidents and fraud</li>
        <li>Generate anonymized analytics to improve features</li>
      </ul>
    </LegalSection>

    <LegalSection title="4. Data Storage & Security">
      <p><strong>Database:</strong> All data is stored in MongoDB Atlas (cloud-hosted). Databases are encrypted at rest and in transit (TLS 1.2+).</p>
      <p><strong>Passwords:</strong> Passwords are hashed using bcrypt before storage. We never store or transmit plain-text passwords.</p>
      <p><strong>API Keys:</strong> API keys are shown only once at creation. Only a SHA-256 hash is stored — we cannot recover a key after it is shown.</p>
      <p><strong>File Attachments:</strong> Uploaded files are stored securely on our servers. We do not share attachment content with third parties.</p>
      <p><strong>Two-Factor Authentication:</strong> One-time codes are generated per session, expire within 10 minutes, and are never reused.</p>
    </LegalSection>

    <LegalSection title="5. Data Sharing & Third Parties">
      <p>We do not sell, rent, or trade your personal information. We may share data only in these limited circumstances:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Service Providers:</strong> We use MongoDB Atlas (database), SendGrid/SMTP (email delivery), and cloud hosting providers. These receive only the data necessary to perform their services.</li>
        <li><strong>AI Providers:</strong> If AI features are enabled, task descriptions and goals may be sent to OpenAI, Anthropic, or Google Gemini for processing. No personal account information is included in AI prompts.</li>
        <li><strong>Integrations you configure:</strong> Slack webhooks and GitHub webhooks are configured by you. We send event data to the URLs you provide.</li>
        <li><strong>Legal Requirements:</strong> We may disclose data if required by law, court order, or to protect the rights and safety of our users.</li>
      </ul>
    </LegalSection>

    <LegalSection title="6. Cookies & Local Storage">
      <p>SyncWork uses:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Cookies:</strong> Your authentication tokens are stored in browser cookies with <code>Secure</code> and <code>SameSite=Strict</code> flags. They expire when you sign out or the token reaches its expiry time.</li>
        <li><strong>Local Storage:</strong> We store your theme preference, recent command palette actions, and onboarding completion status in browser local storage. This data never leaves your device.</li>
      </ul>
      <p>We do not use advertising cookies or cross-site tracking.</p>
    </LegalSection>

    <LegalSection title="7. Data Retention">
      <p>We retain your account data for as long as your account is active. If you delete your account or organization, your personal data is deleted within 30 days. Some data (audit logs, billing records) may be retained for up to 7 years for compliance purposes.</p>
    </LegalSection>

    <LegalSection title="8. Your Rights">
      <p>Depending on your location, you may have rights to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Access the personal data we hold about you</li>
        <li>Correct inaccurate data</li>
        <li>Request deletion of your data ("right to be forgotten")</li>
        <li>Export your data in a portable format</li>
        <li>Opt out of marketing emails</li>
      </ul>
      <p>To exercise any of these rights, contact us at <a href="mailto:info@syncwork.pro" className="text-[#4B1E87] hover:underline">info@syncwork.pro</a>.</p>
    </LegalSection>

    <LegalSection title="9. Children's Privacy">
      <p>SyncWork is not directed at children under 16. We do not knowingly collect personal information from children. If you believe a child has provided us data, please contact us and we will delete it promptly.</p>
    </LegalSection>

    <LegalSection title="10. Changes to This Policy">
      <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or by a notice in the app. Continued use of the Service after changes constitutes acceptance of the updated policy.</p>
    </LegalSection>

    <LegalSection title="11. Contact Us">
      <p>For any privacy-related questions or requests:</p>
      <ul className="list-none space-y-1">
        <li>📧 <a href="mailto:info@syncwork.pro" className="text-[#4B1E87] hover:underline">info@syncwork.pro</a></li>
        <li>🌐 syncwork.pro</li>
      </ul>
    </LegalSection>
  </LegalPage>
);

// ==================== TERMS OF SERVICE PAGE ====================
const TermsOfServicePage = () => (
  <LegalPage title="Terms of Service" lastUpdated="June 23, 2026">
    <LegalSection title="1. Agreement to Terms">
      <p>By accessing or using SyncWork ("the Service") at syncwork.pro, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you may not use the Service.</p>
      <p>These Terms apply to all visitors, users, and organizations that access or use the Service.</p>
    </LegalSection>

    <LegalSection title="2. Description of Service">
      <p>SyncWork is an AI-powered project management platform that allows teams to plan, track, and collaborate on work. Features include Kanban boards, sprint management, AI planning tools, integrations with third-party services, and analytics.</p>
    </LegalSection>

    <LegalSection title="3. Account Registration">
      <p>To use SyncWork, you must create an account with a valid email address and password. You are responsible for:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activity that occurs under your account</li>
        <li>Notifying us immediately of any unauthorized use at <a href="mailto:info@syncwork.pro" className="text-[#4B1E87] hover:underline">info@syncwork.pro</a></li>
      </ul>
      <p>You must be at least 16 years old to create an account.</p>
    </LegalSection>

    <LegalSection title="4. Acceptable Use">
      <p>You agree not to use SyncWork to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Violate any applicable laws or regulations</li>
        <li>Upload or share malicious code, viruses, or harmful content</li>
        <li>Attempt to gain unauthorized access to other accounts or systems</li>
        <li>Scrape, crawl, or extract data from the Service without written permission</li>
        <li>Resell or sublicense access to the Service</li>
        <li>Harass, abuse, or harm other users</li>
        <li>Use the AI features to generate illegal, harmful, or deceptive content</li>
      </ul>
    </LegalSection>

    <LegalSection title="5. Organizations & Teams">
      <p>SyncWork is organized around "Organizations" (workspaces). The person who creates an organization is the Owner. Owners can invite team members with different roles (Admin, Member).</p>
      <p>By adding members to your organization, you represent that you have the right to share the organization's data with those individuals. You are responsible for ensuring members comply with these Terms.</p>
    </LegalSection>

    <LegalSection title="6. Your Content">
      <p>You retain ownership of all content you upload or create in SyncWork ("Your Content"), including tasks, descriptions, attachments, and comments.</p>
      <p>By using the Service, you grant SyncWork a limited, non-exclusive license to store, process, and display Your Content solely to provide the Service to you.</p>
      <p>You represent that you have all rights necessary to grant this license and that Your Content does not violate any third-party rights.</p>
    </LegalSection>

    <LegalSection title="7. Third-Party Integrations">
      <p>SyncWork integrates with third-party services including Slack, GitHub, OpenAI, Anthropic, and Google Gemini. Your use of these integrations is subject to the respective third-party terms of service.</p>
      <p>SyncWork is not responsible for the availability, accuracy, or content of third-party services.</p>
    </LegalSection>

    <LegalSection title="8. API Keys & Programmatic Access">
      <p>If you create API keys to access SyncWork programmatically, you are responsible for keeping those keys secure. API keys grant access to your organization's data. Revoke keys immediately if you suspect they have been compromised.</p>
      <p>SyncWork is not liable for unauthorized access resulting from leaked or stolen API keys.</p>
    </LegalSection>

    <LegalSection title="9. Availability & Uptime">
      <p>We strive to keep SyncWork available 24/7 but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or events beyond our control.</p>
      <p>The free tier of SyncWork is hosted on Render's free plan, which may have cold-start delays of 30–60 seconds after periods of inactivity.</p>
    </LegalSection>

    <LegalSection title="10. Pricing & Payments">
      <p>SyncWork offers free and paid plans. Paid plan pricing is shown on the Pricing page. By subscribing to a paid plan, you agree to pay the applicable fees.</p>
      <p>Subscriptions renew automatically unless cancelled before the renewal date. Refunds are handled on a case-by-case basis — contact us within 7 days of a charge to request a refund.</p>
    </LegalSection>

    <LegalSection title="11. Termination">
      <p>You may delete your account at any time from Settings → Workspace → Danger Zone.</p>
      <p>We may suspend or terminate your account if you violate these Terms, with or without notice. Upon termination, your access to the Service will cease and your data will be deleted per our data retention policy.</p>
    </LegalSection>

    <LegalSection title="12. Disclaimer of Warranties">
      <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied. SyncWork does not warrant that the Service will be error-free, secure, or uninterrupted.</p>
      <p>AI-generated content (sprint plans, task breakdowns, comment rewrites) is provided for informational purposes only. You are responsible for reviewing and validating all AI output before acting on it.</p>
    </LegalSection>

    <LegalSection title="13. Limitation of Liability">
      <p>To the maximum extent permitted by law, SyncWork and its team shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including loss of data, profits, or business opportunities.</p>
      <p>Our total liability to you for any claims arising from these Terms or your use of the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.</p>
    </LegalSection>

    <LegalSection title="14. Changes to Terms">
      <p>We may update these Terms at any time. We will notify you of significant changes by email or in-app notice at least 14 days before they take effect. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
    </LegalSection>

    <LegalSection title="15. Governing Law">
      <p>These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of India. If any provision of these Terms is found unenforceable, the remaining provisions continue in full force.</p>
    </LegalSection>

    <LegalSection title="16. Contact">
      <p>Questions about these Terms? Reach us at:</p>
      <ul className="list-none space-y-1">
        <li>📧 <a href="mailto:info@syncwork.pro" className="text-[#4B1E87] hover:underline">info@syncwork.pro</a></li>
        <li>🌐 syncwork.pro</li>
      </ul>
    </LegalSection>
  </LegalPage>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const allFeatures = [
    { icon: <Brain className="w-7 h-7" />, color: "from-[#4B1E87] to-[#693CB4]", title: "AI Sprint Planning", desc: "AI analyzes your backlog, team capacity and dependencies to generate optimal sprint plans automatically." },
    { icon: <Target className="w-7 h-7" />, color: "from-emerald-500 to-teal-500", title: "Goal → Task Breakdown", desc: "Turn a high-level goal into a full task list with estimates, owners, and acceptance criteria in seconds." },
    { icon: <MessageSquare className="w-7 h-7" />, color: "from-blue-500 to-cyan-500", title: "Smart Comment Assistant", desc: "Convert vague feedback into structured action items with assigned owners and deadlines." },
    { icon: <Shield className="w-7 h-7" />, color: "from-rose-500 to-pink-500", title: "Enterprise RBAC", desc: "7-role hierarchy — Owner, Admin, Project Manager, Scrum Master, Team Lead, Developer, Viewer. Every permission enforced at the API." },
    { icon: <CheckCircle2 className="w-7 h-7" />, color: "from-amber-500 to-orange-500", title: "Two-Stage Approval", desc: "Developer submits → Team Lead reviews → Project Manager approves. Full audit trail with approver name and timestamp." },
    { icon: <BarChart3 className="w-7 h-7" />, color: "from-indigo-500 to-blue-500", title: "Analytics Dashboard", desc: "Velocity, burndown, AI cost savings, team workload, and activity heatmaps. Export to CSV." },
    { icon: <Zap className="w-7 h-7" />, color: "from-yellow-500 to-amber-500", title: "Real-Time Collaboration", desc: "WebSocket-powered live updates. Every task move, comment, and assignment synced instantly across all team members." },
    { icon: <Building2 className="w-7 h-7" />, color: "from-teal-500 to-emerald-500", title: "Multi-Tenant Workspaces", desc: "Any company creates their own isolated workspace. Full data separation. One platform serves unlimited organizations." },
    { icon: <Bell className="w-7 h-7" />, color: "from-purple-500 to-violet-500", title: "Smart Notifications", desc: "In-app and email alerts for task assignments, approvals, rejections, and mentions. Configurable per user." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <MarketingNav activePage="/" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0d0618] via-[#1a0833] to-[#2a0f52] py-24 lg:py-36">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-500/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#4B1E87]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#693CB4]/20 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#f3eeff]0/5 rounded-full blur-3xl" />
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'linear-gradient(rgba(99,102,241,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.3) 1px, transparent 1px)', backgroundSize:'60px 60px'}} />
        {/* Floating dots */}
        <div className="absolute top-32 right-1/4 w-3 h-3 bg-indigo-400 rounded-full opacity-60 animate-bounce" />
        <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-purple-400 rounded-full opacity-60 animate-bounce" style={{animationDelay:'0.5s'}} />
        <div className="absolute top-1/2 right-16 w-2 h-2 bg-teal-400 rounded-full opacity-60 animate-bounce" style={{animationDelay:'1.5s'}} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#f3eeff]0/20 border border-[#4B1E87]/30 text-indigo-300 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" /> Simplify Work. Amplify Impact.
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
            The Smarter Way to<br />
            <span className="bg-gradient-to-r from-[#875AC3] via-[#a07dd4] to-[#c4a8e8] bg-clip-text text-transparent">Manage Your Team</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            SyncWork gives every company — from 5-person startups to 500-person enterprises — a structured workspace where work gets planned, tracked, reviewed, and approved with full accountability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/auth?tab=register')}
              className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5] text-white text-base px-8 py-6 shadow-2xl">
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" onClick={() => navigate('/demo')}
              className="bg-white text-[#4B1E87] hover:bg-[#f3eeff] text-base px-8 py-6 font-semibold">
              Contact Sales
            </Button>
          </div>
          <p className="text-slate-400 text-sm mt-6">Free for teams up to 10 members · No credit card required</p>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="border-b bg-white py-14">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { stat: "60%", label: "Faster Sprint Planning" },
            { stat: "7",   label: "Role Levels Built-in" },
            { stat: "100%", label: "Data Isolation Per Org" },
            { stat: "50%", label: "AI Cost Savings via Cache" },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-4xl font-bold text-[#4B1E87] mb-1">{s.stat}</div>
              <div className="text-gray-500 text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How SyncWork Works</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">From workspace creation to task approval — in four simple steps</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6 items-stretch">
            {[
              { step: "1", icon: <Crown className="w-6 h-6" />, color: "bg-[#4B1E87]", title: "Owner Creates Workspace", desc: "Register, name your company workspace. You're the Owner — full control from day one." },
              { step: "2", icon: <Users className="w-6 h-6" />, color: "bg-[#693CB4]", title: "Invite Your Team", desc: "Send email invites with roles. Admin, Project Manager, Scrum Master, Team Lead, Developer — all assigned by you." },
              { step: "3", icon: <Target className="w-6 h-6" />, color: "bg-[#4B1E87]", title: "Plan & Assign Work", desc: "Use AI to break goals into tasks. Assign to developers. Set priorities and deadlines." },
              { step: "4", icon: <CheckCircle2 className="w-6 h-6" />, color: "bg-emerald-600", title: "Review & Approve", desc: "Developers submit work. Team Lead reviews. Project Manager gives final approval. Full audit trail." },
            ].map((s, i) => (
              <div key={i} className="relative flex flex-col">
                {i < 3 && <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-indigo-200 to-transparent z-0" />}
                <div className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center flex flex-col flex-1">
                  <div className={`w-14 h-14 ${s.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shrink-0`}>
                    {s.icon}
                  </div>
                  <div className="text-xs font-bold text-gray-400 mb-1">STEP {s.step}</div>
                  <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm flex-1">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything Your Team Needs</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">From AI planning to enterprise approvals — all in one platform</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {allFeatures.map((f, i) => (
              <div key={i} className="group rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${f.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button variant="outline" onClick={() => navigate('/features')} size="lg">
              See All Features <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── Business Mindset ── */}
      <section className="py-20 bg-gradient-to-br from-[#0d0618] via-[#1a0833] to-[#2a0f52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Designed Around How Your Business Operates</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Every company has leadership, management, and execution layers. SyncWork is built to match your structure — not force you to adapt to ours.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Crown className="w-7 h-7" />,
                color: "from-yellow-500 to-amber-500",
                title: "Leadership Has Full Visibility",
                desc: "Decision makers get a complete, real-time view of every project across the company — who's working on what, what's delayed, and what's been delivered."
              },
              {
                icon: <Target className="w-7 h-7" />,
                color: "from-[#4B1E87] to-[#693CB4]",
                title: "Managers Control Their Projects",
                desc: "Project managers and team leads stay in full control of planning, delegation, and quality. Work doesn't move forward until the right person signs off."
              },
              {
                icon: <Users className="w-7 h-7" />,
                color: "from-emerald-500 to-teal-500",
                title: "Teams Focus on Delivery",
                desc: "Your developers stay focused on tasks — no noise, no access they don't need. They see their work, submit it for review, and get notified when it's approved."
              },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${c.color} flex items-center justify-center text-white mb-6`}>
                  {c.icon}
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{c.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 grid sm:grid-cols-3 gap-6 text-center">
            {[
              { stat: "Zero confusion", label: "Everyone knows exactly what they can and can't do" },
              { stat: "Full accountability", label: "Every decision is logged with the person's name and time" },
              { stat: "No data leaks", label: "Each company's workspace is completely private and isolated" },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="text-indigo-300 font-bold text-lg mb-2">{s.stat}</div>
                <div className="text-slate-400 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── CTA ── */}
      <section className="py-20 bg-gradient-to-r from-[#4B1E87] to-[#693CB4]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Bring Order to Your Team?</h2>
          <p className="text-indigo-100 text-lg mb-8">Set up your workspace. Invite your team. Start shipping faster.</p>
          <Button size="lg" onClick={() => navigate('/auth?tab=register')}
            className="bg-white text-[#4B1E87] hover:bg-[#f3eeff] text-base px-8 py-6 font-bold">
            Get Started <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center">
              <img src="/footer.svg" alt="SyncWork" className="h-20 w-auto object-contain" style={{maxWidth:'300px'}} />
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <Link to="/features" className="hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/demo" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/auth" className="hover:text-white transition-colors">Sign In</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
            <p className="text-sm">© 2026 SyncWork. All rights reserved. · <a href="mailto:info@syncwork.pro" className="hover:text-white transition-colors">info@syncwork.pro</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== ROLE UTILITIES (Shared) - ONLY 3 ROLES ====================
const getRoleIcon = (role) => {
  const roleLower = role?.toLowerCase();
  switch (roleLower) {
    case 'owner':  return <Crown className="w-4 h-4 text-yellow-500" />;
    case 'admin':  return <Shield className="w-4 h-4 text-blue-500" />;
    case 'hr':     return <Users className="w-4 h-4 text-pink-500" />;
    case 'member': return <UserCheck className="w-4 h-4 text-gray-500" />;
    default:       return <UserCheck className="w-4 h-4 text-gray-500" />;
  }
};

const getRoleBadge = (role) => {
  const roleLower = role?.toLowerCase();
  const badges = {
    'owner':  { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Owner' },
    'admin':  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Admin' },
    'hr':     { bg: 'bg-pink-100',   text: 'text-pink-800',   label: 'HR' },
    'member': { bg: 'bg-gray-100',   text: 'text-gray-800',   label: 'Member' },
  };
  return badges[roleLower] || badges['member'];
};

// ==================== DASHBOARD HEADER ====================
const DashboardHeader = () => {
  const { user, location: _loc, currentOrg, logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive current page label from path
  const PAGE_LABELS = {
    '/dashboard':  'Dashboard',
    '/projects':   'Projects',
    '/analytics':  'Analytics',
    '/team':       'Directory',
    '/enterprise': 'Enterprise',
    '/billing':    'Billing',
    '/settings':   'Settings',
  };
  const SECTION_LABELS = { users: 'Users', activity: 'Activity', teams: 'Teams', groups: 'Groups' };
  const pathBase = '/' + location.pathname.split('/')[1];
  const pathSub  = location.pathname.split('/')[2];
  const baseLabel = PAGE_LABELS[pathBase];
  const pageLabel = baseLabel
    ? (pathSub && SECTION_LABELS[pathSub] ? `${baseLabel} · ${SECTION_LABELS[pathSub]}` : baseLabel)
    : 'SyncWork';

  return (
    <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 h-14 flex items-center px-6">
      {/* Page title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">{pageLabel}</h1>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2">
        {/* Search button */}
        <button
          onClick={() => setSearchOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm text-gray-500 dark:text-gray-400"
        >
          <Search className="w-4 h-4" />
          <span className="hidden md:inline">Search…</span>
          <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 text-[10px] font-mono text-gray-400">⌘K</kbd>
        </button>
        <NotificationsPanel />
        <ThemeToggle />

        {/* Profile dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(p => !p)}
            aria-label="User menu"
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4B1E87] to-[#693CB4] flex items-center justify-center text-white text-xs font-bold shrink-0">
              {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <span className="hidden lg:block text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[120px]">
              {user?.full_name || 'Account'}
            </span>
          </button>

          {showProfileMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  {currentOrg && (
                    <p className="text-xs text-[#4B1E87] dark:text-indigo-400 truncate mt-0.5 font-medium">{currentOrg.name}</p>
                  )}
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors gap-3"
                  >
                    <Settings className="w-4 h-4 text-gray-400" />
                    Settings
                  </button>
                  <button
                    onClick={() => { setShowProfileMenu(false); logout(); }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors gap-3"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <GlobalCommandPalette
        currentOrg={currentOrg}
        onQuickCreateProject={(mode) => { navigate('/dashboard'); window.dispatchEvent(new CustomEvent('syncwork:create-project', { detail: { mode } })); }}
        onQuickCreateTask={(mode) => { navigate('/dashboard'); window.dispatchEvent(new CustomEvent('syncwork:create-task', { detail: { mode } })); }}
        onNavigateNotifications={() => navigate('/dashboard')}
        forceOpen={searchOpen}
        onForceOpenConsumed={() => setSearchOpen(false)}
      />
    </header>
  );
};

// ==================== AI RESULT CARD COMPONENT ====================
const AIResultCard = ({ title, children, cached = false, cachedAt = null, onRegenerate = null }) => {
  return (
    <Card className={`border-l-4 ${cached ? 'border-l-blue-500 bg-blue-50/30' : 'border-l-emerald-500'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            {title}
            {cached && (
              <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                <Clock className="w-3 h-3 mr-1" />
                Cached
              </Badge>
            )}
          </CardTitle>
          {onRegenerate && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRegenerate}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Regenerate
            </Button>
          )}
        </div>
        {cached && cachedAt && (
          <CardDescription className="text-blue-600">
            Generated {new Date(cachedAt).toLocaleString()}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );
};

// ==================== WORKSPACE INVITE PANEL (OWNER / ADMIN) ====================
const WorkspaceInvitePanel = ({ orgId, currentUserRole }) => {
  const [email,      setEmail]      = useState('');
  const [role,       setRole]       = useState('member');
  const [loading,    setLoading]    = useState(false);
  const [members,    setMembers]    = useState([]);

  const loadMembers = async () => {
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/members`));
      setMembers(res.data || []);
    } catch (_) {}
  };

  useEffect(() => { loadMembers(); }, [orgId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/members`), {
        user_email: email.trim(),
        role,
      });
      toast.success(res.data?.message || 'Invite sent');
      setEmail('');
      setRole('member');
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const WORKSPACE_ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

  return (
    <Card className="dashboard-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <span className="mr-3 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-[#4B1E87] ring-1 ring-indigo-200/70">
            <UserCheck className="h-5 w-5" />
          </span>
          Invite to Workspace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Invite people to your workspace. After they register, they can be added to specific projects with a project role.
        </p>

        {/* Invite form */}
        <form onSubmit={handleInvite} className="space-y-2">
          <Input
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="flex gap-2">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currentUserRole === 'owner' && (
                  <SelectItem value="admin">Admin — can manage projects & members</SelectItem>
                )}
                <SelectItem value="member">Member — added to projects by Admin/Owner</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={loading} className="shrink-0">
              {loading ? 'Sending…' : 'Send Invite'}
            </Button>
          </div>
        </form>

        {/* Current workspace members */}
        {members.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Workspace Members</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {members.map((m) => (
                <div key={m.user_id || m.id} className="flex items-center justify-between text-sm py-1">
                  <div>
                    <span className="font-medium">{m.full_name || m.email}</span>
                    {m.full_name && <span className="text-muted-foreground ml-1.5 text-xs">{m.email}</span>}
                  </div>
                  <Badge variant="outline" className="text-xs capitalize">
                    {WORKSPACE_ROLE_LABELS[m.role?.toLowerCase()] || m.role}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== PROJECT MEMBERS MANAGER (ADMIN/HR) ====================
const ProjectMembersManager = ({ orgId, projectId, onUpdate }) => {
  const { organizations, currentOrg } = useAuth();
  const userOrgRole = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  // OWNER can remove anyone. ADMIN can remove anyone except OWNER. Others cannot remove.
  const canActOnMember = (targetOrgRole) => {
    if (userOrgRole === 'OWNER') return true;
    if (userOrgRole === 'ADMIN') return targetOrgRole !== 'OWNER';
    return false;
  };

  const [members, setMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('DEVELOPER');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMembers();
    loadOrgMembers();
  }, [orgId, projectId]);

  const loadMembers = async () => {
    try {
      const response = await axios.get(getApiUrl(`/organizations/${orgId}/projects/${projectId}/members`));
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load project members:', error);
      toast.error('Failed to load project members');
    }
  };

  const loadOrgMembers = async () => {
    try {
      const response = await axios.get(getApiUrl(`/organizations/${orgId}/members`));
      setOrgMembers(response.data);
    } catch (error) {
      console.error('Failed to load org members:', error);
    }
  };

  const addMember = async () => {
    if (!newMemberEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(getApiUrl(`/organizations/${orgId}/projects/${projectId}/members`), {
        user_email: newMemberEmail,
        role_in_project: newMemberRole
      });
      toast.success(response.data?.detail || 'Member added successfully');
      setNewMemberEmail('');
      setNewMemberRole('DEVELOPER');
      loadMembers();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const updateMemberRole = async (userId, newRole) => {
    try {
      await axios.patch(getApiUrl(`/organizations/${orgId}/projects/${projectId}/members/${userId}`), {
        role_in_project: newRole
      });
      toast.success('Role updated');
      loadMembers();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await axios.delete(getApiUrl(`/organizations/${orgId}/projects/${projectId}/members/${userId}`));
      toast.success('Member removed');
      loadMembers();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Add Member Form */}
      <div className="space-y-2">
        <Input
          placeholder="Enter member email"
          value={newMemberEmail}
          onChange={(e) => setNewMemberEmail(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addMember()}
          className="text-sm"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={newMemberRole} onValueChange={setNewMemberRole}>
            <SelectTrigger className="flex-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
              <SelectItem value="SCRUM_MASTER">Scrum Master</SelectItem>
              <SelectItem value="TEAM_LEAD">Team Lead</SelectItem>
              <SelectItem value="DEVELOPER">Developer</SelectItem>
              <SelectItem value="TESTER">Tester</SelectItem>
              <SelectItem value="OBSERVER">Observer</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={addMember} size="sm" disabled={loading} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-1 sm:mr-0" />
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Members List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {members.length === 0 ? (
          <p className="text-xs sm:text-sm text-gray-500 text-center py-4">No members yet</p>
        ) : (
          members.map((member) => {
            const orgMember = orgMembers.find(om => om.user_id === member.user_id);
            const targetOrgRole = orgMember?.role?.toUpperCase();
            const canRemove = canActOnMember(targetOrgRole);
            return (
              <div key={member.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-sm font-medium truncate">
                    {orgMember?.full_name || orgMember?.email || 'Unknown User'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {({'PROJECT_MANAGER':'Project Manager','SCRUM_MASTER':'Scrum Master','TEAM_LEAD':'Team Lead','DEVELOPER':'Developer','TESTER':'Tester','OBSERVER':'Observer','VIEWER':'Viewer'})[member.role_in_project] || member.role_in_project}
                  </div>
                </div>
                <div className="flex items-center space-x-1 w-full sm:w-auto">
                  <Select
                    value={member.role_in_project}
                    onValueChange={(role) => updateMemberRole(member.user_id, role)}
                    disabled={!canRemove}
                  >
                    <SelectTrigger className="w-full sm:w-24 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
                      <SelectItem value="SCRUM_MASTER">Scrum Master</SelectItem>
                      <SelectItem value="TEAM_LEAD">Team Lead</SelectItem>
                      <SelectItem value="DEVELOPER">Developer</SelectItem>
                    </SelectContent>
                  </Select>
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMember(member.user_id)}
                      className="h-8 w-8 p-0 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ==================== PROJECT TEAM VIEW (ALL MEMBERS) ====================
const ProjectTeamView = ({ orgId, projectId }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);

  useEffect(() => {
    loadTeam();
    loadOrgMembers();
  }, [orgId, projectId]);

  const loadTeam = async () => {
    try {
      const response = await axios.get(getApiUrl(`/organizations/${orgId}/projects/${projectId}/team`));
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to load team:', error);
    }
  };

  const loadOrgMembers = async () => {
    try {
      const response = await axios.get(getApiUrl(`/organizations/${orgId}/members`));
      setOrgMembers(response.data);
    } catch (error) {
      console.error('Failed to load org members:', error);
    }
  };

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {teamMembers.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No team members</p>
      ) : (
        teamMembers.map((member) => {
          const orgMember = orgMembers.find(om => om.user_id === member.user_id);
          return (
            <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {orgMember?.full_name || orgMember?.email || 'Unknown User'}
                </div>
                <div className="text-xs text-gray-500">
                  {member.role_in_project === 'MANAGER'
                    ? 'Project Manager'
                    : member.role_in_project === 'LEAD'
                      ? 'Team Lead'
                      : 'Team Member'}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ==================== DASHBOARD COMPONENT ====================
const Dashboard = () => {
  const { currentOrg, user, organizations, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userOrgRole, setUserOrgRole] = useState(null);
  const [projectRole, setProjectRole] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(ONBOARDING_KEY));

  // Sprint & view state
  const [sprints, setSprints] = useState([]);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [selectedTask, setSelectedTask] = useState(null);

  // AI States
  const [sprintPlanResult, setSprintPlanResult] = useState(null);
  const [taskBreakdownResult, setTaskBreakdownResult] = useState(null);
  const [commentRewriteResult, setCommentRewriteResult] = useState(null);

  // Form states
  const [newProjectName, setNewProjectName] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskAssigneeId, setNewTaskAssigneeId] = useState('unassigned');
  const [goalToBreakdown, setGoalToBreakdown] = useState('');
  const [commentToRewrite, setCommentToRewrite] = useState('');
  
  // Project members state
  const [projectMembers, setProjectMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [quickActionType, setQuickActionType] = useState('task');
  const [quickActionText, setQuickActionText] = useState('');
  const [quickActionProjectId, setQuickActionProjectId] = useState('');
  const [quickActionAssigneeId, setQuickActionAssigneeId] = useState('unassigned');
  const projectsControllerRef = useRef(null);
  const tasksControllerRef = useRef(null);
  const membersControllerRef = useRef(null);
  const projectInputRef = useRef(null);
  const taskInputRef = useRef(null);
  // Stable ref so the WS callback always calls the latest loadTasks without
  // causing handleRealtimeEvent to be recreated on every render.
  const taskChangeRef = useRef(null);
  const stableOnTaskChange = useCallback(() => taskChangeRef.current?.(), []);

  const {
    isConnected,
    presence,
    typingUsers
  } = useRealtimeProject(selectedProject?.id, user, currentOrg, {
    onTaskChange: stableOnTaskChange
  });

  useEffect(() => {
    if (currentOrg) {
      // Get user's org role
      const membership = organizations.find(o => o.id === currentOrg.id);
      if (membership) {
        setUserOrgRole(membership.role?.toUpperCase() || 'MEMBER');
      }
      loadProjects();
      loadOrgMembers();
    }
  }, [currentOrg, organizations]);

  useEffect(() => {
    const projectIdFromUrl = new URLSearchParams(window.location.search).get('project');
    if (projectIdFromUrl && projects.length > 0) {
      const matched = projects.find((item) => item.id === projectIdFromUrl);
      if (matched) {
        setSelectedProject(matched);
      }
    }
  }, [projects]);
  
  useEffect(() => {
    if (selectedProject && currentOrg && user) {
      loadTasks(selectedProject.id);
      loadProjectRole();
      loadSprints(selectedProject.id);
    }
  }, [selectedProject, currentOrg, user]);

  useEffect(() => () => {
    projectsControllerRef.current?.abort();
    tasksControllerRef.current?.abort();
    membersControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const handleProjectCreate = (event) => {
      if (event?.detail?.mode === 'modal') {
        setQuickActionType('project');
        setQuickActionText('');
        setQuickActionOpen(true);
        return;
      }
      setTimeout(() => projectInputRef.current?.focus(), 80);
    };
    const handleTaskCreate = (event) => {
      if (event?.detail?.mode === 'modal') {
        setQuickActionType('task');
        setQuickActionText('');
        setQuickActionProjectId(selectedProject?.id || '');
        setQuickActionAssigneeId('unassigned');
        setQuickActionOpen(true);
        return;
      }
      setTimeout(() => taskInputRef.current?.focus(), 80);
    };

    window.addEventListener('syncwork:create-project', handleProjectCreate);
    window.addEventListener('syncwork:create-task', handleTaskCreate);
    return () => {
      window.removeEventListener('syncwork:create-project', handleProjectCreate);
      window.removeEventListener('syncwork:create-task', handleTaskCreate);
    };
  }, [selectedProject]);

  const dashboardInsights = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((task) => String(task.status).toLowerCase() === 'done').length;
    const overdueTasks = tasks.filter((task) => String(task.status).toLowerCase() !== 'done' && task.due_date && new Date(task.due_date) < new Date()).length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      completionRate,
      riskLevel: overdueTasks > 4 ? 'High' : overdueTasks > 0 ? 'Medium' : 'Low',
    };
  }, [tasks]);

  const contextualRecommendations = useMemo(() => {
    const recs = [];

    if (!selectedProject) {
      recs.push({ title: 'Select a project', description: 'Choose a project so the platform can scope execution signals to the correct team, backlog, and approval path.' });
    }
    if (dashboardInsights.totalTasks === 0) {
      recs.push({ title: 'Seed the execution backlog', description: 'Create the first owned task so sprint planning, workload review, and delivery health have real operating data.' });
    }
    if (dashboardInsights.overdueTasks > 0) {
      recs.push({ title: 'Resolve delivery slippage', description: `${dashboardInsights.overdueTasks} overdue item(s) are reducing confidence. Reassign ownership, de-scope, or reset expectations before adding new work.` });
    }
    if (dashboardInsights.completionRate < 40 && dashboardInsights.totalTasks > 5) {
      recs.push({ title: 'Rebalance sprint scope', description: 'Completion is below 40%, which suggests capacity, review latency, or scope quality needs attention before committing more work.' });
    }

    if (recs.length === 0) {
      recs.push({ title: 'Execution health is stable', description: 'No immediate risk signal is dominating the workspace. Keep momentum by validating dependencies and review capacity in the next standup.' });
    }

    return recs.slice(0, 3);
  }, [dashboardInsights, selectedProject]);

  const PROJECT_EDITOR_ROLES   = ['PROJECT_MANAGER', 'SCRUM_MASTER', 'TEAM_LEAD'];
  const PROJECT_APPROVER_ROLES = ['PROJECT_MANAGER', 'SCRUM_MASTER', 'TEAM_LEAD'];
  const ORG_SUPER_ROLES        = ['OWNER', 'ADMIN'];

  // Owner/Admin are never read-only — they can do everything
  const isReadOnlyViewer = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return false;
    return ['VIEWER', 'OBSERVER'].includes(projectRole);
  }, [userOrgRole, projectRole]);

  // Owner/Admin always can manage. PM/SM/TL can manage within their project.
  const canManageCurrentProject = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return true;
    return PROJECT_EDITOR_ROLES.includes(projectRole);
  }, [projectRole, userOrgRole]);

  // Owner/Admin can always approve. PM/SM/TL can approve within their project.
  const canApproveCurrentProject = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return true;
    return PROJECT_APPROVER_ROLES.includes(projectRole);
  }, [projectRole, userOrgRole]);

  const currentProjectManagerCount = useMemo(
    () => projectMembers.filter((m) => m.role_in_project === 'PROJECT_MANAGER').length,
    [projectMembers]
  );
  
  const loadProjectRole = async () => {
    if (!selectedProject || !currentOrg || !user) return;
    
    try {
      const response = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${selectedProject.id}/team`));
      const member = response.data.find(m => m.user_id === user.id);
      setProjectMembers(response.data || []);
      if (member) {
        setProjectRole(member.role_in_project?.toUpperCase() || 'MEMBER');
      } else {
        setProjectRole(null);
      }
    } catch (error) {
      // User might not be a project member, that's ok
      setProjectMembers([]);
      setProjectRole(null);
    }
  };

  const loadProjects = async () => {
    if (!currentOrg) return;
    
    try {
      if (projectsControllerRef.current) {
        projectsControllerRef.current.abort();
      }
      projectsControllerRef.current = new AbortController();
      const response = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects`), {
        signal: projectsControllerRef.current.signal
      });
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      toast.error('Failed to load projects');
    }
  };

  const loadOrgMembers = async () => {
    if (!currentOrg) return;
    try {
      if (membersControllerRef.current) {
        membersControllerRef.current.abort();
      }
      membersControllerRef.current = new AbortController();
      const response = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/members`), {
        signal: membersControllerRef.current.signal
      });
      setOrgMembers(response.data || []);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Failed to load org members', error);
    }
  };

  const loadTasks = async (projectId) => {
    if (!currentOrg) return;

    try {
      if (tasksControllerRef.current) {
        tasksControllerRef.current.abort();
      }
      tasksControllerRef.current = new AbortController();
      const response = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/tasks`), {
        signal: tasksControllerRef.current.signal
      });
      setTasks(response.data);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      toast.error('Failed to load tasks');
    }
  };

  // Keep taskChangeRef pointing at the latest loadTasks + selectedProject
  // so the stable WS callback always triggers a fresh fetch.
  taskChangeRef.current = () => {
    if (selectedProject) loadTasks(selectedProject.id);
  };

  // ── Sprint CRUD ──────────────────────────────────────────────────────────────
  const loadSprints = async (projectId) => {
    if (!currentOrg || !projectId) return;
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/sprints`));
      setSprints(res.data || []);
    } catch { setSprints([]); }
  };

  const createSprint = async (data) => {
    const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/projects/${selectedProject.id}/sprints`), {
      ...data, project_id: selectedProject.id,
    });
    setSprints(prev => [res.data, ...prev]);
    toast.success('Sprint created');
  };

  const updateSprint = async (sprintId, data) => {
    const res = await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}`), data);
    setSprints(prev => prev.map(s => s.id === sprintId ? res.data : s));
    toast.success('Sprint updated');
  };

  const deleteSprint = async (sprintId) => {
    await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}`));
    setSprints(prev => prev.filter(s => s.id !== sprintId));
    toast.success('Sprint deleted');
  };

  const startSprint = async (sprintId) => {
    const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}/start`));
    setSprints(prev => prev.map(s => s.id === sprintId ? res.data : s));
    toast.success('Sprint started');
  };

  const completeSprint = async (sprintId) => {
    const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}/complete`));
    setSprints(prev => prev.map(s => s.id === sprintId ? res.data : s));
    toast.success('Sprint completed');
  };

  // ── Task detail update (used by TaskDetailModal) ─────────────────────────────
  const updateTaskDetail = async (taskId, payload) => {
    const res = await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}`), payload);
    setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
    if (selectedTask?.id === taskId) setSelectedTask(res.data);
    return res.data;
  };

  // ── Bulk task action ─────────────────────────────────────────────────────────
  const bulkTaskAction = async (taskIds, action, payload = {}) => {
    await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/bulk`), {
      task_ids: taskIds, action, payload,
    });
    await loadTasks(selectedProject.id);
  };

  const moveTaskKanban = async (taskId, toStatus, position, rejectionComment = null) => {
    if (!currentOrg || !selectedProject) return;
    const previousTasks = [...tasks];
    const movingTask = tasks.find((t) => t.id === taskId);
    if (!movingTask) return;

    // Optimistic update
    setTasks(tasks.map((t) => t.id === taskId ? { ...t, status: toStatus, position } : t));

    try {
      const body = { to_status: toStatus, position };
      if (rejectionComment) body.rejection_comment = rejectionComment;
      await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}/move`), body);
      await loadTasks(selectedProject.id);
    } catch (error) {
      setTasks(previousTasks);
      toast.error(error.response?.data?.detail || 'Failed to move task');
    }
  };

  const archiveTaskKanban = async (taskId) => {
    if (!currentOrg) return;
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}/archive`));
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success('Task archived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive task');
    }
  };

  const reorderKanbanColumn = async (status, taskIds) => {
    if (!currentOrg || !selectedProject || taskIds.length === 0) return;
    const previousTasks = [...tasks];
    const taskSet = new Set(taskIds);

    const optimisticTasks = tasks.map((task) => {
      if (!taskSet.has(task.id)) return task;
      return { ...task, position: taskIds.indexOf(task.id) };
    });
    setTasks(optimisticTasks);

    try {
      await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/reorder`), {
        task_ids: taskIds,
        status,
      });
      await loadTasks(selectedProject.id);
    } catch (error) {
      setTasks(previousTasks);
      toast.error(error.response?.data?.detail || 'Failed to reorder tasks');
    }
  };

  const updateTaskInline = async (taskId, payload) => {
    if (!currentOrg || !selectedProject) return;
    const previousTasks = [...tasks];

    setTasks((current) =>
      current.map((task) => (task.id === taskId ? { ...task, ...payload } : task))
    );

    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}`), payload);
      await loadTasks(selectedProject.id);
      toast.success('Task updated');
    } catch (error) {
      setTasks(previousTasks);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const normalizeTextOverride = (value, fallback) =>
    typeof value === 'string' ? value : fallback;

  const createProject = async (projectNameOverride = null) => {
    const projectName = normalizeTextOverride(projectNameOverride, newProjectName).trim();
    if (!projectName || !currentOrg) return;
    
    // Check if user can create projects (ADMIN or HR only)
    const canCreate = userOrgRole && ['OWNER', 'ADMIN'].includes(userOrgRole);
    if (!canCreate) {
      toast.error('Only admins and HR can create projects');
      return;
    }
    
    try {
      const response = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/projects`), {
        name: projectName,
        description: `AI-managed project: ${projectName}`
      });
      setProjects([...projects, response.data]);
      setSelectedProject(response.data);
      if (typeof projectNameOverride !== 'string') {
        setNewProjectName('');
      }
      toast.success('Project created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const createTask = async (taskTitleOverride = null, projectIdOverride = null, assigneeIdOverride = null) => {
    const taskTitle = normalizeTextOverride(taskTitleOverride, newTaskTitle).trim();
    const resolvedProjectId = projectIdOverride || selectedProject?.id;
    const resolvedAssigneeId =
      assigneeIdOverride ||
      (typeof taskTitleOverride === 'string' ? null : newTaskAssigneeId);
    if (!taskTitle || !resolvedProjectId || !currentOrg) return;
    
    try {
      const response = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks`), {
        project_id: resolvedProjectId,
        title: taskTitle,
        description: `Task: ${taskTitle}`,
        priority: 2,
        assignee_id: resolvedAssigneeId && resolvedAssigneeId !== 'unassigned' ? resolvedAssigneeId : null
      });
      if (selectedProject?.id === resolvedProjectId) {
        setTasks([...tasks, response.data]);
      } else {
        await loadTasks(resolvedProjectId);
      }
      if (typeof taskTitleOverride !== 'string') {
        setNewTaskTitle('');
        setNewTaskAssigneeId('unassigned');
      }
      toast.success('Task created successfully!');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  // ==================== AI FEATURES WITH CACHING ====================

  const generateSprintPlan = async (forceRegenerate = false) => {
    // Guard: Check authentication first
    if (!user) {
      toast.error('Please log in to generate sprint plans');
      return;
    }

    // Guard: Check required data
    if (!selectedProject || tasks.length === 0 || !currentOrg) {
      toast.error('Please select a project with tasks first');
      return;
    }

    // Guard: Verify token exists
    const token = Cookies.get('access_token');
    if (!token) {
      toast.error('Session expired. Please log in again');
      logout();
      return;
    }

    setLoading(true);
    try {
      const backlogTasks = tasks.map(task => ({
        id: task.id,
        title: task.title,
        estimateHrs: task.estimate_hrs || 8,
        priority: task.priority || 2
      }));

      const response = await axios.post(
        getApiUrl(`/organizations/${currentOrg.id}/ai/sprint-plan`),
        {
          project_id: selectedProject.id,
          backlog_tasks: backlogTasks,
          team_capacity: Math.max(projectMembers.length * 40, 40), // 40hrs per member per sprint, min 40
          sprint_weeks: 2,
          force_regenerate: forceRegenerate
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSprintPlanResult(response.data);
      if (response.data.cached) {
        toast.success('Sprint plan loaded from cache');
      } else {
        toast.success('New sprint plan generated successfully!');
      }
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again');
        logout();
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to generate sprint plans');
      } else if (error.response?.status === 503) {
        toast.error('AI service is temporarily unavailable. Please try again later.');
      } else {
        toast.error(error.response?.data?.detail?.error?.message || 'Failed to generate sprint plan');
      }
      console.error('Sprint plan error:', error);
    } finally {
      setLoading(false);
    }
  };

  const [creatingSprintsFromPlan, setCreatingSprintsFromPlan] = useState(false);
  const createSprintsFromPlan = async () => {
    if (!sprintPlanResult?.sprints?.length || !selectedProject || !currentOrg) return;
    setCreatingSprintsFromPlan(true);
    try {
      const today = new Date();
      for (let i = 0; i < sprintPlanResult.sprints.length; i++) {
        const s = sprintPlanResult.sprints[i];
        const start = new Date(today);
        start.setDate(today.getDate() + i * 14);
        const end = new Date(start);
        end.setDate(start.getDate() + 13);
        await axios.post(getApiUrl(`/organizations/${currentOrg.id}/projects/${selectedProject.id}/sprints`), {
          project_id: selectedProject.id,
          name: `Sprint ${s.sprintNumber || i + 1}`,
          goal: sprintPlanResult.recommendations?.[i] || `Sprint ${i + 1} goals`,
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        });
      }
      toast.success(`${sprintPlanResult.sprints.length} sprint${sprintPlanResult.sprints.length > 1 ? 's' : ''} created!`);
    } catch {
      toast.error('Failed to create sprints');
    } finally {
      setCreatingSprintsFromPlan(false);
    }
  };

  const breakdownGoal = async (forceRegenerate = false, goalOverride = null) => {
    const goalInput = (goalOverride ?? goalToBreakdown).trim();
    if (!goalInput || !currentOrg) {
      toast.error('Please enter a goal to break down');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/ai/task-breakdown`), {
        goal: goalInput,
        project_context: selectedProject?.name || 'General project',
        force_regenerate: forceRegenerate
      });
      
      setTaskBreakdownResult(response.data);
      if (response.data.cached) {
        toast.success('Task breakdown loaded from cache');
      } else {
        toast.success('New task breakdown generated successfully!');
      }
    } catch (error) {
      toast.error('Failed to break down goal');
      console.error('Task breakdown error:', error);
    } finally {
      setLoading(false);
    }
  };

  const rewriteComment = async (forceRegenerate = false, commentOverride = null) => {
    const commentInput = (commentOverride ?? commentToRewrite).trim();
    if (!commentInput || !currentOrg) {
      toast.error('Please enter a comment to rewrite');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/ai/comment-rewrite`), {
        comment: commentInput,
        context: selectedProject?.name || 'Project context',
        force_regenerate: forceRegenerate
      });
      
      setCommentRewriteResult(response.data);
      if (response.data.cached) {
        toast.success('Comment rewrite loaded from cache');
      } else {
        toast.success('New comment rewrite generated successfully!');
      }
    } catch (error) {
      toast.error('Failed to rewrite comment');
      console.error('Comment rewrite error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTasksFromBreakdown = async () => {
    if (!taskBreakdownResult || !selectedProject || !currentOrg) return;

    try {
      for (const task of taskBreakdownResult.tasks) {
        await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks`), {
          project_id: selectedProject.id,
          title: task.title,
          description: task.description,
          estimate_hrs: task.estimateHrs,
          priority: 2,
          tags: task.tags || []
        });
      }
      
      loadTasks(selectedProject.id);
      toast.success(`Added ${taskBreakdownResult.tasks.length} tasks to project!`);
    } catch (error) {
      toast.error('Failed to add tasks');
    }
  };

  const handleQuickActionSubmit = async (event) => {
    event.preventDefault();

    if (quickActionType === 'project') {
      await createProject(quickActionText);
    } else {
      await createTask(
        quickActionText,
        quickActionProjectId || selectedProject?.id,
        quickActionAssigneeId === 'unassigned' ? null : quickActionAssigneeId
      );
    }

    setQuickActionOpen(false);
    setQuickActionText('');
    setQuickActionAssigneeId('unassigned');
  };

  if (!currentOrg) {
    return (
      <AppShell header={<DashboardHeader />}>
        <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">
          <EmptyState
            icon={Building2}
            title="No Organization Selected"
            description="Please select an organization to continue."
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell header={<DashboardHeader />}>
      {showOnboarding && <OnboardingWizard onDone={() => setShowOnboarding(false)} />}
      <div className="border-b border-white/60 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-8">
          <PresenceBar
            presence={presence}
            isConnected={isConnected}
            className="min-h-8"
          />
          <div className="flex items-center gap-2">
            <SyncStateBadge state={isConnected ? "synced" : "offline"} />
            <TypingIndicator typingUsers={typingUsers} context="task" />
          </div>
        </div>
      </div>

      <div className="dashboard-canvas">
      <div className="dashboard-shell max-w-[1500px] mx-auto px-3 sm:px-4 lg:px-8 py-5 sm:py-7 lg:py-10">
        <Dialog open={quickActionOpen} onOpenChange={setQuickActionOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {quickActionType === 'project' ? 'Create Project' : 'Create Task'}
              </DialogTitle>
              <DialogDescription>
                {quickActionType === 'project'
                  ? 'Create a new project quickly from command center.'
                  : 'Create a new task without leaving your flow.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleQuickActionSubmit} className="space-y-4">
              <Input
                autoFocus
                value={quickActionText}
                onChange={(e) => setQuickActionText(e.target.value)}
                placeholder={quickActionType === 'project' ? 'Project name' : 'Task title'}
              />
              {quickActionType === 'task' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Select value={quickActionProjectId || selectedProject?.id || ""} onValueChange={setQuickActionProjectId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={quickActionAssigneeId} onValueChange={setQuickActionAssigneeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Assign to" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {orgMembers.filter(m => m.role?.toLowerCase() !== 'owner').map((member) => (
                        <SelectItem key={member.user_id || member.id} value={member.user_id || member.id}>
                          {member.full_name || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setQuickActionOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {quickActionType === 'project' ? 'Create Project' : 'Create Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <div className="dashboard-hero-rich mb-5 rounded-[28px] p-5 sm:p-7 lg:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="dashboard-pill">
                  {isConnected ? 'Live collaboration' : 'Offline mode'}
                </Badge>
                <Badge variant="outline" className="dashboard-pill">
                  {userOrgRole === 'OWNER' ? 'Owner'
                    : userOrgRole === 'ADMIN' ? 'Admin'
                    : ({'PROJECT_MANAGER':'Project Manager','SCRUM_MASTER':'Scrum Master','TEAM_LEAD':'Team Lead','DEVELOPER':'Developer','TESTER':'Tester','OBSERVER':'Observer','VIEWER':'Viewer'})[projectRole] || projectRole || 'Member'}
                </Badge>
                {presence.length > 0 && (
                  <Badge variant="outline" className="dashboard-pill">
                    {['OWNER','ADMIN'].includes(userOrgRole)
                      ? `${presence.length} online`
                      : `${presence.filter(p => projectMembers.some(m => m.user_id === p.user_id)).length} members online`}
                  </Badge>
                )}
              </div>
              <h1 className="truncate text-3xl font-semibold tracking-tight sm:text-5xl">
                {selectedProject?.name || 'Project command center'}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 sm:text-base">
                AI-assisted planning, realtime collaboration, and delivery signals for the teams, leads, managers, and members in this workspace.
              </p>
            </div>
            <div className="dashboard-hero-stat relative z-10 grid grid-cols-3 gap-2 rounded-2xl p-3 text-center">
              <div className="px-4 py-3">
                <div className="text-2xl font-semibold">{dashboardInsights.completionRate}%</div>
                <div className="text-[11px] uppercase tracking-wide text-white/60">Complete</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-2xl font-semibold">{currentProjectManagerCount}</div>
                <div className="text-[11px] uppercase tracking-wide text-white/60">PMs</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-2xl font-semibold">{dashboardInsights.overdueTasks}</div>
                <div className="text-[11px] uppercase tracking-wide text-white/60">Overdue</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card className="metric-card metric-card-blue">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-300">Total Tasks</div>
              <div className="mt-2 text-4xl font-semibold">{dashboardInsights.totalTasks}</div>
              <div className="mt-3 h-1.5 rounded-full bg-blue-200 dark:bg-blue-900/50">
                <div className="h-full w-2/3 rounded-full bg-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card metric-card-green">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-300">Completed</div>
              <div className="mt-2 text-4xl font-semibold">{dashboardInsights.completedTasks}</div>
              <div className="mt-3 h-1.5 rounded-full bg-emerald-200 dark:bg-emerald-900/50">
                <div className="h-full w-1/2 rounded-full bg-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card metric-card-violet">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-300">Completion Rate</div>
              <div className="mt-2 text-4xl font-semibold">{dashboardInsights.completionRate}%</div>
              <div className="mt-3 h-1.5 rounded-full bg-violet-200 dark:bg-violet-900/50">
                <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(dashboardInsights.completionRate, 8)}%` }} />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card metric-card-amber">
            <CardContent className="pt-6">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-300">Risk Indicator</div>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant={dashboardInsights.riskLevel === 'High' ? 'destructive' : 'outline'}>
                  {dashboardInsights.riskLevel}
                </Badge>
                <span className="text-3xl font-semibold">{dashboardInsights.overdueTasks}</span>
              </div>
              <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">overdue items</div>
            </CardContent>
          </Card>
        </div>
        <div className="mb-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
          <Card className="dashboard-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Contextual Recommendations</CardTitle>
              <CardDescription>Smart suggestions based on current project activity and risk signals.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
              {contextualRecommendations.map((recommendation) => (
                <div key={recommendation.title} className="recommendation-tile rounded-2xl p-4 transition-transform hover:-translate-y-0.5">
                  <div className="text-sm font-medium">{recommendation.title}</div>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{recommendation.description}</p>
                </div>
              ))}
            </CardContent>
          </Card>
          <ActivityFeed
            tasks={tasks}
            recommendations={contextualRecommendations}
            selectedProject={selectedProject}
            presence={presence}
          />
        </div>
        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* Organization Info */}
            <Card className="dashboard-panel">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-[#4B1E87] ring-1 ring-indigo-200/70">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white truncate text-sm leading-tight">
                      {currentOrg?.name || 'No Organization'}
                    </p>
                    {currentOrg && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {getRoleIcon(currentOrg.role)}
                        <span className={`${getRoleBadge(currentOrg.role).bg} ${getRoleBadge(currentOrg.role).text} text-[11px] font-semibold px-2 py-0.5 rounded-full`}>
                          {getRoleBadge(currentOrg.role).label}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Project Selection */}
            <Card className="dashboard-panel">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <span className="mr-3 flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700 ring-1 ring-violet-200/70">
                    <Target className="h-5 w-5" />
                  </span>
                  {userOrgRole && ['OWNER', 'ADMIN'].includes(userOrgRole) ? 'All Projects' : 'My Projects'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {userOrgRole && ['OWNER', 'ADMIN'].includes(userOrgRole) && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      ref={projectInputRef}
                      placeholder="New project name"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && createProject()}
                      className="flex-1 text-sm"
                    />
                    <Button onClick={createProject} size="sm" className="w-full sm:w-auto" aria-label="Create project">
                      <Plus className="w-4 h-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden ml-1">Create</span>
                    </Button>
                  </div>
                )}

                <Select value={selectedProject?.id || ""} onValueChange={(value) => {
                  const project = projects.find(p => p.id === value);
                  setSelectedProject(project);
                }}>
                  <SelectTrigger className="w-full" aria-label="Select project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length > 0 ? (
                      projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          <span className="truncate">{project.name}</span>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-projects" disabled>
                        No projects available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>


          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Tabs defaultValue="sprint-plan" className="space-y-4 sm:space-y-6">
              <TabsList className="dashboard-tabs grid w-full grid-cols-3 h-auto rounded-2xl p-1">
                <TabsTrigger value="sprint-plan" className="data-[state=active]:bg-[#f3eeff] data-[state=active]:text-[#4B1E87] text-xs sm:text-sm py-2 sm:py-2.5">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">AI Sprint Plan</span>
                  <span className="sm:hidden">Sprint</span>
                </TabsTrigger>
                <TabsTrigger value="breakdown" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 text-xs sm:text-sm py-2 sm:py-2.5">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Goal Breakdown</span>
                  <span className="sm:hidden">Breakdown</span>
                </TabsTrigger>
                <TabsTrigger value="chat" className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 text-xs sm:text-sm py-2 sm:py-2.5">
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Comment AI</span>
                  <span className="sm:hidden">Comment</span>
                </TabsTrigger>
              </TabsList>

              {/* AI Sprint Planning Tab */}
              <TabsContent value="sprint-plan" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-[#4B1E87]" />
                      AI Sprint Planning Assistant
                    </CardTitle>
                    <CardDescription>
                      Generate optimal sprint plans based on your backlog, team capacity, and task dependencies
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 bg-[#f3eeff] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#3A1669] text-sm sm:text-base">Ready to plan your sprint?</div>
                        <div className="text-xs sm:text-sm text-[#4B1E87] mt-1">
                          {tasks.length} tasks in backlog • 80 hours team capacity
                        </div>
                      </div>
                      <Button 
                        onClick={() => generateSprintPlan(false)} 
                        disabled={loading || tasks.length === 0}
                        className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5] w-full sm:w-auto text-sm"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Sprint Plan
                          </>
                        )}
                      </Button>
                    </div>

                    {sprintPlanResult && (
                      <AIResultCard
                        title="Sprint Planning Results"
                        cached={sprintPlanResult.cached}
                        cachedAt={sprintPlanResult.cached_at}
                        onRegenerate={() => generateSprintPlan(true)}
                      >
                        <div className="space-y-4">
                          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            {sprintPlanResult.sprints?.map((sprint, index) => (
                              <Card key={index} className="border-l-4 border-l-indigo-500">
                                <CardHeader className="pb-3">
                                  <CardTitle className="text-lg">Sprint {sprint.sprintNumber}</CardTitle>
                                  <CardDescription>
                                    Capacity: {sprint.capacityUsed || sprint.capacity_used || 0}h used
                                  </CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {(sprint.tasks || []).slice(0, 3).map((task, taskIndex) => (
                                      <div key={taskIndex} className="flex items-center justify-between text-sm">
                                        <span className="truncate">{task.title}</span>
                                        <Badge variant="outline">{task.estimateHrs || task.estimate_hrs || 8}h</Badge>
                                      </div>
                                    ))}
                                    {(sprint.tasks || []).length > 3 && (
                                      <div className="text-xs text-gray-500">
                                        +{(sprint.tasks || []).length - 3} more tasks
                                      </div>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>

                          {sprintPlanResult.risks && sprintPlanResult.risks.length > 0 && (
                            <Card className="border-l-4 border-l-orange-500">
                              <CardHeader>
                                <CardTitle className="text-lg text-orange-700">Risk Analysis</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3">
                                  {sprintPlanResult.risks.map((risk, index) => (
                                    <div key={index} className="p-3 bg-orange-50 rounded-lg">
                                      <div className="font-medium text-orange-900">{risk.risk}</div>
                                      <div className="text-sm text-orange-700 mt-1">
                                        <strong>Impact:</strong> {risk.impact} • <strong>Mitigation:</strong> {risk.mitigation}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {sprintPlanResult.recommendations && (
                            <Card className="border-l-4 border-l-green-500">
                              <CardHeader>
                                <CardTitle className="text-lg text-green-700">AI Recommendations</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <ul className="space-y-2">
                                  {sprintPlanResult.recommendations.map((rec, index) => (
                                    <li key={index} className="flex items-start">
                                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                                      <span className="text-sm text-green-800">{rec}</span>
                                    </li>
                                  ))}
                                </ul>
                              </CardContent>
                            </Card>
                          )}
                          {sprintPlanResult.sprints?.length > 0 && (
                            <div className="flex justify-end pt-2">
                              <Button
                                onClick={createSprintsFromPlan}
                                disabled={creatingSprintsFromPlan}
                                className="bg-[#4B1E87] hover:bg-[#3A1669] text-white"
                              >
                                {creatingSprintsFromPlan ? (
                                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Creating…</>
                                ) : (
                                  <><Plus className="w-4 h-4 mr-2" />Create {sprintPlanResult.sprints.length} Sprint{sprintPlanResult.sprints.length > 1 ? 's' : ''} in Project</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </AIResultCard>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Task Breakdown Tab */}
              <TabsContent value="breakdown" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Target className="w-5 h-5 mr-2 text-emerald-600" />
                      Goal → Task Breakdown
                    </CardTitle>
                    <CardDescription>
                      Transform high-level goals into actionable engineering tasks with estimates and dependencies
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Enter your high-level goal (e.g., 'Build user authentication system', 'Implement payment processing', 'Create admin dashboard')"
                        value={goalToBreakdown}
                        onChange={(e) => setGoalToBreakdown(e.target.value)}
                        className="min-h-[100px] text-sm resize-y"
                      />
                      <Button 
                        onClick={() => breakdownGoal(false)} 
                        disabled={loading || !goalToBreakdown.trim()}
                        className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Breaking Down...
                          </>
                        ) : (
                          <>
                            <Brain className="w-4 h-4 mr-2" />
                            Break Down Goal
                          </>
                        )}
                      </Button>
                    </div>

                    {taskBreakdownResult && (
                      <AIResultCard
                        title="Task Breakdown Results"
                        cached={taskBreakdownResult.cached}
                        cachedAt={taskBreakdownResult.cached_at}
                        onRegenerate={() => breakdownGoal(true)}
                      >
                        <div className="space-y-4">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Generated Tasks</h3>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                {taskBreakdownResult.tasks?.length || 0} tasks • Total estimate: {taskBreakdownResult.total_estimate || 0}h
                              </p>
                            </div>
                            <Button onClick={addTasksFromBreakdown} variant="outline" className="w-full sm:w-auto text-sm">
                              <Plus className="w-4 h-4 mr-2" />
                              Add to Project
                            </Button>
                          </div>

                          <div className="grid gap-4">
                            {(taskBreakdownResult.tasks || []).map((task, index) => (
                              <Card key={index} className="border-l-4 border-l-emerald-500">
                                <CardContent className="pt-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h4 className="font-medium text-gray-900 mb-2">{task.title}</h4>
                                      <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                                      <div className="flex items-center space-x-4 text-xs">
                                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                                          {task.estimateHrs || task.estimate_hrs || 8}h
                                        </Badge>
                                        {task.tags && task.tags.length > 0 && (
                                          <div className="flex space-x-1">
                                            {task.tags.map((tag, tagIndex) => (
                                              <Badge key={tagIndex} variant="secondary" className="text-xs">
                                                {tag}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>

                          {taskBreakdownResult.notes && (
                            <Card className="bg-emerald-50 border-emerald-200">
                              <CardContent className="pt-4">
                                <div className="flex items-start">
                                  <Brain className="w-5 h-5 text-emerald-600 mt-0.5 mr-3 flex-shrink-0" />
                                  <div>
                                    <div className="font-medium text-emerald-900 mb-1">AI Notes</div>
                                    <div className="text-sm text-emerald-800">{taskBreakdownResult.notes}</div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      </AIResultCard>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Comment Assistant Tab */}
              <TabsContent value="chat" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <MessageSquare className="w-5 h-5 mr-2 text-violet-600" />
                      Smart Comment Assistant
                    </CardTitle>
                    <CardDescription>
                      Transform vague comments and requests into structured action items with clear owners and deadlines
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Enter a vague comment or request (e.g., 'Fix the login issue ASAP', 'The dashboard needs improvement', 'Users are complaining about performance')"
                        value={commentToRewrite}
                        onChange={(e) => setCommentToRewrite(e.target.value)}
                        className="min-h-[100px] text-sm resize-y"
                      />
                      <Button 
                        onClick={() => rewriteComment(false)} 
                        disabled={loading || !commentToRewrite.trim()}
                        className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Rewriting...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Rewrite Comment
                          </>
                        )}
                      </Button>
                    </div>

                    {commentRewriteResult && (
                      <AIResultCard
                        title="Structured Action Item"
                        cached={commentRewriteResult.cached}
                        cachedAt={commentRewriteResult.cached_at}
                        onRegenerate={() => rewriteComment(true)}
                      >
                        <div className="space-y-4">
                          <div className="p-4 bg-violet-50 rounded-lg relative">
                            <div className="font-medium text-violet-900 mb-2">Action Required</div>
                            <div className="text-violet-800">{commentRewriteResult.action}</div>
                            <button
                              onClick={() => {
                                const text = [
                                  `Action: ${commentRewriteResult.action}`,
                                  commentRewriteResult.owner ? `Owner: ${commentRewriteResult.owner}` : null,
                                  commentRewriteResult.deadline ? `Deadline: ${commentRewriteResult.deadline}` : null,
                                  `Priority: ${commentRewriteResult.priority}`,
                                  `Acceptance: ${commentRewriteResult.acceptance}`,
                                ].filter(Boolean).join('\n');
                                navigator.clipboard.writeText(text);
                                toast.success('Copied to clipboard');
                              }}
                              className="absolute top-3 right-3 flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 bg-white border border-violet-200 rounded-md px-2 py-1 transition-colors"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>

                          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            <div className="space-y-3">
                              <div>
                                <div className="text-sm font-medium text-gray-700">Priority</div>
                                <Badge 
                                  variant={commentRewriteResult.priority === 'High' ? 'destructive' : 
                                          commentRewriteResult.priority === 'Medium' ? 'default' : 'secondary'}
                                >
                                  {commentRewriteResult.priority}
                                </Badge>
                              </div>
                              {commentRewriteResult.owner && (
                                <div>
                                  <div className="text-sm font-medium text-gray-700">Suggested Owner</div>
                                  <div className="text-sm text-gray-600">{commentRewriteResult.owner}</div>
                                </div>
                              )}
                            </div>
                            <div className="space-y-3">
                              {commentRewriteResult.deadline && (
                                <div>
                                  <div className="text-sm font-medium text-gray-700">Suggested Deadline</div>
                                  <div className="text-sm text-gray-600">{commentRewriteResult.deadline}</div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">Acceptance Criteria</div>
                            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-800">
                              {commentRewriteResult.acceptance}
                            </div>
                          </div>
                        </div>
                      </AIResultCard>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      </div>
      <AICopilotPanel
        selectedProject={selectedProject}
        taskCount={tasks.length}
        dashboardInsights={dashboardInsights}
        recommendations={contextualRecommendations}
        loading={loading}
        onGenerateSprintPlan={() => generateSprintPlan(false)}
        onBreakdownGoal={(goal) => breakdownGoal(false, goal)}
        onRewriteComment={(comment) => rewriteComment(false, comment)}
      />
    </AppShell>
  );
};

// ==================== MY TASKS PAGE ====================
const STATUS_COLOR_MAP = {
  todo: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  done: 'bg-green-100 text-green-700',
};
const PRIORITY_COLOR_MAP = {
  1: 'bg-slate-100 text-slate-500',
  2: 'bg-amber-100 text-amber-700',
  3: 'bg-rose-100 text-rose-700',
  4: 'bg-rose-200 text-rose-900',
};
const PRIORITY_LABEL_MAP = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' };

const MyTasksPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const loadMyTasks = useCallback(() => {
    if (!currentOrg?.id) return;
    setLoading(true);
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/my-tasks`))
      .then(r => setTasks(r.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [currentOrg?.id]);

  useEffect(() => { loadMyTasks(); }, [loadMyTasks]);

  // Re-fetch whenever the tab regains focus, so status changes made elsewhere
  // (e.g. the Kanban board) show up without requiring a manual page reload.
  useEffect(() => {
    const onFocus = () => loadMyTasks();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadMyTasks]);

  const isOverdue = t => t.due_date && !['approved','done'].includes(t.status) && new Date(t.due_date) < new Date();
  const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

  const filtered = filterStatus === 'all' ? tasks : tasks.filter(t => t.status === filterStatus);

  const exportCSV = () => {
    const rows = [
      ['Title','Project','Status','Priority','Due Date'],
      ...tasks.map(t => [
        `"${(t.title||'').replace(/"/g,'""')}"`,
        t.project_name || '',
        t.status || '',
        PRIORITY_LABEL_MAP[t.priority] || '',
        t.due_date ? new Date(t.due_date).toISOString().slice(0,10) : '',
      ])
    ];
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(rows.map(r => r.join(',')).join('\n'));
    a.download = 'my_tasks.csv';
    a.click();
  };

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="p-4 sm:p-6 space-y-4 overflow-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">My Tasks</h2>
            <p className="text-sm text-muted-foreground">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''} assigned to you across all projects
              {tasks.filter(isOverdue).length > 0 && (
                <> · <span className="text-red-600 font-medium">{tasks.filter(isOverdue).length} overdue</span></>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={loadMyTasks} disabled={loading}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <Card className="dashboard-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-sm table-fixed w-full" style={{minWidth:'780px'}}>
              <colgroup>
                <col style={{width:'30%'}} />
                <col style={{width:'24%'}} />
                <col style={{width:'16%'}} />
                <col style={{width:'14%'}} />
                <col style={{width:'16%'}} />
              </colgroup>
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3 whitespace-nowrap">Task</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Project</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Priority</th>
                  <th className="text-left px-4 py-3 pr-6 whitespace-nowrap">Due Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground animate-pulse">Loading your tasks…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                    {tasks.length === 0 ? 'No tasks assigned to you yet.' : 'No tasks match the filter.'}
                  </td></tr>
                ) : filtered.map(task => {
                  const overdue = isOverdue(task);
                  const statusKey = String(task.status || 'todo').toLowerCase();
                  return (
                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/projects/${task.project_id}/board`)}
                          className="text-left font-medium text-gray-900 hover:text-[#4B1E87] truncate w-full block transition-colors">
                          {task.title}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f3eeff] text-[#4B1E87] font-medium truncate max-w-full">
                          {task.project_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLOR_MAP[statusKey] || 'bg-gray-100 text-gray-600'}`}>
                          {statusKey.replace(/_/g,' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${PRIORITY_COLOR_MAP[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                          {PRIORITY_LABEL_MAP[task.priority] || 'Medium'}
                        </span>
                      </td>
                      <td className="px-4 py-3 pr-6 text-xs">
                        {task.due_date ? (
                          <span className={`whitespace-nowrap ${overdue ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                            {overdue && '⚠ '}{fmt(task.due_date)}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
};

// ==================== PENDING APPROVALS PAGE ====================
const PendingApprovalsPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const load = () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/pending-approvals`))
      .then(r => setTasks(r.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const approve = async (task) => {
    setActionId(task.id + '_approve');
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${task.id}`), { status: 'approved' });
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success(`"${task.title}" approved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
    finally { setActionId(null); }
  };

  const reject = async (task) => {
    setActionId(task.id + '_reject');
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${task.id}`), { status: 'in_progress' });
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success(`"${task.title}" sent back to In Progress`);
    } catch (e) { toast.error('Failed to reject'); }
    finally { setActionId(null); }
  };

  const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
  const daysSince = d => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : 0;

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="p-4 sm:p-6 space-y-4 overflow-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Pending Approvals</h2>
            <p className="text-sm text-muted-foreground">
              Tasks submitted for review that need your approval
              {tasks.length > 0 && <> · <span className="text-amber-600 font-medium">{tasks.length} waiting</span></>}
            </p>
          </div>
          <button onClick={load}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Table */}
        <Card className="dashboard-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-sm table-fixed w-full" style={{minWidth:'1080px'}}>
              <colgroup>
                <col style={{width:'20%'}} />
                <col style={{width:'16%'}} />
                <col style={{width:'16%'}} />
                <col style={{width:'11%'}} />
                <col style={{width:'12%'}} />
                <col style={{width:'9%'}} />
                <col style={{width:'16%'}} />
              </colgroup>
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3 whitespace-nowrap">Task</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Project</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Submitted By</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Priority</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Due Date</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Waiting</th>
                  <th className="px-4 py-3 pr-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                ) : tasks.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-16 text-center">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400 opacity-60" />
                    <p className="text-sm font-semibold text-emerald-700">All caught up!</p>
                    <p className="text-xs text-muted-foreground mt-1">No tasks waiting for approval.</p>
                  </td></tr>
                ) : tasks.map(task => {
                  const days = daysSince(task.updated_at);
                  const isLong = days >= 2;
                  const busy = actionId?.startsWith(task.id);
                  return (
                    <tr key={task.id} className={`hover:bg-gray-50 transition-colors ${isLong ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/projects/${task.project_id}/board`)}
                          className="text-left font-medium text-gray-900 hover:text-[#4B1E87] truncate w-full block transition-colors">
                          {task.title}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f3eeff] text-[#4B1E87] font-medium truncate max-w-full">
                          {task.project_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 truncate">{task.assignee_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${PRIORITY_COLOR_MAP[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                          {PRIORITY_LABEL_MAP[task.priority] || 'Medium'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{task.due_date ? fmt(task.due_date) : '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`whitespace-nowrap ${isLong ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                          {days === 0 ? 'Today' : `${days}d`}
                        </span>
                      </td>
                      <td className="px-4 py-3 pr-6">
                        <div className="flex gap-1.5">
                          <button onClick={() => reject(task)} disabled={!!actionId}
                            className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50">
                            {actionId === task.id + '_reject' ? '…' : 'Reject'}
                          </button>
                          <button onClick={() => approve(task)} disabled={!!actionId}
                            className="text-xs px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50">
                            {actionId === task.id + '_approve' ? '…' : 'Approve'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
};

// ==================== ARCHIVED TASKS SECTION ====================
const ArchivedTasksSection = ({ orgId, projectId, canManage }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(getApiUrl(`/organizations/${orgId}/projects/${projectId}/tasks/archived`))
      .then(r => setTasks(r.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (orgId && projectId) load(); }, [orgId, projectId]);

  const restore = async (task) => {
    setRestoringId(task.id);
    try {
      await axios.post(getApiUrl(`/organizations/${orgId}/tasks/${task.id}/restore`));
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success(`"${task.title}" restored`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
    finally { setRestoringId(null); }
  };

  const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="p-4 sm:p-6 space-y-4 overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Archived Tasks</h2>
          <p className="text-sm text-muted-foreground">
            {tasks.length} archived task{tasks.length !== 1 ? 's' : ''} · restore to bring them back to the board
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <Card className="dashboard-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3 w-[40%]">Task</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Archived</th>
                {canManage && <th className="px-4 py-3 w-[100px]"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={canManage ? 5 : 4} className="px-4 py-12 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={canManage ? 5 : 4} className="px-4 py-12 text-center text-muted-foreground">No archived tasks.</td></tr>
              ) : tasks.map(task => (
                <tr key={task.id} className="hover:bg-gray-50 transition-colors opacity-75">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-700 truncate max-w-[280px]">{task.title}</p>
                    {task.description && <p className="text-xs text-gray-400 truncate max-w-[280px] mt-0.5">{task.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR_MAP[task.status] || 'bg-gray-100 text-gray-600'}`}>
                      {(task.status || 'todo').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${PRIORITY_COLOR_MAP[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                      {PRIORITY_LABEL_MAP[task.priority] || 'Medium'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{fmt(task.deleted_at)}</td>
                  {canManage && (
                    <td className="px-4 py-3">
                      <button onClick={() => restore(task)} disabled={!!restoringId}
                        className="text-xs px-2.5 py-1 rounded-lg border border-[#d4b8f0] text-[#4B1E87] hover:bg-[#f3eeff] transition-colors disabled:opacity-50">
                        {restoringId === task.id ? '…' : 'Restore'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ==================== PROJECT SETTINGS SECTION ====================
const PROJECT_COLORS = [
  '#6366f1','#8b5cf6','#ec4899','#ef4444','#f97316','#eab308',
  '#22c55e','#14b8a6','#06b6d4','#3b82f6','#64748b','#1e293b',
];

const ProjectSettingsSection = ({ orgId, projectId, project, canManage, onProjectUpdated }) => {
  const navigate = useNavigate();
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [color, setColor] = useState(project?.color || PROJECT_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setName(project?.name || '');
    setDescription(project?.description || '');
    setColor(project?.color || PROJECT_COLORS[0]);
  }, [project]);

  const save = async () => {
    if (!name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const res = await axios.patch(getApiUrl(`/organizations/${orgId}/projects/${projectId}`), {
        name: name.trim(),
        description: description.trim() || null,
        color,
      });
      onProjectUpdated?.(res.data);
      toast.success('Project updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteProject = async () => {
    setDeleting(true);
    try {
      await axios.delete(getApiUrl(`/organizations/${orgId}/projects/${projectId}`));
      toast.success('Project deleted');
      navigate('/projects');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); setDeleting(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-2xl overflow-auto">
      <div>
        <h2 className="text-xl font-semibold">Project Settings</h2>
        <p className="text-sm text-muted-foreground">Manage name, description, and project lifecycle</p>
      </div>

      {/* General */}
      <Card className="dashboard-panel">
        <div className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">General</h3>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Project Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!canManage}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={!canManage}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 resize-none"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600">Project Color</label>
            <div className="flex flex-wrap gap-2">
              {PROJECT_COLORS.map(c => (
                <button key={c} onClick={() => canManage && setColor(c)} disabled={!canManage}
                  style={{ background: c }}
                  className={`w-7 h-7 rounded-full transition-all ${color === c ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'opacity-70 hover:opacity-100'}`}
                />
              ))}
            </div>
          </div>
          {canManage && (
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#4B1E87] hover:bg-[#3A1669] text-white text-sm font-medium transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      {canManage && (
        <Card className="dashboard-panel border-rose-200">
          <div className="p-6 space-y-4">
            <h3 className="text-sm font-semibold text-rose-600 uppercase tracking-wide">Danger Zone</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Delete this project</p>
                <p className="text-xs text-gray-400">Permanently deletes the project and all its tasks. This cannot be undone.</p>
              </div>
              {!deleteConfirm ? (
                <button onClick={() => setDeleteConfirm(true)}
                  className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-600 text-xs font-medium hover:bg-rose-50 transition-colors">
                  Delete Project
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setDeleteConfirm(false)} className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={deleteProject} disabled={deleting}
                    className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Yes, Delete'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// ==================== AUDIT LOG PAGE ====================
const AuditLogPage = () => {
  const { currentOrg, organizations } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterAction, setFilterAction] = useState('');
  const orgRole = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  const isAdmin = ['OWNER', 'ADMIN'].includes(orgRole);

  const load = () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    const params = {};
    if (filterType && filterType !== 'all') params.entity_type = filterType;
    if (filterAction) params.action = filterAction;
    params.limit = 200;
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/audit-logs`), { params })
      .then(r => setLogs(r.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [currentOrg?.id, filterType, filterAction]);

  const exportCSV = () => {
    window.open(getApiUrl(`/organizations/${currentOrg.id}/audit-logs/export`), '_blank');
  };

  const fmt = d => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const ACTION_COLOR = {
    create: 'bg-emerald-100 text-emerald-700',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-rose-100 text-rose-700',
    archive: 'bg-amber-100 text-amber-700',
    restore: 'bg-indigo-100 text-[#4B1E87]',
  };
  const actionColor = (action) => {
    const verb = action?.split('.')?.[1] || action?.split('.')?.[0] || '';
    return ACTION_COLOR[verb] || 'bg-gray-100 text-gray-600';
  };

  const ENTITY_TYPES = ['task', 'project', 'organization_settings', 'department', 'team', 'employee', 'membership', 'session', 'api_key'];

  if (!isAdmin) {
    return (
      <AppShell header={<DashboardHeader />}>
        <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground p-8">
          <ShieldCheck className="w-12 h-12 opacity-20" />
          <p className="text-sm">Audit Log is only available to Owners and Admins.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="p-4 sm:p-6 space-y-4 overflow-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Audit Log</h2>
            <p className="text-sm text-muted-foreground">
              Full event history for your workspace · {logs.length} events shown
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <input value={filterAction} onChange={e => setFilterAction(e.target.value)}
              placeholder="Filter action…"
              className="text-xs border rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 w-36" />
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        <Card className="dashboard-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Time</th>
                  <th className="text-left px-4 py-3">Actor</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Entity Type</th>
                  <th className="text-left px-4 py-3">Entity ID</th>
                  <th className="text-left px-4 py-3">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground animate-pulse">Loading audit log…</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No events found.</td></tr>
                ) : logs.map((log, i) => (
                  <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmt(log.created_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 font-mono truncate max-w-[120px]">
                      {log.actor_user_id ? log.actor_user_id.slice(0, 8) + '…' : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${actionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{log.entity_type?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400 font-mono truncate max-w-[100px]">
                      {log.entity_id ? log.entity_id.slice(0, 8) + '…' : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{log.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
};

// ==================== ISSUES PAGE ====================
const IssuesPage = ({ orgId, projectId, currentUserId, canManage }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open'); // open | resolved
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueComments, setIssueComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [commentImageFile, setCommentImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const commentImageRef = useRef(null);

  const loadIssues = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/projects/${projectId}/issues`));
      setIssues(res.data || []);
    } catch { setIssues([]); } finally { setLoading(false); }
  };

  useEffect(() => { loadIssues(); }, [projectId]);

  const createIssue = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/projects/${projectId}/issues`), {
        title: newTitle.trim(), description: newDesc.trim() || null,
      });
      const newIssue = res.data;
      // upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        try {
          const form = new FormData();
          form.append('file', imageFile);
          await axios.post(getApiUrl(`/organizations/${orgId}/issues/${newIssue.id}/upload-image`), form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const refreshed = await axios.get(getApiUrl(`/organizations/${orgId}/projects/${projectId}/issues`));
          setIssues(refreshed.data || []);
        } finally { setUploadingImage(false); }
      } else {
        setIssues(prev => [newIssue, ...prev]);
      }
      setNewTitle(''); setNewDesc(''); setImageFile(null); setShowCreate(false);
      toast.success('Issue reported');
    } catch (e) { toast.error('Failed to create issue'); } finally { setCreating(false); }
  };

  const resolveIssue = async (issueId) => {
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/issues/${issueId}/resolve`));
      setIssues(prev => prev.map(i => i.id === issueId ? res.data : i));
      if (selectedIssue?.id === issueId) setSelectedIssue(res.data);
      toast.success('Issue marked as resolved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resolve issue'); }
  };

  const reopenIssue = async (issueId) => {
    try {
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/issues/${issueId}/reopen`));
      setIssues(prev => prev.map(i => i.id === issueId ? res.data : i));
      if (selectedIssue?.id === issueId) setSelectedIssue(res.data);
      toast.success('Issue reopened');
    } catch (e) { toast.error('Failed to reopen issue'); }
  };

  const openIssue = async (issue) => {
    setSelectedIssue(issue);
    setCommentsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${orgId}/issues/${issue.id}/comments`));
      setIssueComments(res.data || []);
    } catch { setIssueComments([]); } finally { setCommentsLoading(false); }
  };

  const postIssueComment = async () => {
    if (!commentText.trim() && !commentImageFile) return;
    setPostingComment(true);
    try {
      let imageFilename = null;
      if (commentImageFile) {
        const form = new FormData();
        form.append('file', commentImageFile);
        const imgRes = await axios.post(
          getApiUrl(`/organizations/${orgId}/issues/${selectedIssue.id}/comment-image`), form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        imageFilename = imgRes.data.filename;
      }
      const res = await axios.post(getApiUrl(`/organizations/${orgId}/issues/${selectedIssue.id}/comments`), {
        content: commentText.trim() || '(image)',
      });
      let commentWithImage = res.data;
      if (imageFilename) {
        const patchRes = await axios.patch(
          getApiUrl(`/organizations/${orgId}/issue-comments/${res.data.id}/set-image`),
          { filename: imageFilename }
        );
        commentWithImage = patchRes.data;
      }
      setIssueComments(prev => [...prev, commentWithImage]);
      setCommentText(''); setCommentImageFile(null);
    } catch (e) { toast.error('Failed to post comment'); } finally { setPostingComment(false); }
  };

  const getImageUrl = (issueId, filename) =>
    getApiUrl(`/organizations/${orgId}/issues/${issueId}/images/${filename}`);

  const filteredIssues = issues.filter(i => tab === 'open' ? i.status === 'open' : i.status === 'resolved');
  const fmt = d => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="flex h-full overflow-hidden">
      {/* Issue list */}
      <div className={`flex flex-col ${selectedIssue ? 'w-[420px] shrink-0 border-r border-slate-200' : 'flex-1'} overflow-hidden`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500" /> Issue Reports
            </h2>
            <Button size="sm" onClick={() => setShowCreate(s => !s)} className="h-7 text-xs gap-1">
              <Plus className="w-3 h-3" /> Report Issue
            </Button>
          </div>
          {/* Tabs */}
          <div className="flex gap-1">
            {['open', 'resolved'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${tab === t ? 'bg-indigo-100 text-[#4B1E87]' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t === 'open' ? `Open (${issues.filter(i => i.status === 'open').length})` : `Resolved (${issues.filter(i => i.status === 'resolved').length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="px-6 py-4 border-b border-indigo-100 bg-[#f3eeff]/50 space-y-3 shrink-0">
            <p className="text-xs font-semibold text-[#4B1E87]">New Issue Report</p>
            <Input
              value={newTitle} onChange={e => setNewTitle(e.target.value)}
              placeholder="Issue title (what went wrong?)" className="h-8 text-sm"
              onKeyDown={e => e.key === 'Enter' && createIssue()}
            />
            <textarea
              value={newDesc} onChange={e => setNewDesc(e.target.value)}
              placeholder="Describe the issue in detail… (optional)"
              rows={3}
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <div className="flex items-center gap-2">
              <input type="file" accept="image/*" ref={imageInputRef} className="hidden"
                onChange={e => setImageFile(e.target.files?.[0])} />
              <button
                onClick={() => imageInputRef.current?.click()}
                className="flex items-center gap-1.5 text-xs text-[#4B1E87] hover:text-[#3A1669] transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                {imageFile ? imageFile.name : 'Attach screenshot'}
              </button>
              {imageFile && (
                <button onClick={() => setImageFile(null)} className="text-xs text-rose-500 hover:text-rose-700">
                  <X className="w-3 h-3" />
                </button>
              )}
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setNewTitle(''); setNewDesc(''); setImageFile(null); }} className="h-7 text-xs">Cancel</Button>
                <Button size="sm" onClick={createIssue} disabled={creating || !newTitle.trim()} className="h-7 text-xs">
                  {creating ? 'Reporting…' : 'Report Issue'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm animate-pulse">Loading issues…</div>
          ) : filteredIssues.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <AlertCircle className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">{tab === 'open' ? 'No open issues — great job!' : 'No resolved issues yet'}</p>
            </div>
          ) : (
            filteredIssues.map(issue => (
              <button
                key={issue.id}
                onClick={() => openIssue(issue)}
                className={`w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors ${selectedIssue?.id === issue.id ? 'bg-[#f3eeff]/60 border-l-2 border-[#4B1E87]' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${issue.status === 'open' ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{issue.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[11px] text-muted-foreground">By {issue.reporter_name}</span>
                      <span className="text-[11px] text-muted-foreground">· {fmt(issue.created_at)}</span>
                      {issue.image_filenames?.length > 0 && (
                        <span className="text-[11px] text-[#4B1E87]">· {issue.image_filenames.length} image{issue.image_filenames.length > 1 ? 's' : ''}</span>
                      )}
                      {issue.status === 'resolved' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Resolved by {issue.resolved_by}</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Issue detail panel */}
      {selectedIssue && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-2 h-2 rounded-full shrink-0 ${selectedIssue.status === 'open' ? 'bg-rose-500' : 'bg-emerald-500'}`} />
              <h3 className="font-semibold text-sm truncate">{selectedIssue.title}</h3>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {selectedIssue.status === 'open' && (selectedIssue.reporter_id === currentUserId || canManage) && (
                <Button size="sm" onClick={() => resolveIssue(selectedIssue.id)}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Mark Resolved
                </Button>
              )}
              {selectedIssue.status === 'resolved' && canManage && (
                <Button size="sm" variant="outline" onClick={() => reopenIssue(selectedIssue.id)} className="h-7 text-xs">
                  Reopen
                </Button>
              )}
              <button onClick={() => setSelectedIssue(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Meta */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span>Reported by <strong className="text-slate-700">{selectedIssue.reporter_name}</strong></span>
              <span>· {fmt(selectedIssue.created_at)}</span>
              {selectedIssue.status === 'resolved' && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  Resolved by {selectedIssue.resolved_by} · {fmt(selectedIssue.resolved_at)}
                </span>
              )}
            </div>

            {/* Description */}
            {selectedIssue.description && (
              <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap">
                {selectedIssue.description}
              </div>
            )}

            {/* Images */}
            {selectedIssue.image_filenames?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Attachments</p>
                <div className="flex flex-wrap gap-3">
                  {selectedIssue.image_filenames.map(fn => (
                    <a key={fn} href={getImageUrl(selectedIssue.id, fn)} target="_blank" rel="noreferrer"
                      className="block rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-400 transition-colors">
                      <img src={getImageUrl(selectedIssue.id, fn)} alt="issue" className="w-40 h-28 object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Comments thread */}
            <div className="space-y-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Comments & Discussion</p>
              {commentsLoading ? (
                <p className="text-xs text-muted-foreground animate-pulse">Loading comments…</p>
              ) : issueComments.length === 0 ? (
                <p className="text-xs text-muted-foreground">No comments yet. Know the fix? Share it here!</p>
              ) : (
                <div className="space-y-4">
                  {issueComments.map(c => (
                    <div key={c.id} className="flex gap-3">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
                        {(c.user_name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">{c.user_name}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.content !== '(image)' ? c.content : ''}</p>
                        {c.image_filename && (
                          <a href={getImageUrl(selectedIssue.id, c.image_filename)} target="_blank" rel="noreferrer"
                            className="mt-2 block rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-400 transition-colors w-fit">
                            <img src={getImageUrl(selectedIssue.id, c.image_filename)} alt="comment" className="max-w-xs max-h-48 object-cover" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Post comment */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <textarea
                  value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Add a comment — describe what you found or how to fix it…"
                  rows={3}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postIssueComment(); }}
                />
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" ref={commentImageRef} className="hidden"
                    onChange={e => setCommentImageFile(e.target.files?.[0])} />
                  <button
                    onClick={() => commentImageRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs text-[#4B1E87] hover:text-[#3A1669] transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {commentImageFile ? commentImageFile.name : 'Attach image'}
                  </button>
                  {commentImageFile && (
                    <button onClick={() => setCommentImageFile(null)} className="text-xs text-rose-500">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  <Button size="sm" onClick={postIssueComment}
                    disabled={postingComment || (!commentText.trim() && !commentImageFile)}
                    className="h-7 text-xs gap-1 ml-auto">
                    <Send className="w-3 h-3" />{postingComment ? 'Posting…' : 'Comment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ASSIGN TASKS PAGE ====================
const AssignTasksPage = ({ tasks, members, sprints, orgId, projectId, canManage, onUpdate, onCreate, onDelete, onTaskClick }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSprint, setFilterSprint] = useState('all');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [importRows, setImportRows] = useState(null); // parsed CSV rows for preview
  const [importing, setImporting] = useState(false);
  const importFileRef = React.useRef(null);
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState('unassigned');
  const [newPriority, setNewPriority] = useState('medium');
  const [newStatus, setNewStatus] = useState('todo');
  const [newSprint, setNewSprint] = useState('none');
  const [newStartDate, setNewStartDate] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const newTitleRef = React.useRef(null);

  const STATUS_COLOR = {
    todo: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-blue-100 text-blue-700',
    in_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    done: 'bg-green-100 text-green-700',
  };

  const filtered = tasks.filter(t => {
    if (filterSprint !== 'all' && t.sprint_id !== filterSprint) return false;
    if (filterStatus === 'unassigned') return !t.assignee_id;
    if (filterStatus === 'active') return !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase());
    return true;
  });

  const getMember = id => members.find(m => m.user_id === id || m.id === id);
  const getSprint = id => sprints.find(s => s.id === id);

  const handleFieldSave = async (task, payload) => {
    setSavingId(task.id);
    try { await onUpdate(task.id, payload); }
    finally { setSavingId(null); }
  };

  const handleDelete = async (taskId) => {
    setDeletingId(taskId);
    try { await onDelete(taskId); }
    finally { setDeletingId(null); setConfirmDeleteId(null); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await onCreate({
        title: newTitle.trim(),
        priority: newPriority,
        status: newStatus,
        assignee_id: newAssignee === 'unassigned' ? null : newAssignee,
        sprint_id: newSprint === 'none' ? null : newSprint,
        start_date: newStartDate ? new Date(newStartDate).toISOString() : null,
        due_date: newDueDate ? new Date(newDueDate).toISOString() : null,
      });
      setNewTitle(''); setNewAssignee('unassigned'); setNewPriority('medium');
      setNewStatus('todo'); setNewSprint('none'); setNewStartDate(''); setNewDueDate('');
      setShowCreate(false);
    } finally { setCreating(false); }
  };

  const unassignedCount = tasks.filter(t => !t.assignee_id && !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase())).length;

  const parseImportCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) { toast.error('CSV must have a header and at least one row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, '_'));
      const titleIdx = headers.findIndex(h => h.includes('title') || h === 'name');
      if (titleIdx < 0) { toast.error('CSV must have a "Title" column'); return; }
      const statusIdx = headers.findIndex(h => h.includes('status'));
      const priorityIdx = headers.findIndex(h => h.includes('priority'));
      const dueDateIdx = headers.findIndex(h => h.includes('due'));
      const rows = lines.slice(1).map((line, i) => {
        const cols = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g) || line.split(',');
        const clean = v => (v || '').replace(/^"|"$/g, '').trim();
        const priorityMap = { low: 1, medium: 2, high: 3, critical: 4 };
        return {
          _row: i + 2,
          title: clean(cols[titleIdx]),
          status: statusIdx >= 0 ? (clean(cols[statusIdx]) || 'todo') : 'todo',
          priority: priorityIdx >= 0 ? (priorityMap[clean(cols[priorityIdx]).toLowerCase()] || 2) : 2,
          due_date: dueDateIdx >= 0 && clean(cols[dueDateIdx]) ? new Date(clean(cols[dueDateIdx])).toISOString() : null,
        };
      }).filter(r => r.title);
      setImportRows(rows);
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!importRows?.length) return;
    setImporting(true);
    let created = 0, failed = 0;
    for (const row of importRows) {
      try {
        await onCreate({ title: row.title, status: row.status, priority: row.priority, due_date: row.due_date });
        created++;
      } catch { failed++; }
    }
    toast.success(`Imported ${created} task${created !== 1 ? 's' : ''}${failed ? ` · ${failed} failed` : ''}`);
    setImportRows(null);
    setImporting(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Task Management</h2>
          <p className="text-sm text-muted-foreground">
            {tasks.length} tasks
            {unassignedCount > 0 && <> · <span className="text-amber-600 font-medium">{unassignedCount} unassigned</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterSprint} onValueChange={setFilterSprint}>
            <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sprints</SelectItem>
              {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tasks</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="unassigned">Unassigned only</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={() => {
              const rows = [
                ['ID','Title','Status','Priority','Assignee','Due Date','Sprint'],
                ...tasks.map(t => [
                  t.id,
                  `"${(t.title||'').replace(/"/g,'""')}"`,
                  t.status,
                  t.priority === 4 ? 'Critical' : t.priority === 3 ? 'High' : t.priority === 2 ? 'Medium' : 'Low',
                  members.find(m => (m.user_id||m.id) === t.assignee_id)?.full_name || '',
                  t.due_date ? new Date(t.due_date).toISOString().slice(0,10) : '',
                  sprints.find(s => s.id === t.sprint_id)?.name || '',
                ])
              ];
              const csv = rows.map(r => r.join(',')).join('\n');
              const a = document.createElement('a');
              a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
              a.download = 'tasks.csv';
              a.click();
            }}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Export CSV
          </button>
          {canManage && (
            <>
              <input ref={importFileRef} type="file" accept=".csv" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) parseImportCSV(f); e.target.value = ''; }} />
              <button onClick={() => importFileRef.current?.click()}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
                <Download className="w-3.5 h-3.5" /> Import CSV
              </button>
            </>
          )}
          {canManage && (
            <button onClick={() => { setShowCreate(v => !v); setTimeout(() => newTitleRef.current?.focus(), 50); }}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-[#4B1E87] hover:bg-[#3A1669] text-white transition-colors">
              <Plus className="w-3.5 h-3.5" /> New Task
            </button>
          )}
        </div>
      </div>

      {/* Inline create form */}
      {showCreate && canManage && (
        <Card className="border-[#d4b8f0] bg-[#f3eeff]/30">
          <CardContent className="pt-4 pb-4">
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Plus className="w-4 h-4 text-[#4B1E87]" />
                <span className="text-sm font-semibold text-[#4B1E87]">Create New Task</span>
              </div>
              <input
                ref={newTitleRef}
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="Task title…"
                required
                className="w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Select value={newAssignee} onValueChange={setNewAssignee}>
                  <SelectTrigger className="h-8 text-xs px-2 py-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.full_name || m.email}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger className="h-8 text-xs px-2 py-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-8 text-xs px-2 py-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="in_review">In Review</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={newSprint} onValueChange={setNewSprint}>
                  <SelectTrigger className="h-8 text-xs px-2 py-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Sprint</SelectItem>
                    {sprints.filter(s => s.status !== 'completed').map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Start Date</label>
                  <input type="date" value={newStartDate} onChange={e => setNewStartDate(e.target.value)}
                    className="w-full text-xs border rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Due Date</label>
                  <input type="date" value={newDueDate} onChange={e => setNewDueDate(e.target.value)}
                    min={newStartDate || undefined}
                    className="w-full text-xs border rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowCreate(false)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={creating || !newTitle.trim()}
                  className="text-xs px-4 py-1.5 rounded-lg bg-[#4B1E87] hover:bg-[#3A1669] disabled:opacity-50 text-white font-medium transition-colors flex items-center gap-1.5">
                  {creating ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Creating…</> : 'Create Task'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {unassignedCount > 0 && filterStatus !== 'unassigned' && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span><strong>{unassignedCount} task{unassignedCount !== 1 ? 's' : ''}</strong> still unassigned.</span>
          <button onClick={() => setFilterStatus('unassigned')} className="ml-auto text-xs underline">Show only</button>
        </div>
      )}

      {/* Table */}
      <Card className="dashboard-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3 w-[28%]">Task</th>
                <th className="text-left px-4 py-3">Sprint</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Assignee</th>
                <th className="px-4 py-3 w-[80px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  {tasks.length === 0 ? 'No tasks yet — click "New Task" to create one.' : 'No tasks match the filter.'}
                </td></tr>
              ) : filtered.map(task => {
                const member = getMember(task.assignee_id);
                const sprint = getSprint(task.sprint_id);
                const statusKey = String(task.status || 'todo').toLowerCase();
                const isSaving = savingId === task.id;
                const isDeleting = deletingId === task.id;
                const confirmDelete = confirmDeleteId === task.id;

                return (
                  <tr key={task.id} className={`transition-colors hover:bg-gray-50 ${!task.assignee_id ? 'bg-amber-50/20' : ''}`}>
                    {/* Title */}
                    <td className="px-4 py-3">
                      <button onClick={() => onTaskClick(task)}
                        className="text-left font-medium text-gray-900 hover:text-[#4B1E87] truncate max-w-[220px] block transition-colors">
                        {task.title}
                      </button>
                      {task.due_date && (
                        <div className={`text-xs mt-0.5 ${new Date(task.due_date) < new Date() && !['done','approved'].includes(statusKey) ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                          Due {new Date(task.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </div>
                      )}
                    </td>

                    {/* Sprint */}
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {canManage ? (
                        <Select value={task.sprint_id || 'none'} disabled={isSaving}
                          onValueChange={v => handleFieldSave(task, { sprint_id: v === 'none' ? null : v })}>
                          <SelectTrigger className="h-7 text-xs px-2 py-1 max-w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Sprint</SelectItem>
                            {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : sprint ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#f3eeff] text-[#4B1E87] font-medium">
                          <Zap className="w-3 h-3" />{sprint.name}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {canManage ? (
                        <Select value={statusKey} disabled={isSaving}
                          onValueChange={v => handleFieldSave(task, { status: v })}>
                          <SelectTrigger className="h-7 text-xs px-2 py-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">To Do</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="in_review">In Review</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[statusKey] || 'bg-gray-100 text-gray-600'}`}>
                          {statusKey.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-3">
                      {canManage ? (
                        <Select value={task.priority || 'medium'} disabled={isSaving}
                          onValueChange={v => handleFieldSave(task, { priority: v })}>
                          <SelectTrigger className="h-7 text-xs px-2 py-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                          task.priority === 'critical' ? 'bg-red-100 text-red-700'
                          : task.priority === 'high' ? 'bg-orange-100 text-orange-700'
                          : task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-slate-100 text-slate-600'
                        }`}>{task.priority || 'medium'}</span>
                      )}
                    </td>

                    {/* Assignee */}
                    <td className="px-4 py-3">
                      <div className="relative flex items-center gap-1">
                        {isSaving && <div className="w-3 h-3 border-2 border-[#4B1E87] border-t-transparent rounded-full animate-spin shrink-0" />}
                        {canManage ? (
                          <Select value={task.assignee_id || 'unassigned'} disabled={isSaving}
                            onValueChange={v => handleFieldSave(task, { assignee_id: v === 'unassigned' ? null : v })}>
                            <SelectTrigger className={`h-7 text-xs px-2 py-1 max-w-[150px] ${!task.assignee_id ? 'border-amber-300 text-amber-700' : 'text-gray-700'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {members.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.full_name || m.email}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : member ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 rounded-full bg-indigo-100 text-[#4B1E87] flex items-center justify-center text-xs font-semibold shrink-0">
                              {(member.full_name || member.email || '?')[0].toUpperCase()}
                            </div>
                            <span className="text-xs text-gray-700 truncate">{member.full_name || member.email}</span>
                          </div>
                        ) : <span className="text-xs text-amber-600">Unassigned</span>}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => onTaskClick(task)} title="Edit details"
                          className="p-1.5 rounded-lg hover:bg-[#f3eeff] text-gray-400 hover:text-[#4B1E87] transition-colors">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        {canManage && (
                          confirmDelete ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDelete(task.id)} disabled={isDeleting}
                                className="text-[10px] px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50">
                                {isDeleting ? '…' : 'Yes'}
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)}
                                className="text-[10px] px-2 py-1 rounded border hover:bg-gray-50 text-gray-600 transition-colors">
                                No
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(task.id)} title="Delete task"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* CSV Import Preview Modal */}
      {importRows && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900">Import Preview</h3>
                <p className="text-xs text-gray-500 mt-0.5">{importRows.length} task{importRows.length !== 1 ? 's' : ''} will be created</p>
              </div>
              <button onClick={() => setImportRows(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Title</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Priority</th>
                    <th className="text-left px-4 py-2">Due Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {importRows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800 max-w-[240px] truncate">{row.title}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{row.status}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{PRIORITY_LABEL_MAP[row.priority] || 'Medium'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{row.due_date ? new Date(row.due_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t">
              <button onClick={() => setImportRows(null)} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={runImport} disabled={importing}
                className="px-4 py-2 text-sm bg-[#4B1E87] hover:bg-[#3A1669] text-white rounded-lg disabled:opacity-50">
                {importing ? 'Importing…' : `Import ${importRows.length} Tasks`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TASK TIMELINE (countdown view) ====================
const TaskTimeline = ({ tasks, members, sprints, onTaskClick }) => {
  const [filterSprint, setFilterSprint] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getMember = (id) => members.find(m => m.user_id === id || m.id === id);

  const daysInfo = (task) => {
    if (!task.due_date) return null;
    const due = new Date(task.due_date);
    due.setHours(0, 0, 0, 0);
    const diff = Math.round((due - today) / 86400000);
    return { diff, due };
  };

  const filtered = tasks.filter(t => {
    if (filterSprint !== 'all' && t.sprint_id !== filterSprint) return false;
    if (filterStatus === 'active') return !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase());
    if (filterStatus === 'overdue') {
      const info = daysInfo(t);
      return info && info.diff < 0 && !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase());
    }
    return true;
  }).sort((a, b) => {
    const da = a.due_date ? new Date(a.due_date) : new Date('2099-01-01');
    const db = b.due_date ? new Date(b.due_date) : new Date('2099-01-01');
    return da - db;
  });

  const STATUS_COLORS = {
    todo: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-blue-100 text-blue-700',
    in_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    done: 'bg-green-100 text-green-700',
  };

  const overdueCount = tasks.filter(t => {
    const info = daysInfo(t);
    return info && info.diff < 0 && !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase());
  }).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Task Timeline</h2>
          <p className="text-sm text-muted-foreground">
            {filtered.length} tasks · {overdueCount > 0 && <span className="text-red-600 font-medium">{overdueCount} overdue</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterSprint} onValueChange={setFilterSprint}>
            <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sprints</SelectItem>
              {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-auto text-xs gap-1.5 px-2 py-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active tasks</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="all">All tasks</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      {overdueCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span><strong>{overdueCount} task{overdueCount !== 1 ? 's' : ''}</strong> past their due date — click to reassign or update the deadline.</span>
        </div>
      )}

      {/* Table */}
      <Card className="dashboard-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3 w-[35%]">Task</th>
                <th className="text-left px-4 py-3">Assignee</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Start</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-left px-4 py-3">Countdown</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No tasks match the current filter.</td></tr>
              ) : filtered.map(task => {
                const info = daysInfo(task);
                const isDone = ['done', 'approved'].includes(String(task.status).toLowerCase());
                const isOverdue = info && info.diff < 0 && !isDone;
                const member = getMember(task.assignee_id);
                const statusKey = String(task.status || 'todo').toLowerCase();

                let countdownEl = null;
                if (!task.due_date) {
                  countdownEl = <span className="text-gray-400 text-xs">No due date</span>;
                } else if (isDone) {
                  countdownEl = <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" />Done</span>;
                } else if (info.diff < 0) {
                  countdownEl = (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full animate-pulse">
                      <AlertCircle className="w-3 h-3" />{Math.abs(info.diff)}d overdue
                    </span>
                  );
                } else if (info.diff === 0) {
                  countdownEl = <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />Due today</span>;
                } else if (info.diff <= 3) {
                  countdownEl = <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />{info.diff}d left</span>;
                } else {
                  countdownEl = <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Clock className="w-3 h-3" />{info.diff}d left</span>;
                }

                return (
                  <tr
                    key={task.id}
                    onClick={() => onTaskClick(task)}
                    className={`cursor-pointer transition-colors hover:bg-gray-50 ${isOverdue ? 'bg-red-50/30' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[260px]">{task.title}</div>
                      {task.priority && <div className="text-xs text-gray-400 mt-0.5 capitalize">{task.priority} priority</div>}
                    </td>
                    <td className="px-4 py-3">
                      {member ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-indigo-100 text-[#4B1E87] flex items-center justify-center text-xs font-semibold shrink-0">
                            {(member.full_name || member.email || '?')[0].toUpperCase()}
                          </div>
                          <span className="text-xs text-gray-700 truncate max-w-[80px]">{member.full_name || member.email}</span>
                        </div>
                      ) : <span className="text-xs text-gray-400">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[statusKey] || 'bg-gray-100 text-gray-600'}`}>
                        {String(task.status || 'todo').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {task.start_date ? new Date(task.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {task.due_date ? (
                        <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                          {new Date(task.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">{countdownEl}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ==================== PROJECT WORKSPACE (full-page sub-routes) ====================
const ProjectWorkPage = () => {
  const { projectId, section = 'board' } = useParams();
  const { currentOrg, user, organizations } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [project,        setProject]        = useState(null);
  const [tasks,          setTasks]          = useState([]);
  const [sprints,        setSprints]        = useState([]);
  const [orgMembers,     setOrgMembers]     = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [dataLoading,    setDataLoading]    = useState(true);
  const [loading,        setLoading]        = useState(false);
  const [viewMode,       setViewMode]       = useState('kanban');
  const [selectedTask,   setSelectedTask]   = useState(null);
  const [userOrgRole,    setUserOrgRole]    = useState(null);
  const [projectRole,    setProjectRole]    = useState(null);

  // AI state
  const [sprintPlanResult,    setSprintPlanResult]    = useState(null);
  const [taskBreakdownResult, setTaskBreakdownResult] = useState(null);
  const [commentRewriteResult,setCommentRewriteResult]= useState(null);
  const [goalToBreakdown,     setGoalToBreakdown]     = useState('');
  const [commentToRewrite,    setCommentToRewrite]    = useState('');

  const ORG_SUPER_ROLES       = ['OWNER', 'ADMIN'];
  const PROJECT_EDITOR_ROLES  = ['PROJECT_MANAGER', 'SCRUM_MASTER', 'TEAM_LEAD'];
  const PROJECT_APPROVER_ROLES= ['PROJECT_MANAGER', 'SCRUM_MASTER', 'TEAM_LEAD'];

  const isReadOnlyViewer = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return false;
    return ['VIEWER', 'OBSERVER'].includes(projectRole);
  }, [userOrgRole, projectRole]);

  const canManageCurrentProject = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return true;
    return PROJECT_EDITOR_ROLES.includes(projectRole);
  }, [projectRole, userOrgRole]);

  const canApproveCurrentProject = useMemo(() => {
    if (ORG_SUPER_ROLES.includes(userOrgRole)) return true;
    return PROJECT_APPROVER_ROLES.includes(projectRole);
  }, [projectRole, userOrgRole]);

  // Realtime updates
  const taskLoadRef = useRef(null);
  const stableOnTaskChange = useCallback(() => taskLoadRef.current?.(), []);
  const { isConnected, presence } = useRealtimeProject(projectId, user, currentOrg, { onTaskChange: stableOnTaskChange });

  const loadingRef = useRef(false);

  useEffect(() => {
    if (!currentOrg || !projectId || !user) return;
    const membership = organizations?.find(o => o.id === currentOrg.id);
    setUserOrgRole(membership?.role?.toUpperCase() || 'MEMBER');
    loadData();
  }, [currentOrg?.id, projectId, user?.id]); // stable primitives — won't double-fire

  const loadData = async () => {
    if (!currentOrg || !projectId || loadingRef.current) return;
    loadingRef.current = true;
    setDataLoading(true);
    try {
      // Only the project itself is fatal — everything else degrades gracefully
      const [projRes, tasksRes, sprintsRes, membersRes, teamRes] = await Promise.all([
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}`)),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/tasks`)).catch(() => ({ data: [] })),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/sprints`)).catch(() => ({ data: [] })),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/members`)).catch(() => ({ data: [] })),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/team`)).catch(() => ({ data: [] })),
      ]);
      setProject(projRes.data);
      setTasks(tasksRes.data || []);
      setSprints(sprintsRes.data || []);
      setOrgMembers(membersRes.data || []);
      setProjectMembers(teamRes.data || []);
      const member = (teamRes.data || []).find(m => m.user_id === user?.id);
      if (member) setProjectRole(member.role_in_project?.toUpperCase() || 'DEVELOPER');
    } catch (err) {
      // Only fires if the project itself 404s (doesn't exist / no access)
      toast.error(err.response?.data?.detail || 'Failed to load project');
      navigate('/projects');
    } finally {
      setDataLoading(false);
      loadingRef.current = false;
    }
  };

  const loadTasks = useCallback(async () => {
    if (!currentOrg || !projectId) return;
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/tasks`));
      setTasks(res.data || []);
    } catch { toast.error('Failed to reload tasks'); }
  }, [currentOrg, projectId]);

  taskLoadRef.current = loadTasks;

  // Sprint CRUD
  const createSprint    = async (data) => { const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/sprints`), { ...data, project_id: projectId }); setSprints(p => [res.data, ...p]); toast.success('Sprint created'); return res.data; };
  const updateSprint    = async (id, data) => { const res = await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/sprints/${id}`), data); setSprints(p => p.map(s => s.id === id ? res.data : s)); toast.success('Sprint updated'); };
  const deleteSprint    = async (id) => { await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/sprints/${id}`)); setSprints(p => p.filter(s => s.id !== id)); toast.success('Sprint deleted'); };
  const startSprint     = async (id) => { const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/sprints/${id}/start`)); setSprints(p => p.map(s => s.id === id ? res.data : s)); toast.success('Sprint started'); };
  const completeSprint  = async (id) => { const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/sprints/${id}/complete`)); setSprints(p => p.map(s => s.id === id ? res.data : s)); toast.success('Sprint completed'); };
  const addTasksToSprint = async (sprintId, taskIds) => {
    await axios.post(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}/tasks`), { task_ids: taskIds });
    setTasks(prev => prev.map(t => taskIds.includes(t.id) ? { ...t, sprint_id: sprintId } : t));
    toast.success(`${taskIds.length} task${taskIds.length !== 1 ? 's' : ''} assigned to sprint`);
  };

  const removeTaskFromSprint = async (sprintId, taskId) => {
    await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/sprints/${sprintId}/tasks/${taskId}`));
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, sprint_id: null } : t));
  };

  // Re-fetch tasks + sprints (used after AI-plan steps auto-complete a task/sprint)
  const refreshSprintData = async () => {
    if (!currentOrg || !projectId) return;
    loadTasks();
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/sprints`));
      setSprints(res.data || []);
    } catch { /* keep current sprint state on failure */ }
  };

  // Open task from navigation state (e.g. from command palette search)
  useEffect(() => {
    const openId = location.state?.openTaskId;
    if (!openId || !tasks.length) return;
    const found = tasks.find(t => t.id === openId);
    if (found) {
      setSelectedTask(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openTaskId, tasks]);

  // Task ops
  const updateTaskDetail = async (taskId, payload) => {
    const res = await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}`), payload);
    setTasks(p => p.map(t => t.id === taskId ? res.data : t));
    if (selectedTask?.id === taskId) setSelectedTask(res.data);
    return res.data;
  };
  const updateTaskInline = async (taskId, payload) => {
    const prev = [...tasks];
    setTasks(cur => cur.map(t => t.id === taskId ? { ...t, ...payload } : t));
    try { await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}`), payload); await loadTasks(); }
    catch { setTasks(prev); toast.error('Failed to update task'); }
  };
  const moveTaskKanban = async (taskId, toStatus, position, rejectionComment = null) => {
    const prev = [...tasks];
    setTasks(tasks.map(t => t.id === taskId ? { ...t, status: toStatus, position } : t));
    try {
      const body = { to_status: toStatus, position };
      if (rejectionComment) body.rejection_comment = rejectionComment;
      await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}/move`), body);
      await loadTasks();
    } catch (err) { setTasks(prev); toast.error(err.response?.data?.detail || 'Failed to move task'); }
  };
  const archiveTaskKanban = async (taskId) => {
    try { await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}/archive`)); setTasks(p => p.filter(t => t.id !== taskId)); toast.success('Task archived'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to archive task'); }
  };
  const reorderKanbanColumn = async (status, taskIds) => {
    const prev = [...tasks];
    const ts = new Set(taskIds);
    setTasks(tasks.map(t => !ts.has(t.id) ? t : { ...t, position: taskIds.indexOf(t.id) }));
    try { await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/reorder`), { task_ids: taskIds, status }); await loadTasks(); }
    catch { setTasks(prev); toast.error('Failed to reorder'); }
  };
  const bulkTaskAction = async (taskIds, action, payload = {}) => {
    await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks/bulk`), { task_ids: taskIds, action, payload });
    await loadTasks();
  };

  const createTaskInProject = async ({ title, priority, status, assignee_id, sprint_id, start_date, due_date }) => {
    const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks`), {
      project_id: projectId, title,
      description: '',
      priority: priority === 'critical' ? 4 : priority === 'high' ? 3 : priority === 'medium' ? 2 : 1,
      status: status || 'todo',
      assignee_id: assignee_id || null,
      sprint_id: sprint_id || null,
      start_date: start_date || null,
      due_date: due_date || null,
    });
    setTasks(prev => [res.data, ...prev]);
    toast.success('Task created');
  };

  const deleteTask = async (taskId) => {
    await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/tasks/${taskId}`));
    setTasks(prev => prev.filter(t => t.id !== taskId));
    toast.success('Task deleted');
  };

  // AI
  const generateSprintPlan = async (force = false) => {
    if (tasks.length === 0) { toast.error('Add tasks first'); return; }
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/ai/sprint-plan`), {
        project_id: projectId,
        backlog_tasks: tasks.map(t => ({ id: t.id, title: t.title, estimateHrs: t.estimate_hrs || 8, priority: t.priority || 2 })),
        team_capacity: Math.max(projectMembers.length * 40, 40),
        sprint_weeks: 2, force_regenerate: force,
      });
      setSprintPlanResult(res.data);
      toast.success(res.data.cached ? 'Sprint plan from cache' : 'Sprint plan generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate sprint plan'); }
    finally { setLoading(false); }
  };
  const breakdownGoal = async (force = false) => {
    if (!goalToBreakdown.trim()) { toast.error('Enter a goal first'); return; }
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/ai/task-breakdown`), {
        goal: goalToBreakdown.trim(), project_context: project?.name || 'General',
        force_regenerate: force,
      });
      setTaskBreakdownResult(res.data);
      toast.success(res.data.cached ? 'Breakdown from cache' : 'Goal broken down!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to break down goal'); }
    finally { setLoading(false); }
  };
  const rewriteComment = async (force = false) => {
    if (!commentToRewrite.trim()) { toast.error('Enter a comment first'); return; }
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/ai/comment-rewrite`), {
        comment: commentToRewrite.trim(), project_context: project?.name || 'General',
        force_regenerate: force,
      });
      setCommentRewriteResult(res.data);
      toast.success(res.data.cached ? 'Comment from cache' : 'Comment rewritten!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to rewrite comment'); }
    finally { setLoading(false); }
  };
  const addTasksFromBreakdown = async () => {
    if (!taskBreakdownResult) return;
    try {
      for (const task of taskBreakdownResult.tasks) {
        await axios.post(getApiUrl(`/organizations/${currentOrg.id}/tasks`), {
          project_id: projectId, title: task.title, description: task.description,
          estimate_hrs: task.estimateHrs, priority: 2, tags: task.tags || [],
        });
      }
      await loadTasks();
      toast.success(`Added ${taskBreakdownResult.tasks.length} tasks!`);
    } catch { toast.error('Failed to add tasks'); }
  };

  const [creatingSprintsFromPlan, setCreatingSprintsFromPlan] = useState(false);
  const createSprintsFromPlan = async () => {
    if (!sprintPlanResult?.sprints?.length) return;
    setCreatingSprintsFromPlan(true);
    try {
      const today = new Date();
      for (let i = 0; i < sprintPlanResult.sprints.length; i++) {
        const s = sprintPlanResult.sprints[i];
        const start = new Date(today); start.setDate(today.getDate() + i * 14);
        const end = new Date(start); end.setDate(start.getDate() + 13);
        const newSprint = await createSprint({
          name: `Sprint ${s.sprintNumber || i + 1}`,
          goal: sprintPlanResult.recommendations?.[i] || `Sprint ${i + 1} goals`,
          start_date: start.toISOString(), end_date: end.toISOString(),
        });
        if (s.tasks?.length && newSprint?.id) {
          const matchedIds = s.tasks.map(t => t.id).filter(id => tasks.find(tk => tk.id === id));
          if (matchedIds.length) await addTasksToSprint(newSprint.id, matchedIds);
        }
      }
      toast.success(`${sprintPlanResult.sprints.length} sprint${sprintPlanResult.sprints.length > 1 ? 's' : ''} created!`);
    } catch { toast.error('Failed to create sprints'); }
    finally { setCreatingSprintsFromPlan(false); }
  };

  if (dataLoading) return (
    <AppShell header={<DashboardHeader />}>
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="w-6 h-6 border-2 border-[#4B1E87] border-t-transparent rounded-full animate-spin mr-3" />
        Loading project…
      </div>
    </AppShell>
  );

  const SECTION_LABEL = { board: 'Board', timeline: 'Timeline', sprints: 'Sprints', assign: 'Assign Tasks', issues: 'Issue Reports', archived: 'Archived Tasks', ai: 'AI Tools', members: 'Members', settings: 'Project Settings' };

  return (
    <AppShell header={<DashboardHeader />}>
      {/* Breadcrumb + project header */}
      <div className="border-b bg-white dark:bg-gray-900 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => navigate('/projects')} className="text-muted-foreground hover:text-foreground transition-colors">Projects</button>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold text-foreground truncate max-w-[200px]">{project?.name}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-muted-foreground">{SECTION_LABEL[section] || 'Board'}</span>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" title="Live" />}
          <Badge variant="outline" className="text-xs">{tasks.length} tasks</Badge>
        </div>
      </div>

      {/* ── Board ── */}
      {section === 'board' && (
        <div className="p-4 sm:p-6 space-y-4 overflow-auto">
          <SprintPanel
            sprints={sprints} tasks={tasks} members={orgMembers}
            orgId={currentOrg?.id} projectId={projectId}
            canManage={canManageCurrentProject}
            onCreateSprint={createSprint} onUpdateSprint={updateSprint}
            onDeleteSprint={deleteSprint} onStartSprint={startSprint}
            onCompleteSprint={completeSprint} onAddTasksToSprint={addTasksToSprint}
            onRemoveTaskFromSprint={removeTaskFromSprint} onRefresh={refreshSprintData}
          />
          <Card className="dashboard-panel">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-[#4B1E87]">
                    <Target className="w-5 h-5" />
                  </span>
                  {viewMode === 'kanban' ? 'Kanban Board' : 'Task List'}
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{tasks.length} tasks</Badge>
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <button onClick={() => setViewMode('kanban')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'kanban' ? 'bg-[#4B1E87] text-white' : 'bg-white text-muted-foreground hover:bg-slate-50'}`}>
                      Board
                    </button>
                    <button onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-[#4B1E87] text-white' : 'bg-white text-muted-foreground hover:bg-slate-50'}`}>
                      List
                    </button>
                  </div>
                </div>
              </CardTitle>
              <CardDescription>
                {viewMode === 'kanban'
                  ? 'Drag and drop tasks across columns with realtime syncing.'
                  : 'Sortable table view with bulk actions and full task details.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {viewMode === 'kanban' ? (
                <ModernKanban
                  tasks={tasks} members={orgMembers} sprints={sprints}
                  currentUserId={user?.id}
                  canManageBoard={canManageCurrentProject}
                  canApprove={canApproveCurrentProject}
                  isReadOnly={isReadOnlyViewer}
                  busy={loading}
                  onMoveTask={moveTaskKanban}
                  onReorderColumn={reorderKanbanColumn}
                  onUpdateTask={updateTaskInline}
                  onArchiveTask={canManageCurrentProject ? archiveTaskKanban : null}
                  onTaskClick={setSelectedTask}
                />
              ) : (
                <TaskListView
                  tasks={tasks} members={orgMembers} sprints={sprints}
                  orgId={currentOrg?.id}
                  onTaskClick={setSelectedTask}
                  onBulkAction={bulkTaskAction}
                  canManage={canManageCurrentProject}
                  currentUserId={user?.id}
                />
              )}
            </CardContent>
          </Card>
          {selectedTask && (
            <TaskDetailModal
              task={selectedTask} members={orgMembers} sprints={sprints}
              orgId={currentOrg?.id}
              onClose={() => setSelectedTask(null)}
              onUpdate={updateTaskDetail}
              canManage={canManageCurrentProject}
              canEditMetadata={canManageCurrentProject}
              currentUserId={user?.id}
            />
          )}
        </div>
      )}

      {/* ── Timeline ── */}
      {section === 'timeline' && (
        <div className="p-4 sm:p-6 overflow-auto">
          <TaskTimeline tasks={tasks} members={orgMembers} sprints={sprints} onTaskClick={setSelectedTask} />
          {selectedTask && (
            <TaskDetailModal
              task={selectedTask} members={orgMembers} sprints={sprints}
              orgId={currentOrg?.id}
              onClose={() => setSelectedTask(null)}
              onUpdate={updateTaskDetail}
              canManage={canManageCurrentProject}
              canEditMetadata={canManageCurrentProject}
              currentUserId={user?.id}
            />
          )}
        </div>
      )}

      {/* ── Sprints ── */}
      {section === 'sprints' && (
        <div className="p-4 sm:p-6 space-y-4 overflow-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-xl font-semibold">Sprint Management</h2>
              <p className="text-sm text-muted-foreground">{sprints.length} sprint{sprints.length !== 1 ? 's' : ''} · {tasks.length} tasks</p>
            </div>
          </div>
          <SprintPanel
            sprints={sprints} tasks={tasks} members={orgMembers}
            orgId={currentOrg?.id} projectId={projectId}
            canManage={canManageCurrentProject}
            onCreateSprint={createSprint} onUpdateSprint={updateSprint}
            onDeleteSprint={deleteSprint} onStartSprint={startSprint}
            onCompleteSprint={completeSprint} onAddTasksToSprint={addTasksToSprint}
            onRemoveTaskFromSprint={removeTaskFromSprint} onRefresh={refreshSprintData}
          />
          {/* Task list below sprint panel so you can see all tasks */}
          <Card className="dashboard-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                  <Target className="w-4 h-4" />
                </span>
                All Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TaskListView
                tasks={tasks} members={orgMembers} sprints={sprints}
                orgId={currentOrg?.id}
                onTaskClick={setSelectedTask}
                onBulkAction={bulkTaskAction}
                canManage={canManageCurrentProject}
                currentUserId={user?.id}
              />
            </CardContent>
          </Card>
          {selectedTask && (
            <TaskDetailModal
              task={selectedTask} members={orgMembers} sprints={sprints}
              orgId={currentOrg?.id}
              onClose={() => setSelectedTask(null)}
              onUpdate={updateTaskDetail}
              canManage={canManageCurrentProject}
              canEditMetadata={canManageCurrentProject}
              currentUserId={user?.id}
            />
          )}
        </div>
      )}

      {/* ── Assign Tasks ── */}
      {section === 'assign' && (
        <div className="p-4 sm:p-6 overflow-auto">
          <AssignTasksPage
            tasks={tasks}
            members={orgMembers}
            sprints={sprints}
            orgId={currentOrg?.id}
            projectId={projectId}
            canManage={canManageCurrentProject}
            onUpdate={updateTaskDetail}
            onCreate={createTaskInProject}
            onDelete={deleteTask}
            onTaskClick={setSelectedTask}
          />
          {selectedTask && (
            <TaskDetailModal
              task={selectedTask} members={orgMembers} sprints={sprints}
              orgId={currentOrg?.id}
              onClose={() => setSelectedTask(null)}
              onUpdate={updateTaskDetail}
              canManage={canManageCurrentProject}
              canEditMetadata={canManageCurrentProject}
              currentUserId={user?.id}
            />
          )}
        </div>
      )}

      {/* ── Issue Reports ── */}
      {section === 'issues' && (
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(100vh - 112px)' }}>
          <IssuesPage
            orgId={currentOrg?.id}
            projectId={projectId}
            currentUserId={user?.id}
            canManage={canManageCurrentProject}
          />
        </div>
      )}

      {/* ── Archived Tasks ── */}
      {section === 'archived' && (
        <ArchivedTasksSection
          orgId={currentOrg?.id}
          projectId={projectId}
          canManage={canManageCurrentProject}
        />
      )}

      {/* ── Project Settings ── */}
      {section === 'settings' && (
        <ProjectSettingsSection
          orgId={currentOrg?.id}
          projectId={projectId}
          project={project}
          canManage={canManageCurrentProject}
          onProjectUpdated={(updated) => setProject(updated)}
        />
      )}

      {/* ── AI Tools ── */}
      {section === 'ai' && (
        <div className="p-4 sm:p-6 space-y-6 overflow-auto">
          <div className="mb-2">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#4B1E87]" /> AI Tools
            </h2>
            <p className="text-sm text-muted-foreground">Sprint planning, goal breakdown, and comment structuring — powered by AI</p>
          </div>

          {/* Sprint Plan */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Calendar className="w-5 h-5 mr-2 text-[#4B1E87]" />AI Project Sprint Planning</CardTitle>
              <CardDescription>AI generates a full project-level sprint plan — distributes all backlog tasks across multiple sprints based on team capacity and dependencies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-[#f3eeff] rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-[#3A1669]">Ready to plan your sprint?</div>
                  <div className="text-sm text-[#4B1E87] mt-1">{tasks.length} tasks in backlog · {Math.max(projectMembers.length * 40, 40)}h team capacity</div>
                </div>
                <Button onClick={() => generateSprintPlan(false)} disabled={loading || tasks.length === 0}
                  className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5] w-full sm:w-auto">
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Generating…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Sprint Plan</>}
                </Button>
              </div>
              {sprintPlanResult && (
                <AIResultCard title="Sprint Planning Results" cached={sprintPlanResult.cached} cachedAt={sprintPlanResult.cached_at} onRegenerate={() => generateSprintPlan(true)}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {sprintPlanResult.sprints?.map((sprint, i) => (
                      <Card key={i} className="border-l-4 border-l-indigo-500">
                        <CardHeader className="pb-3"><CardTitle className="text-lg">Sprint {sprint.sprintNumber}</CardTitle><CardDescription>Capacity: {sprint.capacityUsed || sprint.capacity_used || 0}h</CardDescription></CardHeader>
                        <CardContent>
                          {(sprint.tasks || []).slice(0, 3).map((t, ti) => (
                            <div key={ti} className="flex items-center justify-between text-sm mb-1"><span className="truncate">{t.title}</span><Badge variant="outline">{t.estimateHrs || 8}h</Badge></div>
                          ))}
                          {(sprint.tasks || []).length > 3 && <div className="text-xs text-gray-500">+{(sprint.tasks || []).length - 3} more</div>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {sprintPlanResult.risks?.length > 0 && (
                    <Card className="border-l-4 border-l-orange-500 mt-4">
                      <CardHeader><CardTitle className="text-base text-orange-700">Risk Analysis</CardTitle></CardHeader>
                      <CardContent>
                        {sprintPlanResult.risks.map((r, i) => (
                          <div key={i} className="p-3 bg-orange-50 rounded-lg mb-2">
                            <div className="font-medium text-orange-900">{r.risk}</div>
                            <div className="text-sm text-orange-700 mt-1"><strong>Impact:</strong> {r.impact} · <strong>Mitigation:</strong> {r.mitigation}</div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {sprintPlanResult.sprints?.length > 0 && (
                    <div className="flex justify-end pt-2">
                      <Button onClick={createSprintsFromPlan} disabled={creatingSprintsFromPlan} className="bg-[#4B1E87] hover:bg-[#3A1669] text-white">
                        {creatingSprintsFromPlan
                          ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Creating…</>
                          : <><Plus className="w-4 h-4 mr-2" />Create {sprintPlanResult.sprints.length} Sprint{sprintPlanResult.sprints.length > 1 ? 's' : ''}</>}
                      </Button>
                    </div>
                  )}
                </AIResultCard>
              )}
            </CardContent>
          </Card>

          {/* Goal Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Target className="w-5 h-5 mr-2 text-emerald-600" />Goal → Task Breakdown</CardTitle>
              <CardDescription>Transform high-level goals into actionable tasks with estimates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Textarea placeholder="Enter your goal (e.g., 'Build user authentication system')" value={goalToBreakdown} onChange={e => setGoalToBreakdown(e.target.value)} className="min-h-[100px] text-sm resize-y" />
                <Button onClick={() => breakdownGoal(false)} disabled={loading || !goalToBreakdown.trim()} className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]">
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Breaking Down…</> : <><Brain className="w-4 h-4 mr-2" />Break Down Goal</>}
                </Button>
              </div>
              {taskBreakdownResult && (
                <AIResultCard title="Task Breakdown Results" cached={taskBreakdownResult.cached} cachedAt={taskBreakdownResult.cached_at} onRegenerate={() => breakdownGoal(true)}>
                  <div className="flex items-center justify-between mb-4">
                    <div><h3 className="font-semibold">Generated Tasks</h3><p className="text-sm text-gray-600">{taskBreakdownResult.tasks?.length || 0} tasks · {taskBreakdownResult.total_estimate || 0}h total</p></div>
                    <Button onClick={addTasksFromBreakdown} variant="outline"><Plus className="w-4 h-4 mr-2" />Add to Project</Button>
                  </div>
                  <div className="grid gap-3">
                    {(taskBreakdownResult.tasks || []).map((task, i) => (
                      <Card key={i} className="border-l-4 border-l-emerald-500">
                        <CardContent className="pt-4">
                          <h4 className="font-medium mb-1">{task.title}</h4>
                          <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                          <div className="flex gap-2 flex-wrap">
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{task.estimateHrs || 8}h</Badge>
                            {(task.tags || []).map((tag, ti) => <Badge key={ti} variant="secondary" className="text-xs">{tag}</Badge>)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AIResultCard>
              )}
            </CardContent>
          </Card>

          {/* Comment AI */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><MessageSquare className="w-5 h-5 mr-2 text-violet-600" />Smart Comment Assistant</CardTitle>
              <CardDescription>Transform vague comments into structured action items with clear owners and deadlines</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Textarea placeholder="Enter a vague comment (e.g., 'Fix the login issue ASAP')" value={commentToRewrite} onChange={e => setCommentToRewrite(e.target.value)} className="min-h-[100px] text-sm resize-y" />
                <Button onClick={() => rewriteComment(false)} disabled={loading || !commentToRewrite.trim()} className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]">
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Rewriting…</> : <><Sparkles className="w-4 h-4 mr-2" />Rewrite Comment</>}
                </Button>
              </div>
              {commentRewriteResult && (
                <AIResultCard title="Structured Action Item" cached={commentRewriteResult.cached} cachedAt={commentRewriteResult.cached_at} onRegenerate={() => rewriteComment(true)}>
                  <div className="p-4 bg-violet-50 rounded-lg mb-4 relative">
                    <div className="font-medium text-violet-900 mb-2">Action Required</div>
                    <div className="text-violet-800">{commentRewriteResult.action}</div>
                    <button
                      onClick={() => {
                        const text = [
                          `Action: ${commentRewriteResult.action}`,
                          commentRewriteResult.owner ? `Owner: ${commentRewriteResult.owner}` : null,
                          commentRewriteResult.deadline ? `Deadline: ${commentRewriteResult.deadline}` : null,
                          `Priority: ${commentRewriteResult.priority}`,
                          commentRewriteResult.acceptance ? `Acceptance: ${commentRewriteResult.acceptance}` : null,
                        ].filter(Boolean).join('\n');
                        navigator.clipboard.writeText(text);
                        toast.success('Copied to clipboard');
                      }}
                      className="absolute top-3 right-3 flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 bg-white border border-violet-200 rounded-md px-2 py-1 transition-colors"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div><div className="text-sm font-medium mb-1">Priority</div><Badge variant={commentRewriteResult.priority === 'High' ? 'destructive' : 'default'}>{commentRewriteResult.priority}</Badge></div>
                    {commentRewriteResult.owner && <div><div className="text-sm font-medium mb-1">Suggested Owner</div><div className="text-sm text-gray-600">{commentRewriteResult.owner}</div></div>}
                    {commentRewriteResult.deadline && <div><div className="text-sm font-medium mb-1">Suggested Deadline</div><div className="text-sm text-gray-600">{commentRewriteResult.deadline}</div></div>}
                  </div>
                  {commentRewriteResult.acceptance && <div><div className="text-sm font-medium mb-2">Acceptance Criteria</div><div className="p-3 bg-gray-50 rounded-lg text-sm">{commentRewriteResult.acceptance}</div></div>}
                </AIResultCard>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </AppShell>
  );
};

// ==================== USER GUIDE PAGE ====================
const GUIDE_SECTIONS = [
  {
    id: 'getting-started', label: 'Getting Started', icon: '🚀',
    content: [
      { title: 'What is SyncWork?', body: 'SyncWork is an AI-powered project management platform that helps teams plan, track, and deliver work. It combines Kanban boards, list views, sprint management, timeline tracking, subtasks, file attachments, time logging, integrations, and AI assistance — all in one place.' },
      { title: 'Sign Up & Login', body: 'Go to the homepage and click "Get Started" to create an account. Enter your email and password. You\'ll receive a verification email — click the link to activate your account.\n\nIf you already have an account, click "Sign In" and enter your credentials. Use "Forgot password?" to reset via a one-time email code.' },
      { title: 'Onboarding Wizard', body: 'First-time users see a 6-step onboarding wizard after login that walks you through:\n1. Creating your organization\n2. Inviting team members\n3. Creating your first project\n4. Adding tasks\n5. Exploring AI tools\n6. Getting started\n\nYou can skip any step or the whole wizard and come back later.' },
      { title: 'Create Your Organization', body: 'After logging in for the first time, you\'ll be prompted to create an organization (your company/team workspace). Give it a name and you\'re ready to go. You can invite team members from the Team section.' },
      { title: 'Navigate the App', body: 'The left sidebar is your main navigation:\n• Dashboard — Home, AI tools, project summary\n• My Tasks — Tasks assigned to you across all projects\n• Approvals — Tasks waiting on your review or sign-off\n• Projects — All your projects\n• Analytics — Charts and metrics\n• Directory — Team members, teams, and groups\n• Integrations — Slack, GitHub, webhooks\n• Audit Log — Full activity history (Admins and Owners only)\n• Enterprise — Organization-wide console: security settings, API keys, AI savings tracker\n• Billing — Plan and subscription management\n• Settings — Profile, security, preferences\n• Help & Support — FAQs and contact form\n• User Guide — This page\n\nWhen inside a project, the sidebar expands to show: Board, Timeline, Sprints, Assign Tasks, AI Tools, Members, Archived, Settings.' },
    ]
  },
  {
    id: 'dashboard', label: 'Dashboard', icon: '📊',
    content: [
      { title: 'Dashboard Overview', body: 'The Dashboard is your home screen after login. It shows a summary of your selected project — completion rate, overdue tasks, total tasks, team members, and AI-powered contextual recommendations.' },
      { title: 'Project Selector', body: 'Use the Project dropdown at the top of the right panel to switch between your projects. All stats, recommendations, and AI tools update instantly for the selected project.' },
      { title: 'Contextual Recommendations', body: 'The AI analyzes your project data and surfaces up to 3 actionable recommendations — such as resolving overdue tasks, completing in-progress work, or kicking off your next sprint. Click any recommendation to act on it.' },
      { title: 'AI Sprint Plan', body: 'Click "Generate Sprint Plan" on the Dashboard AI tab to have the AI distribute all your backlog tasks across multiple 2-week sprints based on team capacity.\n\nAfter generating:\n• Review the sprint breakdown and risk analysis\n• Click "Create N Sprints in Project" to instantly create all suggested sprints in the project\n• Click "Regenerate" to get a fresh AI plan' },
      { title: 'Goal → Task Breakdown', body: 'Type a high-level goal (e.g. "Build payment system") and click "Break Down Goal". The AI generates 3–7 actionable tasks with descriptions, time estimates, and tags.\n\nClick "Add to Project" to create all tasks in your selected project in one click.' },
      { title: 'Comment AI Rewriter', body: 'Paste a vague comment (e.g. "Fix this ASAP") and click "Rewrite Comment". The AI returns a structured action item with clear action, priority, suggested owner, deadline, and acceptance criteria.\n\nClick the "Copy" button to copy the result to your clipboard.' },
    ]
  },
  {
    id: 'projects', label: 'Projects', icon: '📁',
    content: [
      { title: 'Projects List', body: 'Click "Projects" in the sidebar to see all your projects. Each card shows the project name, description, member count, task count, and a color stripe if a project color is set. Click any card to open the project.' },
      { title: 'Create a Project', body: 'Click "New Project" on the Projects page. Enter a name, description, and optionally select a methodology (Scrum, Kanban, etc.). The project is created immediately and appears in your projects list.' },
      { title: 'Project Sub-navigation', body: 'When you open a project, the sidebar expands to show project-specific tools:\n• Board — Kanban board + list view\n• Timeline — Due date countdown table\n• Sprints — Sprint management\n• Assign Tasks — Full task table with CSV import\n• AI Tools — Sprint planner, goal breakdown, comment AI\n• Members — Project team and roles\n• Archived — Archived/deleted tasks with restore option\n• Settings — Project name, description, color, and danger zone\n\nClick "← Projects" at the top to return to the projects list.' },
      { title: 'Project Settings', body: 'Open a project → Settings (in the sidebar). From here you can:\n• Edit the project name and description\n• Pick a project color (shown as a stripe on the project card)\n• Delete the project (Danger Zone — permanent and cannot be undone)\n\nOnly Project Managers, Admins, and Owners can change project settings.' },
      { title: 'Archived Tasks', body: 'Open a project → Archived. This shows all tasks that were archived (soft-deleted). Each row has a "Restore" button to bring the task back to active status. Archived tasks do not appear on the board or in analytics.' },
    ]
  },
  {
    id: 'board', label: 'Board & List View', icon: '🗂️',
    content: [
      { title: 'Kanban Columns', body: 'The Board view shows tasks in 5 columns: To Do → In Progress → In Review → Approved → Done. Drag and drop cards between columns to update their status in real time.' },
      { title: 'Task Cards', body: 'Each Kanban card shows:\n• Task title\n• Priority badge (Low / Medium / High / Critical)\n• Assignee avatar\n• Due date (turns red if overdue)\n• Tags as colored chips\n• Subtask count badge\n• Estimate in hours\n\nClick any card to open the full Task Detail modal.' },
      { title: 'List / Table View', body: 'Use the Board/List toggle in the top-right to switch to a sortable table view. The list shows all task fields in rows. Click any column header to sort. Click a row to open the task detail.' },
      { title: 'Bulk Actions', body: 'In List view, check the checkboxes on the left of task rows to select multiple tasks. A bulk action toolbar appears at the bottom with options to:\n• Change status\n• Change priority\n• Assign to a team member\n• Add to a sprint\n• Archive selected tasks\n• Delete selected tasks' },
      { title: 'Sprint Panel', body: 'Above the board you\'ll see the Sprint Panel showing the active sprint\'s name, goal, dates, and a progress bar (tasks done / total). You can create, edit, start, or complete sprints directly from this panel.' },
      { title: 'Create a Task on the Board', body: 'Click the "+" button at the top of any Kanban column to create a task directly in that status column. For full task details (description, due date, tags, estimate), use the Assign Tasks page instead.' },
      { title: 'Task Approval Flow', body: 'SyncWork has a two-stage completion flow:\n1. Developer submits the task → moves to "In Review"\n2. Team Lead / Project Manager clicks Approve\n3. Task moves to "Approved" or "Done"\n\nOnly authorized roles (PM, Team Lead, Scrum Master, Admin, Owner) can approve tasks.' },
    ]
  },
  {
    id: 'task-detail', label: 'Task Detail', icon: '📋',
    content: [
      { title: 'Opening a Task', body: 'Click any task card on the board, any row in the list view, or any task title in the Assign Tasks table to open the Task Detail modal. You can also search for a task using Cmd/Ctrl+K and select it to open it directly on its project board.' },
      { title: 'Description', body: 'The left column of the task modal has a text area for the task description. Click into it, type your notes, and the description auto-saves. It supports multi-line plain text.' },
      { title: 'Subtasks', body: 'Below the description you\'ll find the Subtasks section. Click "Add subtask" to create a child task linked to this one. Each subtask has its own status checkbox. Complete subtasks are shown with a strikethrough. Subtask count is shown on the parent task card.' },
      { title: 'File Attachments', body: 'Drag and drop files onto the attachment zone, or click to browse. Attachments are stored securely. Each shows the filename, size, uploader, and a download button. Click the trash icon to delete an attachment.' },
      { title: 'Comments', body: 'Add comments at the bottom of the left column. Use @username to mention a team member — they\'ll get a notification. Comments show the author, timestamp, and support editing if you\'re the author.' },
      { title: 'Time Logging', body: 'Log time spent on a task using the Time Logging section. Enter hours and an optional note, then click "Log Time". All time entries appear in a list below with the logger\'s name, hours, and note. You can delete your own entries.' },
      { title: 'Dependencies (Blocked By)', body: 'In the right sidebar, the "Blocked by" section lets you declare which tasks must be completed before this one. Search for tasks by name and click to add. Dependencies appear as amber chips — click × to remove one.' },
      { title: 'Watch / Follow a Task', body: 'Click the Eye icon in the modal header to watch a task. Watchers receive notifications when the task status changes or new comments are added. The watcher count is shown next to the icon. Click again to unwatch.' },
      { title: 'Right Sidebar Fields', body: 'The right sidebar shows all task metadata — editable inline:\n• Status (dropdown)\n• Priority (Low / Medium / High / Critical)\n• Assignee (team member picker)\n• Due Date (date picker)\n• Start Date\n• Tags (add/remove chips)\n• Estimate (hours)\n• Sprint assignment' },
    ]
  },
  {
    id: 'timeline', label: 'Timeline', icon: '⏱️',
    content: [
      { title: 'Task Countdown View', body: 'The Timeline page shows all project tasks in a table with their start date, due date, assignee, and a live countdown timer. It gives a quick visual of what\'s coming up and what\'s overdue.' },
      { title: 'Countdown Colors', body: '• Gray — More than 3 days remaining\n• Amber — 3 days or fewer remaining\n• Orange — Due today\n• Red (pulsing) — Overdue\n• Green checkmark — Task is Done or Approved' },
      { title: 'Overdue Alert Banner', body: 'If any tasks are past their due date, a red banner appears at the top of the page showing the count. Click any overdue row to open the task and update its due date or reassign it.' },
      { title: 'Filters', body: 'Use the Sprint and Status filters at the top to narrow the list:\n• Status filter: Active tasks, Overdue only, All tasks\n• Sprint filter: All sprints, Backlog, or a specific sprint name' },
    ]
  },
  {
    id: 'sprints', label: 'Sprints', icon: '⚡',
    content: [
      { title: 'What are Sprints?', body: 'Sprints are time-boxed work cycles (typically 1–2 weeks) where your team commits to completing a set of tasks. SyncWork supports three sprint states: Planning → Active → Completed.' },
      { title: 'Create a Sprint', body: 'Go to a project → Board (or Sprints via the sub-nav). Click "New Sprint" in the Sprint Panel. Fill in:\n• Sprint Name (e.g. "Sprint 1 — Auth")\n• Sprint Goal\n• Start Date and End Date\n• Select tasks from the backlog checklist\n\nClick "Create Sprint" to save. Selected tasks are automatically assigned to this sprint.' },
      { title: 'Start a Sprint', body: 'When a sprint is in "Planning" state, click the "Start" button. This activates the sprint and shows a progress bar. Only one sprint can be active at a time per project.' },
      { title: 'Complete a Sprint', body: 'Once all tasks are done or moved, click "Complete" on the active sprint. The sprint moves to Completed status and you can start a new one — or let SyncWork complete it for you automatically (see "AI Task Execution Plan" below).' },
      { title: 'Sprint Panel Overview', body: 'The Sprint Panel at the top of the Board lists every sprint in the project — Active, Planning, and Completed — not just the current one. Each sprint card shows its name, status badge, days remaining (if active), goal, dates, and a progress bar (tasks done / total).\n\nClick the ⌄ chevron on any sprint card to expand it and see its full task list, or collapse it again. The active sprint is expanded by default.' },
      { title: 'AI Task Execution Plan', body: 'Inside an expanded sprint, click any task to reveal its AI-generated execution plan — a vertical step-by-step timeline (e.g. Research → Design → Implementation → Testing → Review) with each step\'s own date range calculated within the sprint window.\n\nTick each step\'s circle as you complete it. When you tick the last step:\n• The task is automatically marked Done\n• If every task in the sprint is now done, the sprint auto-completes — no manual "Complete" click needed\n\nClick "Regenerate" next to the plan to ask the AI for a fresh breakdown at any time. If no AI key is configured, a clearly-labeled "(sample)" template plan is shown instead, still using real sprint dates.' },
      { title: 'AI-Generated Sprints', body: 'Use the AI Sprint Plan tool (Dashboard or AI Tools page) to automatically generate a multi-sprint plan from your backlog. After reviewing the AI\'s suggestions, click "Create N Sprints in Project" to create all sprints at once — including task assignments.' },
    ]
  },
  {
    id: 'assign-tasks', label: 'Assign Tasks', icon: '👤',
    content: [
      { title: 'Task Management Table', body: 'The Assign Tasks page is your central task management hub — create, assign, reassign, edit, and delete tasks from one table without opening individual modals.' },
      { title: 'Create a Task', body: 'Click "New Task" in the top-right. An inline form expands with:\n• Title\n• Assignee\n• Priority (Low / Medium / High / Critical)\n• Status\n• Sprint\n• Start Date & Due Date\n\nClick "Create Task" to save.' },
      { title: 'Inline Editing', body: 'Every table row has inline dropdowns for Status, Priority, Sprint, and Assignee. Click any field to change it — it saves instantly with no modal required.' },
      { title: 'Export Tasks as CSV', body: 'Click "Export CSV" in the top toolbar to download all visible tasks as a spreadsheet. The CSV includes title, status, priority, assignee, sprint, and dates.' },
      { title: 'Import Tasks from CSV', body: 'Click "Import CSV" (visible to Project Managers and above) and select a CSV file. SyncWork detects columns automatically and shows a preview before importing.\n\nSupported columns: title, description, priority, status, assignee email, due_date.\nThe title column is required. All others are optional.' },
      { title: 'Delete a Task', body: 'Click the trash icon on any row. An inline Yes/No confirmation appears — click "Yes" to permanently delete the task. This cannot be undone (but you can use Archive instead to recover tasks later).' },
    ]
  },
  {
    id: 'ai-tools', label: 'AI Tools', icon: '🤖',
    content: [
      { title: 'Where to Find AI Tools', body: 'AI tools are available in two places:\n1. Dashboard — AI tabs on the right panel (works across projects)\n2. Project → AI Tools (in project sub-navigation) — works within the current project\n\nBoth locations have the same three AI features.' },
      { title: 'AI Sprint Planning (GPT-4o)', body: 'Analyzes all your backlog tasks and team capacity to generate a complete multi-sprint plan.\n\nHow to use:\n1. Make sure your project has tasks\n2. Click "Generate Sprint Plan"\n3. Review sprints, capacity, risks, and recommendations\n4. Click "Create N Sprints in Project" to create the sprints automatically\n5. Click "Regenerate" to get a fresh plan' },
      { title: 'Goal → Task Breakdown (Claude)', body: 'Type a high-level goal and the AI generates 3–7 actionable engineering tasks with title, description, time estimate, and tags.\n\nAfter the result appears, click "Add to Project" to create all tasks in your active project instantly.' },
      { title: 'Comment AI Rewriter (Gemini)', body: 'Paste a vague comment or Slack message and click "Rewrite Comment". The AI outputs:\n• Clear action statement\n• Priority level (High/Medium/Low)\n• Suggested owner\n• Suggested deadline\n• Acceptance criteria\n\nClick the "Copy" button on the result to copy everything to clipboard.' },
      { title: 'AI Caching', body: 'AI results for the same input are cached to save time and cost. If you want a fresh answer (different random output), click "Regenerate" to bypass the cache.' },
      { title: 'Mock Mode', body: 'If your workspace Admin hasn\'t configured AI API keys (OpenAI, Anthropic, Gemini), the AI features run in mock mode — they return realistic-looking sample data so you can explore the UI without real AI credits.' },
    ]
  },
  {
    id: 'search', label: 'Search & Navigation', icon: '🔍',
    content: [
      { title: 'Command Palette', body: 'Press Cmd+K (Mac) or Ctrl+K (Windows/Linux) anywhere in the app to open the Command Palette. It lets you:\n• Search tasks, projects, and team members\n• Navigate to any page\n• Create a task or project\n• Switch theme\n• Open notifications' },
      { title: 'Search Tasks', body: 'Type at least 2 characters in the command palette to search across all your organization\'s tasks, projects, and members. Results appear grouped by type.\n\nClick a task result to jump directly to its project board with the task detail modal already open.' },
      { title: 'Keyboard Shortcuts', body: 'The command palette supports shortcuts when no query is typed:\n• G D — Go to Dashboard\n• G P — Go to Projects\n• G A — Go to Analytics\n• G S — Go to Settings\n• C T — Create Task\n• C P — Create Project\n• N — Open Notifications' },
      { title: 'Recent Actions', body: 'When no query is typed, the palette shows your recently used actions for quick access. These are stored locally in your browser.' },
    ]
  },
  {
    id: 'team', label: 'Team & Directory', icon: '👥',
    content: [
      { title: 'Workspace Members', body: 'Go to Directory in the sidebar to see all members in your organization. You can view their roles, join dates, departments, and email. Use the search bar to filter members.' },
      { title: 'Invite Members', body: 'Click "Invite Member" on the Directory page. Enter their email and select a workspace role:\n• Owner — Full access, can delete the organization\n• Admin — Can manage all members, projects, and settings\n• Member — Can work on assigned projects only' },
      { title: 'Project Roles', body: 'Each project has its own role system separate from workspace roles. Go to a project\'s Members page to assign:\n• Project Manager — Full project control, can approve tasks and manage sprints\n• Scrum Master / Team Lead — Can manage sprints and approve tasks\n• Developer / Tester — Can create and work on tasks, submit for review\n• Viewer / Observer — Read-only access, cannot create or edit anything' },
      { title: 'Teams & Groups', body: 'Use the Teams tab in Directory to create named groups (e.g. "Frontend Team"). Assign members to teams to organize your directory by department or function.' },
    ]
  },
  {
    id: 'notifications', label: 'Notifications', icon: '🔔',
    content: [
      { title: 'Notification Bell', body: 'Click the bell icon in the top header to open your notifications panel. Unread notifications show a red badge with the count. The panel lists all recent activity sorted by time.' },
      { title: 'Notification Types', body: 'You receive notifications for:\n• Task assigned to you\n• @mention in a comment\n• Task status changed on a task you\'re watching\n• Project invite\n• Sprint started or completed' },
      { title: 'Mark as Read', body: 'Click any notification to mark it as read and navigate to the relevant task or project. Use "Mark all read" at the top of the panel to clear all unread at once.' },
      { title: 'Notification Preferences', body: 'Go to Settings → Notifications to control:\n• In-app notifications — toggle on/off\n• Email notifications — toggle on/off\n• Per-event preferences — enable/disable specific event types\n• Digest mode — receive a daily or weekly email digest instead of individual emails' },
    ]
  },
  {
    id: 'analytics', label: 'Analytics', icon: '📈',
    content: [
      { title: 'Analytics Overview', body: 'The Analytics page shows charts and metrics for your projects. Select a project from the dropdown at the top to see its data. All charts update when you change the project.' },
      { title: 'Velocity Chart', body: 'A bar chart showing how many tasks your team completes per sprint over time. A rising trend indicates improving team throughput and predictability.' },
      { title: 'Burndown Chart', body: 'Tracks remaining tasks in a sprint over time. The ideal line goes from the sprint\'s total tasks down to zero. Compare it to the actual line to see if you\'re on track or falling behind.' },
      { title: 'Workload View', body: 'A stacked bar chart showing per-member task distribution — how many tasks each person has in each status. Use this to identify overloaded team members and redistribute work evenly.' },
      { title: 'Health Score', body: 'A 0–100 score (graded A to F) computed from:\n• On-time completion rate\n• Open high-priority tasks\n• Unassigned tasks\n• Total estimated hours remaining\n\nAim for 90+ (grade A) for a healthy project.' },
      { title: 'AI Savings Tracker', body: 'Tracks the estimated hours saved by using AI features (sprint planning, goal breakdown, comment rewrites) based on cached vs. live AI calls. Visible in the Enterprise Console.' },
    ]
  },
  {
    id: 'integrations', label: 'Integrations', icon: '🔌',
    content: [
      { title: 'Integrations Overview', body: 'Go to Integrations in the sidebar to connect SyncWork with your other tools. Available integrations:\n• Slack — receive task notifications in your Slack channels\n• GitHub — link your repository and auto-complete tasks on PR merge\n• Outgoing Webhooks — send signed JSON payloads to any URL when events happen' },
      { title: 'Slack Integration', body: 'Click "Add" on the Slack card. Paste your Slack Incoming Webhook URL and set a channel name. Select which events to notify (task assigned, status changed, sprint started, etc.).\n\nClick "Test" on an existing integration to send a test message and verify it\'s working. Toggle the switch to enable or disable without deleting.' },
      { title: 'GitHub Integration', body: 'Click "Add" on the GitHub card. Enter your repo owner (username or org) and repo name. Optionally add a GitHub access token for private repos.\n\nAfter saving, SyncWork generates a webhook URL and secret. Add these to your GitHub repo under Settings → Webhooks. Once connected:\n• Merging a PR mentioning a task ID auto-completes the task\n• Reviewing a PR can create a task (if enabled)' },
      { title: 'Outgoing Webhooks', body: 'Create a generic outgoing webhook to POST JSON to any URL when SyncWork events occur. Each webhook supports:\n• Custom name and URL\n• Optional HMAC-SHA256 signing secret (verified via X-ProjectFlow-Signature header)\n• Per-event filtering\n• Enable/disable toggle\n• Test delivery with HTTP status response' },
    ]
  },
  {
    id: 'settings', label: 'Settings & Profile', icon: '⚙️',
    content: [
      { title: 'Profile Photo', body: 'Go to Settings → Profile. Hover over the avatar circle and click the camera icon to upload a photo. Supported formats: JPG, PNG, GIF. Maximum size: 2 MB. Your photo appears in task cards, comments, and the header.' },
      { title: 'Update Your Name', body: 'In Settings → Profile, edit the Full Name field and click "Save Changes". Your name updates immediately across the app.' },
      { title: 'Change Password', body: 'Go to Settings → Security. Enter your current password and a new password (minimum 8 characters). Click "Change Password" to save.' },
      { title: 'Two-Factor Authentication', body: 'Go to Settings → Security and toggle on "Email OTP on every login". When enabled, you\'ll receive a 6-digit code by email every time you sign in after entering your password.' },
      { title: 'Notification Preferences', body: 'Go to Settings → Notifications to control in-app and email notifications per event type. You can also set a daily or weekly digest instead of individual notifications.' },
      { title: 'Appearance / Dark Mode', body: 'Go to Settings → Appearance to pick Light, Dark, or System theme. You can also toggle theme using the sun/moon icon in the top header. Your preference is saved automatically.' },
      { title: 'Organization Settings', body: 'As an Owner or Admin, go to Settings → Workspace to update your organization name and description. You can also manage billing and delete the organization from this page.' },
    ]
  },
  {
    id: 'audit-log', label: 'Audit Log', icon: '📜',
    content: [
      { title: 'What is the Audit Log?', body: 'The Audit Log records every significant action taken in your organization — who did what, when, and from which IP address. It\'s available to Admins and Owners only.' },
      { title: 'Accessing the Audit Log', body: 'Click "Audit Log" in the sidebar (visible to Admins and Owners only). The log shows a table with:\n• Timestamp\n• Actor (who performed the action)\n• Action type (created, updated, deleted, etc.)\n• Entity (what was affected: task, project, member)\n• IP address' },
      { title: 'Export Audit Log', body: 'Click "Export CSV" in the top-right of the Audit Log page to download the full log as a CSV file. Useful for compliance reporting or security reviews.' },
    ]
  },
  {
    id: 'roles', label: 'Roles & Permissions', icon: '🔐',
    content: [
      { title: 'Workspace Roles', body: '• Owner — Full access to everything. Can delete the organization.\n• Admin — Can manage all members, all projects, all settings, and view the Audit Log.\n• Member — Access only to projects they\'ve been added to.' },
      { title: 'Project Roles', body: '• Project Manager — Full project control: create/manage sprints, approve tasks, manage members.\n• Scrum Master / Team Lead — Can manage sprints and approve tasks.\n• Developer / Tester — Can create tasks, log time, add comments, submit tasks for review.\n• Viewer / Observer — Read-only. Cannot create, edit, or delete anything.' },
      { title: 'Task Approval', body: 'Only Project Managers, Scrum Masters, Team Leads, Admins, and Owners can move tasks to "Approved". Developers can only submit tasks to "In Review" — they cannot self-approve.' },
      { title: 'Sprint Management', body: 'Creating, starting, and completing sprints requires Project Manager, Scrum Master, or Team Lead role within the project (or Admin/Owner at workspace level).' },
      { title: 'Project Settings & Deletion', body: 'Only Project Managers, Admins, and Owners can edit project settings (name, color, description) or delete a project. Regular members and viewers cannot access the Settings sub-section.' },
      { title: 'API Keys', body: 'API key creation and revocation in the Enterprise Console requires Admin or Owner role. API keys grant programmatic access to your organization\'s data.' },
    ]
  },
  {
    id: 'help', label: 'Help & Support', icon: '🆘',
    content: [
      { title: 'Help & Support Page', body: 'Click "Help & Support" at the bottom of the sidebar to open the support page. It has:\n• Support contact details (email, response time, hours)\n• FAQ accordion with answers to common questions\n• Contact form to reach our team directly' },
      { title: 'Common Issues', body: '• Can\'t log in? — Check your email is verified. Use "Forgot password?" on the login screen.\n• Not seeing a project? — You may not be a member. Ask your Project Manager to add you.\n• Tasks not saving? — Check your internet connection. Changes save instantly when online.\n• AI tools not working? — AI needs valid API keys configured by your workspace Admin in the Enterprise Console.\n• Sprint creation fails? — Make sure the backend is running and you have Project Manager or higher role.' },
      { title: 'Contact Support', body: 'Use the contact form on the Help & Support page to reach us:\n• Email: info@syncwork.pro\n• Response time: Within 24 hours on business days (Mon–Fri, 9am–6pm IST)\n• Subject options: General Question, Bug Report, Feature Request, Billing & Subscription, Account Issue' },
    ]
  },
];

const HelpContactForm = () => {
  const { user } = useAuth();
  const [name, setName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('General Question');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(getApiUrl('/contact'), { name, email, subject, message });
      setSent(true);
      setMessage('');
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
        <div className="text-4xl mb-3">✅</div>
        <h3 className="text-lg font-semibold text-green-800 mb-1">Message Sent!</h3>
        <p className="text-sm text-green-700">We've received your message and will get back to you within 24 hours.</p>
        <button onClick={() => setSent(false)} className="mt-4 text-sm text-green-700 underline hover:text-green-900">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-indigo-100 text-[#4B1E87] flex items-center justify-center text-xs font-bold shrink-0">✉</span>
        Contact Support
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Your Name</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" required />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Email Address</label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Subject</label>
        <Select value={subject} onValueChange={setSubject}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="General Question">General Question</SelectItem>
            <SelectItem value="Bug Report">Bug Report</SelectItem>
            <SelectItem value="Feature Request">Feature Request</SelectItem>
            <SelectItem value="Billing & Subscription">Billing & Subscription</SelectItem>
            <SelectItem value="Account Issue">Account Issue</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Message</label>
        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe your issue or question in as much detail as possible..."
          className="min-h-[120px] resize-y text-sm"
          required
        />
      </div>
      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-gray-400">We typically respond within 24 hours on business days.</p>
        <Button type="submit" disabled={sending || !message.trim()} className="bg-[#4B1E87] hover:bg-[#3A1669] text-white">
          {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Sending…</> : <><Send className="w-4 h-4 mr-2" />Send Message</>}
        </Button>
      </div>
    </form>
  );
};

const HELP_FAQS = [
  {
    q: "I can't log in — what should I do?",
    a: "Make sure your email is verified. Check your inbox for a verification email from SyncWork and click the link. If you've forgotten your password, click \"Forgot password?\" on the login screen to reset it.",
  },
  {
    q: "I don't see a project I should have access to.",
    a: "You may not have been added as a member. Ask your Project Manager or Admin to go to the project's Members page and invite you with the appropriate role.",
  },
  {
    q: "My task changes aren't saving.",
    a: "Check your internet connection — changes save instantly when online. If the problem persists, try refreshing the page. If you see an error toast, note the message and contact support.",
  },
  {
    q: "The AI tools aren't generating results.",
    a: "AI features require API keys (OpenAI, Anthropic, Gemini) to be configured by your workspace Admin in the Enterprise settings. If keys aren't set, AI runs in mock/demo mode.",
  },
  {
    q: "How do I invite team members?",
    a: "Go to Directory → Users and click \"Invite Member\". Enter their email and select a workspace role. They'll receive an invite email and can join after verifying their account.",
  },
  {
    q: "Can I change my email or password?",
    a: "Yes. Go to Settings → Profile to update your email. Go to Settings → Security to change your password. You'll need to confirm your current password before setting a new one.",
  },
  {
    q: "How do I delete a project?",
    a: "Open the project, go to the Settings sub-section in the sidebar, scroll to the Danger Zone, and click \"Delete Project\". This is permanent and cannot be undone.",
  },
  {
    q: "What's the difference between workspace roles and project roles?",
    a: "Workspace roles (Owner, Admin, Member) control access to organization-wide settings. Project roles (Project Manager, Developer, Viewer etc.) control what you can do inside a specific project.",
  },
];

const HelpSupportPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <AppShell header={<DashboardHeader />}>
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-100 mb-2">
            <span className="text-3xl">🛟</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Help & Support</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Find answers to common questions or reach out to our team.</p>
        </div>

        {/* Support info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 text-center shadow-sm">
            <div className="text-2xl mb-2">📧</div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Email Support</div>
            <div className="text-xs text-[#4B1E87] dark:text-indigo-400">info@syncwork.pro</div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 text-center shadow-sm">
            <div className="text-2xl mb-2">⏱️</div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Response Time</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Within 24 hours</div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 text-center shadow-sm">
            <div className="text-2xl mb-2">🕘</div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Support Hours</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Mon–Fri, 9am–6pm IST</div>
          </div>
        </div>

        {/* FAQs */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {HELP_FAQS.map((faq, i) => (
              <div key={i} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-100 dark:border-gray-800 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact form */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Support</h2>
          <HelpContactForm />
        </div>
      </div>
    </AppShell>
  );
};

const UserGuidePage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const section = GUIDE_SECTIONS.find(s => s.id === activeSection) || GUIDE_SECTIONS[0];
  const idx = GUIDE_SECTIONS.findIndex(s => s.id === activeSection);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center gap-3 shadow-sm" style={{height:'64px'}}>
        <img src="/syncwork.svg" alt="SyncWork" className="h-10 sm:h-14 w-auto object-contain" style={{maxWidth:'160px'}} />
        <span className="text-gray-300 mx-1">|</span>
        <span className="text-gray-600 font-medium text-sm sm:text-base">User Guide</span>
      </div>

      {/* Mobile section selector — shown instead of the desktop sidebar */}
      <div className="md:hidden sticky top-[64px] z-20 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <Select value={activeSection} onValueChange={setActiveSection}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GUIDE_SECTIONS.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.icon} {s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left nav — desktop only, top offset matches header height */}
        <aside className="hidden md:block w-64 shrink-0 bg-white border-r border-gray-200 overflow-y-auto sticky top-[64px] h-[calc(100vh-64px)]">
          <div className="p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 px-2">Contents</p>
            <nav className="space-y-0.5">
              {GUIDE_SECTIONS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                    activeSection === s.id
                      ? s.id === 'help' ? 'bg-rose-50 text-rose-700 font-semibold' : 'bg-[#f3eeff] text-[#4B1E87] font-semibold'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-base leading-none">{s.icon}</span>
                  <span className="truncate">{s.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-y-auto min-w-0">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
            <div className="flex items-center gap-3 mb-8">
              <span className="text-4xl">{section.icon}</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{section.label}</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {activeSection === 'help' ? 'Get help from our team' : `${section.content.length} topic${section.content.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {section.content.map((item, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-[#4B1E87] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                    {item.title}
                  </h2>
                  <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                    {item.body}
                  </div>
                </div>
              ))}

              {/* Contact form — only shown in Help section */}
              {activeSection === 'help' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 my-2">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">Contact Us</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                  <HelpContactForm />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 text-center shadow-sm">
                      <div className="text-2xl mb-2">📧</div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">Email Support</div>
                      <div className="text-xs text-[#4B1E87]">info@syncwork.pro</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 text-center shadow-sm">
                      <div className="text-2xl mb-2">⏱️</div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">Response Time</div>
                      <div className="text-xs text-gray-500">Within 24 hours</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 text-center shadow-sm">
                      <div className="text-2xl mb-2">🕘</div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">Support Hours</div>
                      <div className="text-xs text-gray-500">Mon–Fri, 9am–6pm IST</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Prev / Next */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200">
              {idx > 0 ? (
                <button
                  onClick={() => setActiveSection(GUIDE_SECTIONS[idx - 1].id)}
                  className="flex items-center gap-2 text-sm text-[#4B1E87] hover:text-[#4B1E87] font-medium"
                >
                  ← {GUIDE_SECTIONS[idx - 1].label}
                </button>
              ) : <div />}
              {idx < GUIDE_SECTIONS.length - 1 ? (
                <button
                  onClick={() => setActiveSection(GUIDE_SECTIONS[idx + 1].id)}
                  className="flex items-center gap-2 text-sm text-[#4B1E87] hover:text-[#4B1E87] font-medium"
                >
                  {GUIDE_SECTIONS[idx + 1].label} →
                </button>
              ) : <div />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ==================== ANALYTICS LAYOUT ====================
const IntegrationsLayout = () => {
  const { currentOrg } = useAuth();
  return (
    <AppShell header={<DashboardHeader />}>
      <IntegrationsPage orgId={currentOrg?.id} />
    </AppShell>
  );
};

const AnalyticsLayout = () => {
  const { currentOrg } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sprints, setSprints] = useState([]);
  const projectsControllerRef = useRef(null);

  useEffect(() => {
    if (currentOrg) {
      loadProjects();
    }
  }, [currentOrg]);

  useEffect(() => {
    if (selectedProject && currentOrg) {
      axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${selectedProject.id}/sprints`))
        .then(r => setSprints(r.data || []))
        .catch(() => setSprints([]));
    }
  }, [selectedProject, currentOrg]);

  const loadProjects = async () => {
    if (!currentOrg) return;
    
    try {
      if (projectsControllerRef.current) {
        projectsControllerRef.current.abort();
      }
      projectsControllerRef.current = new AbortController();
      const response = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects`), {
        signal: projectsControllerRef.current.signal
      });
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      toast.error('Failed to load projects');
    }
  };

  useEffect(() => () => {
    projectsControllerRef.current?.abort();
  }, []);

  if (!currentOrg) {
    return (
      <AppShell header={<DashboardHeader />}>
        <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">
          <EmptyState
            icon={Building2}
            title="No Organization Selected"
            description="Please select an organization to view analytics."
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell header={<DashboardHeader />}>
      <Suspense fallback={<RouteFallback />}>
        <AnalyticsPage
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          sprints={sprints}
        />
      </Suspense>
    </AppShell>
  );
};

// ==================== SETTINGS LAYOUT ====================
// ==================== CREATE WORKSPACE PAGE ====================
const CreateWorkspacePage = () => {
  const { user, organizations, setOrganizations, setCurrentOrg } = useAuth();
  const [name, setName]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect: not logged in → auth, already has org → dashboard
  useEffect(() => {
    if (!user) { navigate('/auth', { replace: true }); return; }
    if (organizations && organizations.length > 0) { navigate('/dashboard', { replace: true }); }
  }, [user, organizations, navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl('/auth/create-workspace'), { name: name.trim() });
      const orgs = res.data.organizations || [];
      setOrganizations(orgs);
      if (orgs.length > 0) setCurrentOrg(orgs[0]);
      const isNew = !!res.data.organization;
      toast.success(isNew ? `Workspace "${orgs[0]?.name}" created! Welcome 🎉` : 'Workspace ready! Taking you in…');
      navigate('/dashboard');
    } catch (err) {
      // The backend may have created the workspace even though the response was
      // lost in transit (gateway blip). Verify against /me before declaring failure —
      // if the org exists, proceed into the app instead of showing a false error.
      try {
        const me = await axios.get(getApiUrl('/me'));
        const orgs = me.data?.organizations || [];
        if (orgs.length > 0) {
          setOrganizations(orgs);
          setCurrentOrg(orgs[0]);
          toast.success('Workspace ready! Taking you in…');
          navigate('/dashboard');
          return;
        }
      } catch (_) { /* fall through to real error handling below */ }
      if (err.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/auth');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create workspace');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AuthLogo />
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-gradient-to-r from-[#4B1E87] to-[#693CB4] rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <CardTitle>Create Your Workspace</CardTitle>
            <CardDescription>Give your company workspace a name. You'll be the Owner.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Workspace Name</label>
                <Input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Acme Corp, My Startup…"
                  autoFocus
                  required
                />
                <p className="text-xs text-gray-400 mt-1">This is your company's workspace. You can only have one.</p>
              </div>
              <Button type="submit" disabled={loading || !name.trim()}
                className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white">
                {loading ? 'Creating…' : 'Create Workspace & Continue →'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== BILLING PAGE ====================
const FREE_PLAN_MEMBER_LIMIT = 10;

const BillingPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('subscriptions');

  const orgRole = currentOrg?.role?.toUpperCase();
  const canAccessBilling = ['OWNER', 'ADMIN'].includes(orgRole);

  // ── Billing Profile state ──
  const [profile, setProfile] = useState({ full_name:'', company_name:'', email:'', phone:'', tax_id:'' });
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Address state ──
  const [address, setAddress] = useState({ line1:'', line2:'', city:'', state:'', pin:'', country:'' });
  const [savingAddress, setSavingAddress] = useState(false);

  // ── Invoices state ──
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // ── Subscription state ──
  const [subscription, setSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);

  // ── Payment methods state ──
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPM, setLoadingPM] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCard, setNewCard] = useState({ brand:'visa', last4:'', exp_month:'', exp_year:'', label:'' });
  const [savingPM, setSavingPM] = useState(false);

  const NAV = [
    { id: 'subscriptions',   label: 'Subscriptions',   icon: CheckCircle2 },
    { id: 'billing-profile', label: 'Billing Profile',  icon: User },
    { id: 'payment-method',  label: 'Payment Method',   icon: Shield },
    { id: 'addresses',       label: 'Addresses',        icon: MapPin },
    { id: 'invoices',        label: 'Invoices',         icon: BarChart3 },
  ];

  // Load profile, address, subscription, payment methods on mount
  useEffect(() => {
    if (!currentOrg) return;
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/billing/profile`))
      .then(r => { if (r.data && r.data.org_id) setProfile({ full_name: r.data.full_name||'', company_name: r.data.company_name||'', email: r.data.email||'', phone: r.data.phone||'', tax_id: r.data.tax_id||'' }); })
      .catch(() => {});
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/billing/address`))
      .then(r => { if (r.data && r.data.org_id) setAddress({ line1: r.data.line1||'', line2: r.data.line2||'', city: r.data.city||'', state: r.data.state||'', pin: r.data.pin||'', country: r.data.country||'' }); })
      .catch(() => {});
    setLoadingSub(true);
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/billing/subscription`))
      .then(r => setSubscription(r.data))
      .catch(() => {})
      .finally(() => setLoadingSub(false));
    setLoadingPM(true);
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/billing/payment-methods`))
      .then(r => setPaymentMethods(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingPM(false));
  }, [currentOrg]);

  // Load invoices when tab is opened
  useEffect(() => {
    if (activeSection !== 'invoices' || !currentOrg) return;
    setLoadingInvoices(true);
    axios.get(getApiUrl(`/organizations/${currentOrg.id}/billing/invoices`))
      .then(r => setInvoices(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingInvoices(false));
  }, [activeSection, currentOrg]);

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    setSavingPM(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/billing/payment-methods`), newCard);
      setPaymentMethods(prev => [...prev, res.data]);
      setNewCard({ brand:'visa', last4:'', exp_month:'', exp_year:'', label:'' });
      setShowAddCard(false);
      toast.success('Payment method added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add payment method'); }
    finally { setSavingPM(false); }
  };

  const handleRemovePaymentMethod = async (pmId) => {
    try {
      await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/billing/payment-methods/${pmId}`));
      setPaymentMethods(prev => prev.filter(p => p.id !== pmId));
      toast.success('Payment method removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleSetDefault = async (pmId) => {
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/billing/payment-methods/${pmId}/default`));
      setPaymentMethods(prev => prev.map(p => ({ ...p, is_default: p.id === pmId })));
    } catch { toast.error('Failed to update default'); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axios.put(getApiUrl(`/organizations/${currentOrg.id}/billing/profile`), profile);
      setProfile({ full_name: res.data.full_name||'', company_name: res.data.company_name||'', email: res.data.email||'', phone: res.data.phone||'', tax_id: res.data.tax_id||'' });
      toast.success('Billing profile saved');
    } catch { toast.error('Failed to save profile'); }
    finally { setSavingProfile(false); }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      const res = await axios.put(getApiUrl(`/organizations/${currentOrg.id}/billing/address`), address);
      setAddress({ line1: res.data.line1||'', line2: res.data.line2||'', city: res.data.city||'', state: res.data.state||'', pin: res.data.pin||'', country: res.data.country||'' });
      toast.success('Address saved');
    } catch { toast.error('Failed to save address'); }
    finally { setSavingAddress(false); }
  };

  if (!canAccessBilling) {
    return (
      <AppShell header={<DashboardHeader />}>
        <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
            <p className="text-gray-500 mt-1">Manage your subscription, payment methods, and invoices.</p>
          </div>
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
            <p className="text-gray-500 max-w-sm">
              You don't have permission to view billing details. Only workspace Owners and Admins can access this page.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
          <p className="text-gray-500 mt-1">Manage your subscription, payment methods, and invoices.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Side Nav ── */}
          <aside className="lg:w-56 shrink-0">
            <nav className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors border-b border-gray-100 last:border-0
                      ${active
                        ? 'bg-[#f3eeff] text-[#4B1E87] border-l-4 border-l-[#4B1E87]'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#4B1E87]' : 'text-gray-400'}`} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* ── Main Content ── */}
          <div className="flex-1 min-w-0">

            {/* SUBSCRIPTIONS */}
            {activeSection === 'subscriptions' && (
              <div className="space-y-6">
                {/* Current Subscription — only shown once a plan has actually
                    been purchased. A brand-new workspace on the Free plan has
                    no real subscription record, so there's nothing to show
                    here yet; Plans & Pricing below covers that case. */}
                {loadingSub ? (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Current Subscription</h2>
                    <div className="h-24 bg-gray-50 rounded-xl animate-pulse" />
                  </div>
                ) : subscription?.is_purchased ? (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Current Subscription</h2>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-[#f3eeff] border border-[#d4b8f0] rounded-xl">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl font-bold text-gray-900">{subscription.plan}</span>
                          <Badge className={`text-xs ${subscription.status === 'active' ? 'bg-[#4B1E87] text-white' : 'bg-gray-200 text-gray-700'}`}>
                            {subscription.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600">{subscription.range} · {subscription.member_count} members</div>
                        {subscription.next_billing_date && (
                          <div className="text-sm text-gray-500 mt-1">Next billing: <strong>{subscription.next_billing_date}</strong></div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-[#4B1E87]">
                          {subscription.price ? `$${subscription.price}` : 'Custom'}
                        </div>
                        <div className="text-xs text-gray-400">per month</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Plans & Pricing */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Plans & Pricing</h2>
                  <p className="text-sm text-gray-500 mb-5">Free for small teams. Contact us as you grow.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Free plan */}
                    <div className="border border-gray-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-bold text-gray-900">Free</span>
                        {!subscription?.is_purchased && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Current</Badge>
                        )}
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mb-1">$0<span className="text-sm font-normal text-gray-400">/month</span></div>
                      <p className="text-sm text-gray-500 mb-4">Up to {FREE_PLAN_MEMBER_LIMIT} members in your workspace.</p>
                      <ul className="text-sm text-gray-600 space-y-1.5">
                        <li>✓ Unlimited projects & tasks</li>
                        <li>✓ Kanban board, sprints, timeline</li>
                        <li>✓ AI Copilot (mock or your own API keys)</li>
                        <li>✓ Up to {FREE_PLAN_MEMBER_LIMIT} team members</li>
                      </ul>
                    </div>

                    {/* Enterprise / above the limit */}
                    <div className="border border-[#d4b8f0] bg-[#f3eeff] rounded-2xl p-5">
                      <div className="text-base font-bold text-gray-900 mb-1">Enterprise</div>
                      <div className="text-3xl font-bold text-[#4B1E87] mb-1">Custom</div>
                      <p className="text-sm text-gray-600 mb-4">
                        More than {FREE_PLAN_MEMBER_LIMIT} members? Reach out and we'll put together a plan for your team.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1.5 mb-5">
                        <li>✓ Unlimited team members</li>
                        <li>✓ Everything in Free</li>
                        <li>✓ Priority support</li>
                      </ul>
                      <Button
                        onClick={() => navigate('/help')}
                        className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white"
                      >
                        Contact Sales
                      </Button>
                    </div>
                  </div>
                  {subscription?.member_count > FREE_PLAN_MEMBER_LIMIT && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      Your workspace has {subscription.member_count} members, over the Free plan's {FREE_PLAN_MEMBER_LIMIT}-member limit. Contact Sales for pricing.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BILLING PROFILE */}
            {activeSection === 'billing-profile' && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Billing Profile</h2>
                <form onSubmit={handleSaveProfile} className="space-y-4 max-w-lg">
                  {[
                    { label: 'Full Name',      key: 'full_name',     placeholder: 'Your name',           type: 'text'  },
                    { label: 'Company Name',   key: 'company_name',  placeholder: 'Your company',        type: 'text'  },
                    { label: 'Email Address',  key: 'email',         placeholder: 'billing@example.com', type: 'email' },
                    { label: 'Phone Number',   key: 'phone',         placeholder: '+91 9999999999',      type: 'tel'   },
                    { label: 'Tax ID / GSTIN', key: 'tax_id',        placeholder: 'Optional',            type: 'text'  },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                      <Input type={f.type} placeholder={f.placeholder}
                        value={profile[f.key]}
                        onChange={e => setProfile(p => ({ ...p, [f.key]: e.target.value }))} />
                    </div>
                  ))}
                  <Button type="submit" disabled={savingProfile}
                    className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white w-full mt-2">
                    {savingProfile ? 'Saving…' : 'Save Billing Profile'}
                  </Button>
                </form>
              </div>
            )}

            {/* PAYMENT METHOD */}
            {activeSection === 'payment-method' && (
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-2">Payment Method</h2>
                  <p className="text-sm text-gray-500 mb-5">Choose how you want to pay. All methods are processed securely.</p>

                  {/* Saved payment methods */}
                  {loadingPM ? (
                    <div className="h-16 bg-gray-50 rounded-xl animate-pulse mb-4" />
                  ) : paymentMethods.length > 0 ? (
                    <div className="space-y-3 mb-5">
                      {paymentMethods.map(pm => (
                        <div key={pm.id} className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                          <div className="w-12 h-8 bg-[#4B1E87] rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0 uppercase">
                            {pm.brand?.slice(0,4) || '💳'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">
                              {pm.last4 ? `•••• •••• •••• ${pm.last4}` : pm.label || pm.brand}
                            </div>
                            {pm.exp_month && <div className="text-xs text-gray-500">Expires {pm.exp_month}/{pm.exp_year}</div>}
                          </div>
                          {pm.is_default
                            ? <Badge className="bg-emerald-100 text-emerald-700 border-0 shrink-0">Default</Badge>
                            : <button type="button" onClick={() => handleSetDefault(pm.id)} className="text-xs text-[#4B1E87] hover:underline shrink-0">Set default</button>
                          }
                          <button type="button" onClick={() => handleRemovePaymentMethod(pm.id)}
                            className="text-gray-400 hover:text-red-500 shrink-0 ml-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-5">No payment methods saved yet.</p>
                  )}

                  {/* Add new card form */}
                  {showAddCard ? (
                    <form onSubmit={handleAddPaymentMethod} className="space-y-3 p-4 border border-[#d4b8f0] rounded-xl bg-[#f3eeff] mb-4">
                      <p className="text-sm font-semibold text-gray-800">Add Card</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Brand</label>
                          <Select value={newCard.brand} onValueChange={v => setNewCard(c=>({...c,brand:v}))}>
                            <SelectTrigger className="h-9 text-sm bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['visa','mastercard','amex','paypal','applepay','googlepay','bank'].map(b=>(
                                <SelectItem key={b} value={b}>{b.charAt(0).toUpperCase()+b.slice(1)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Last 4 digits</label>
                          <Input maxLength={4} placeholder="4242" value={newCard.last4}
                            onChange={e=>setNewCard(c=>({...c,last4:e.target.value.replace(/\D/g,'').slice(0,4)}))} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Exp Month</label>
                          <Input maxLength={2} placeholder="12" value={newCard.exp_month}
                            onChange={e=>setNewCard(c=>({...c,exp_month:e.target.value.replace(/\D/g,'').slice(0,2)}))} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Exp Year</label>
                          <Input maxLength={4} placeholder="2027" value={newCard.exp_year}
                            onChange={e=>setNewCard(c=>({...c,exp_year:e.target.value.replace(/\D/g,'').slice(0,4)}))} />
                        </div>
                      </div>
                      <Input placeholder="Label (optional, e.g. Business Visa)" value={newCard.label}
                        onChange={e=>setNewCard(c=>({...c,label:e.target.value}))} />
                      <div className="flex gap-2">
                        <Button type="submit" disabled={savingPM} size="sm" className="bg-[#4B1E87] text-white">
                          {savingPM ? 'Saving…' : 'Save Card'}
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={()=>setShowAddCard(false)}>Cancel</Button>
                      </div>
                    </form>
                  ) : (
                    <Button variant="outline" className="w-full border-dashed" onClick={()=>setShowAddCard(true)}>
                      + Add Payment Method
                    </Button>
                  )}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                  🔒 Card details are stored securely. Stripe integration available for full PCI-compliant processing.
                </div>
              </div>
            )}

            {/* ADDRESSES */}
            {activeSection === 'addresses' && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Billing Address</h2>
                <form onSubmit={handleSaveAddress} className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                    <Input placeholder="123 Main Street" value={address.line1} onChange={e => setAddress(a => ({...a, line1: e.target.value}))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                    <Input placeholder="Suite 100 (optional)" value={address.line2} onChange={e => setAddress(a => ({...a, line2: e.target.value}))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <Input placeholder="City" value={address.city} onChange={e => setAddress(a => ({...a, city: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <Input placeholder="State / Province" value={address.state} onChange={e => setAddress(a => ({...a, state: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                      <Input placeholder="ZIP / Postal code" value={address.pin} onChange={e => setAddress(a => ({...a, pin: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <Input placeholder="Country" value={address.country} onChange={e => setAddress(a => ({...a, country: e.target.value}))} />
                    </div>
                  </div>
                  <Button type="submit" disabled={savingAddress}
                    className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white w-full">
                    {savingAddress ? 'Saving…' : 'Save Address'}
                  </Button>
                </form>
              </div>
            )}

            {/* INVOICES */}
            {activeSection === 'invoices' && (
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-gray-900">Invoices</h2>
                  <p className="text-sm text-gray-500 mt-1">Your billing history</p>
                </div>
                {loadingInvoices ? (
                  <div className="text-center py-12 text-gray-400">
                    <BarChart3 className="w-8 h-8 mx-auto mb-3 animate-pulse opacity-40" />
                    <p>Loading invoices…</p>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>No invoices yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {invoices.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-[#4B1E87]" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{inv.invoice_number || inv.id}</div>
                            <div className="text-xs text-gray-500">
                              {inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : inv.date}
                              {inv.plan ? ` · ${inv.plan} Plan` : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-semibold text-gray-900 text-sm">{inv.amount}</div>
                            <Badge className={`text-xs border-0 ${inv.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                              {inv.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </AppShell>
  );
};

const SettingsLayout = () => {
  return (
    <AppShell header={<DashboardHeader />}>
      <Suspense fallback={<RouteFallback />}>
        <UserSettings />
      </Suspense>
    </AppShell>
  );
};

// ==================== ENTERPRISE LAYOUT ====================
const EnterpriseLayout = () => {
  const { currentOrg, organizations } = useAuth();
  const orgRole = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  const isAdminOrOwner = ['OWNER', 'ADMIN'].includes(orgRole);

  if (!isAdminOrOwner) {
    return (
      <AppShell header={<DashboardHeader />}>
        <div className="w-full max-w-[1400px] px-6 lg:px-8 py-16 flex flex-col items-center justify-center gap-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Restricted</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              The Enterprise Console is only available to Workspace Owners and Admins.
              Contact your Owner or Admin if you need access to organisation settings.
            </p>
          </div>
          <div className="px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 text-xs text-amber-800 dark:text-amber-300">
            Your role: <span className="font-semibold">{orgRole?.replace(/_/g, ' ') || 'Member'}</span>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell header={<DashboardHeader />}>
      <Suspense fallback={<RouteFallback />}>
        <EnterpriseConsole currentOrg={currentOrg} />
      </Suspense>
    </AppShell>
  );
};

// ==================== PROJECTS PAGE ====================
const ProjectsPage = () => {
  const { organizations, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [creating, setCreating]       = useState(false);
  const [showCreate, setShowCreate]   = useState(false);
  const [newName, setNewName]         = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [search, setSearch]           = useState('');

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });
  const openConfirm = (title, message, onConfirm) => setConfirmModal({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmModal(s => ({ ...s, open: false }));

  const userOrgRole = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  const isOwnerOrAdmin = ['OWNER', 'ADMIN'].includes(userOrgRole);

  const COLORS = ['from-indigo-500 to-purple-500','from-teal-500 to-emerald-500','from-orange-500 to-pink-500','from-blue-500 to-cyan-500','from-rose-500 to-fuchsia-500'];

  useEffect(() => { if (currentOrg) loadProjects(); }, [currentOrg]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects`));
      setProjects(res.data || []);
    } catch (_) { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/projects`), {
        name: newName.trim(), description: newDesc.trim() || null,
      });
      setProjects(prev => [res.data, ...prev]);
      setNewName(''); setNewDesc(''); setShowCreate(false);
      toast.success('Project created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally { setCreating(false); }
  };

  const handleDelete = (projectId, projectName) => openConfirm(
    'Delete project',
    `Delete "${projectName}"? This will permanently remove all tasks and members.`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}`));
        setProjects(prev => prev.filter(p => p.id !== projectId));
        toast.success('Project deleted');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete project'); }
      closeConfirm();
    }
  );

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );


  return (
    <AppShell header={<DashboardHeader />}>
      <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Projects</h1>
              <p className="text-sm text-muted-foreground">{projects.length} project{projects.length !== 1 ? 's' : ''} in this workspace</p>
            </div>
          </div>
          {isOwnerOrAdmin && (
            <Button onClick={() => setShowCreate(v => !v)}
              className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white shrink-0">
              <Plus className="w-4 h-4 mr-2" /> Create Project
            </Button>
          )}
        </div>

        {/* Create form */}
        {showCreate && isOwnerOrAdmin && (
          <form onSubmit={handleCreate} className="bg-white dark:bg-gray-800 border border-[#d4b8f0] dark:border-[#4B1E87] rounded-2xl p-6 mb-6 shadow-sm space-y-4">
            <h2 className="font-semibold text-foreground">New Project</h2>
            <div>
              <label className="block text-sm font-medium mb-1">Project Name <span className="text-red-500">*</span></label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Mobile App Redesign" autoFocus required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="What is this project about?" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={creating} className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white">
                {creating ? 'Creating…' : 'Create Project'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setNewName(''); setNewDesc(''); }}>Cancel</Button>
            </div>
          </form>
        )}

        {/* Search */}
        {projects.length > 3 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input className="pl-9" placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        )}

        {/* Projects grid */}
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 animate-pulse opacity-40" />
            <p>Loading projects…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">{search ? 'No projects match your search' : 'No projects yet'}</p>
            {!search && isOwnerOrAdmin && <p className="text-sm mt-1">Click "Create Project" above to get started</p>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((proj, i) => (
              <div key={proj.id}
                className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer"
                onClick={() => navigate(`/projects/${proj.id}/board`, { state: { project: proj } })}>
                <div className={`h-2 ${proj.color ? '' : `bg-gradient-to-r ${COLORS[i % COLORS.length]}`}`} style={proj.color ? { background: proj.color } : {}} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${proj.color ? '' : `bg-gradient-to-r ${COLORS[i % COLORS.length]}`}`}
                      style={proj.color ? { background: proj.color } : {}}>
                      <FolderOpen className="w-5 h-5 text-white" />
                    </div>
                    {isOwnerOrAdmin && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(proj.id, proj.name); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                        title="Delete project">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <h3 className="font-semibold text-foreground text-sm mb-1 truncate">{proj.name}</h3>
                  {proj.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{proj.description}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50 dark:border-gray-700">
                    <span className="text-xs text-muted-foreground">
                      {new Date(proj.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <Badge className={`border-0 text-xs ${
                      proj.status === 'active'    ? 'bg-emerald-50 text-emerald-700' :
                      proj.status === 'on_hold'   ? 'bg-amber-50 text-amber-700' :
                      proj.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                      'bg-gray-100 text-gray-500'}`}>
                      {proj.status ? proj.status.replace('_',' ') : 'active'}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirm modal */}
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Confirm delete"
          onConfirm={confirmModal.onConfirm}
          onCancel={closeConfirm}
        />

      </div>
    </AppShell>
  );
};

// ==================== PROJECT DETAIL PAGE ====================
const ProjectDetailPage = () => {
  const { organizations, currentOrg } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [project,     setProject]     = useState(location.state?.project || null);
  const [members,     setMembers]     = useState([]);
  const [wsMembers,   setWsMembers]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [addOpen,     setAddOpen]     = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropOpen, setMemberDropOpen] = useState(false);
  const [addRole,     setAddRole]     = useState('DEVELOPER');
  const [adding,      setAdding]      = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const userOrgRole  = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  const isOwnerOrAdmin = ['OWNER', 'ADMIN'].includes(userOrgRole);

  const PROJ_ROLE_LABEL = {
    PROJECT_MANAGER: 'Project Manager', SCRUM_MASTER: 'Scrum Master',
    TEAM_LEAD: 'Team Lead', DEVELOPER: 'Developer', TESTER: 'Tester', OBSERVER: 'Observer', VIEWER: 'Viewer',
  };
  const PROJ_ROLE_COLOR = {
    PROJECT_MANAGER: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    SCRUM_MASTER:    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    TEAM_LEAD:       'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    DEVELOPER:       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    TESTER:          'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    OBSERVER:        'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400',
    VIEWER:          'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400',
  };

  const openConfirm  = (title, message, onConfirm) => setConfirmModal({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmModal(s => ({ ...s, open: false }));

  useEffect(() => {
    if (!currentOrg || !projectId) return;
    loadAll();
  }, [currentOrg, projectId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [projRes, membRes, wsRes] = await Promise.all([
        project ? Promise.resolve({ data: project }) : axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}`)),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members`)),
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/members`)),
      ]);
      setProject(projRes.data);
      setMembers(membRes.data || []);
      setWsMembers(wsRes.data || []);
    } catch (_) { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  };

  const addMember = async (email) => {
    if (!email?.trim()) return;
    setAdding(true);
    try {
      await axios.post(
        getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members`),
        { user_email: email, role_in_project: addRole }
      );
      const updated = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members`));
      setMembers(updated.data || []);
      setMemberSearch(''); setMemberDropOpen(false); setAddOpen(false);
      toast.success('Member added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
    finally { setAdding(false); }
  };

  const updateMemberRole = async (userId, newRole) => {
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members/${userId}`), {
        role_in_project: newRole,
      });
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role_in_project: newRole } : m));
      toast.success('Role updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const removeMember = (userId, name) => openConfirm(
    'Remove member',
    `Remove ${name} from this project?`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members/${userId}`));
        setMembers(prev => prev.filter(m => m.user_id !== userId));
        toast.success('Member removed');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      closeConfirm();
    }
  );

  const alreadyIn  = new Set(members.map(m => m.user_id));
  const memberQ    = memberSearch.toLowerCase();
  const memberMatches = memberQ.length >= 1
    ? wsMembers.filter(m => !alreadyIn.has(m.user_id) && (
        (m.full_name || '').toLowerCase().includes(memberQ) ||
        (m.email || '').toLowerCase().includes(memberQ)
      ))
    : [];

  const totalByRole = Object.fromEntries(
    Object.keys(PROJ_ROLE_LABEL).map(r => [r, members.filter(m => m.role_in_project === r).length])
  );

  if (loading) return (
    <AppShell header={<DashboardHeader />}>
      <div className="flex items-center justify-center py-32 text-muted-foreground">
        <FolderOpen className="w-8 h-8 animate-pulse opacity-40 mr-3" />
        Loading project…
      </div>
    </AppShell>
  );

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="w-full max-w-[1400px] px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <button onClick={() => navigate('/projects')} className="hover:text-foreground transition-colors">Projects</button>
          <span>/</span>
          <button onClick={() => navigate(`/projects/${projectId}/board`)} className="hover:text-foreground transition-colors">{project?.name || 'Project'}</button>
          <span>/</span>
          <span className="text-foreground font-medium">Members</span>
        </div>

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{project?.name}</h1>
              {project?.description && <p className="text-sm text-muted-foreground mt-0.5">{project.description}</p>}
            </div>
          </div>
          {isOwnerOrAdmin && (
            <Button onClick={() => setAddOpen(true)}
              className="bg-[#4B1E87] hover:bg-[#3A1669] text-white self-start sm:self-auto">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add user
            </Button>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total members',    value: members.length },
            { label: 'Project Managers', value: totalByRole.PROJECT_MANAGER },
            { label: 'Developers',       value: totalByRole.DEVELOPER },
            { label: 'Team Leads',       value: totalByRole.TEAM_LEAD + totalByRole.SCRUM_MASTER },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 shadow-sm">
              <div className="text-2xl font-bold text-foreground">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Members table */}
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_220px_190px_48px] gap-4 px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <span>User</span>
            <span className="hidden sm:block">Email</span>
            <span>Role</span>
            <span></span>
          </div>

          {members.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">No members yet</p>
              {isOwnerOrAdmin && <p className="text-xs mt-1 opacity-70">Click "Add user" to assign workspace members</p>}
            </div>
          ) : members.map((m, i) => {
            const ws   = wsMembers.find(w => w.user_id === m.user_id);
            const name = m.full_name || ws?.full_name || ws?.email || 'Unknown';
            const email = ws?.email || '';
            return (
              <div key={i}
                className="grid grid-cols-[1fr_220px_190px_48px] gap-4 items-center px-5 py-3.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors">
                {/* User cell */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate text-foreground">{name}</div>
                    <div className="text-xs text-muted-foreground sm:hidden">{email}</div>
                  </div>
                </div>
                {/* Email — full, no truncate */}
                <div className="hidden sm:block text-sm text-muted-foreground">{email}</div>
                {/* Role dropdown */}
                <div>
                  {isOwnerOrAdmin ? (
                    <Select value={m.role_in_project} onValueChange={(role) => updateMemberRole(m.user_id, role)}>
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
                        <SelectItem value="SCRUM_MASTER">Scrum Master</SelectItem>
                        <SelectItem value="TEAM_LEAD">Team Lead</SelectItem>
                        <SelectItem value="DEVELOPER">Developer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${PROJ_ROLE_COLOR[m.role_in_project] || 'bg-gray-100 text-gray-700'}`}>
                      {PROJ_ROLE_LABEL[m.role_in_project] || m.role_in_project}
                    </span>
                  )}
                </div>
                {/* Actions */}
                <div className="flex justify-end">
                  {isOwnerOrAdmin && (
                    <button
                      onClick={() => removeMember(m.user_id, name)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      title="Remove from project">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add user modal */}
        {addOpen && isOwnerOrAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) { setAddOpen(false); setMemberSearch(''); } }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Add user to project</h2>
                <button onClick={() => { setAddOpen(false); setMemberSearch(''); }}
                  className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-muted-foreground">Only workspace members can be added to a project.</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Search member</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input className="pl-9" placeholder="Search by name or email…"
                      value={memberSearch}
                      onChange={e => { setMemberSearch(e.target.value); setMemberDropOpen(true); }}
                      onFocus={() => setMemberDropOpen(true)}
                      onBlur={() => setTimeout(() => setMemberDropOpen(false), 150)}
                      autoComplete="off" autoFocus
                    />
                    {memberDropOpen && memberQ.length >= 1 && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                        {memberMatches.length > 0 ? memberMatches.map((m, i) => (
                          <button key={i} type="button"
                            onMouseDown={() => { setMemberSearch(m.email); setMemberDropOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f3eeff] dark:hover:bg-indigo-900/20 transition-colors text-left">
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {(m.full_name || m.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate text-foreground">{m.full_name || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">{m.role}</span>
                          </button>
                        )) : (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">No workspace members match</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Project role</label>
                  <Select value={addRole} onValueChange={setAddRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
                      <SelectItem value="SCRUM_MASTER">Scrum Master</SelectItem>
                      <SelectItem value="TEAM_LEAD">Team Lead</SelectItem>
                      <SelectItem value="DEVELOPER">Developer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => { setAddOpen(false); setMemberSearch(''); }}>Cancel</Button>
                <Button className="flex-1 bg-[#4B1E87] hover:bg-[#3A1669] text-white"
                  disabled={!memberSearch.trim() || adding}
                  onClick={() => addMember(memberSearch)}>
                  {adding ? 'Adding…' : 'Add user'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm modal */}
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Confirm delete"
          onConfirm={confirmModal.onConfirm}
          onCancel={closeConfirm}
        />

      </div>
    </AppShell>
  );
};

// ==================== CONFIRM MODAL ====================
// (defined here so ProjectDetailPage and DirectoryPage can both use it)
const ConfirmModal = ({ open, title, message, confirmLabel = 'Delete', onConfirm, onCancel, danger = true }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${danger ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
            <AlertTriangle className={`w-5 h-5 ${danger ? 'text-red-600 dark:text-red-400' : 'text-amber-600'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 pt-1">
          <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button
            className={`flex-1 text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== DIRECTORY PAGE ====================
const TeamPage = () => {
  const { user, organizations, currentOrg, onlineUserIds } = useAuth();
  const { section = 'users' } = useParams();
  const [wsMembers,   setWsMembers]   = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [projMembers, setProjMembers] = useState({});
  const [userSearch,  setUserSearch]  = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole,  setInviteRole]  = useState('member');
  const [inviteOpen,  setInviteOpen]  = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [allTasks,    setAllTasks]    = useState([]);
  const [activityProjectId, setActivityProjectId] = useState('');

  // Add to Project state
  const [projSearch,     setProjSearch]     = useState('');
  const [projSearchOpen, setProjSearchOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [addProjectId,   setAddProjectId]   = useState('');
  const [addProjectRole, setAddProjectRole] = useState('DEVELOPER');
  const [adding,         setAdding]         = useState(false);
  const [addProjOpen,    setAddProjOpen]    = useState(false);

  // Teams state
  const [teams,          setTeams]          = useState([]);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [newTeamName,    setNewTeamName]    = useState('');
  const [newTeamDesc,    setNewTeamDesc]    = useState('');
  const [expandedTeam,   setExpandedTeam]   = useState(null);
  const [teamAddSearch,  setTeamAddSearch]  = useState({});
  const [teamAddOpen,    setTeamAddOpen]    = useState({});

  // Groups state
  const [groups,          setGroups]          = useState([]);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName,    setNewGroupName]    = useState('');
  const [newGroupDesc,    setNewGroupDesc]    = useState('');
  const [expandedGroup,   setExpandedGroup]   = useState(null);
  const [groupAddSearch,  setGroupAddSearch]  = useState({});
  const [groupAddOpen,    setGroupAddOpen]    = useState({});

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const userOrgRole = organizations?.find(o => o.id === currentOrg?.id)?.role?.toUpperCase();
  const isOwnerOrAdmin = ['OWNER', 'ADMIN'].includes(userOrgRole);

  const WS_ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member' };
  const WS_ROLE_COLOR = {
    owner:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    admin:  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    member: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  const PROJ_ROLE_LABEL = {
    PROJECT_MANAGER: 'Project Manager', SCRUM_MASTER: 'Scrum Master',
    TEAM_LEAD: 'Team Lead', DEVELOPER: 'Developer', TESTER: 'Tester', OBSERVER: 'Observer', VIEWER: 'Viewer',
  };
  const PROJ_ROLE_COLOR = {
    PROJECT_MANAGER: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    SCRUM_MASTER:    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    TEAM_LEAD:       'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    DEVELOPER:       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    TESTER:          'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    OBSERVER:        'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400',
    VIEWER:          'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400',
  };
  const STATUS_STYLE = {
    ACTIVE:  'border border-emerald-500 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
    INVITED: 'border border-amber-400 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt)) return '—';
    const now = new Date();
    const diffMs = now - dt;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: diffDays > 365 ? 'numeric' : undefined });
  };

  useEffect(() => {
    if (!currentOrg) return;
    loadWorkspaceMembers();
    loadProjects();
    loadTeams();
    loadGroups();
  }, [currentOrg]);

  const loadWorkspaceMembers = async () => {
    try {
      const [membersRes, pendingRes] = await Promise.all([
        axios.get(getApiUrl(`/organizations/${currentOrg.id}/members`)),
        isOwnerOrAdmin ? axios.get(getApiUrl(`/organizations/${currentOrg.id}/members/pending`)) : Promise.resolve({ data: [] }),
      ]);
      setWsMembers(membersRes.data || []);
      setPendingInvites(pendingRes.data || []);
    } catch (_) {}
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects`));
      const list = res.data || [];
      setProjects(list);
      list.forEach(p => loadProjectMembers(p.id));
      // Load tasks for all projects to power the workspace activity view
      const taskResults = await Promise.all(
        list.map(p =>
          axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${p.id}/tasks`))
            .then(r => (r.data || []).map(t => ({ ...t, project_name: p.name, project_id: p.id })))
            .catch(() => [])
        )
      );
      setAllTasks(taskResults.flat());
    } catch (_) {}
  };

  const loadProjectMembers = async (projectId) => {
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/projects/${projectId}/members`));
      setProjMembers(prev => ({ ...prev, [projectId]: res.data || [] }));
    } catch (_) {}
  };

  const canActOnWsMember = (targetRole) => {
    if (userOrgRole === 'OWNER') return true;
    if (userOrgRole === 'ADMIN') return targetRole?.toUpperCase() !== 'OWNER';
    return false;
  };

  const confirm = (title, message, onConfirm) =>
    setConfirmModal({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmModal(s => ({ ...s, open: false }));

  // ── Teams ──
  const loadTeams = async () => {
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/custom-teams`));
      setTeams(res.data || []);
    } catch (_) {}
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/custom-teams`), {
        name: newTeamName.trim(), description: newTeamDesc.trim() || null,
      });
      setTeams(prev => [...prev, res.data]);
      setNewTeamName(''); setNewTeamDesc(''); setCreateTeamOpen(false);
      toast.success('Team created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create team'); }
  };

  const deleteTeam = (teamId, teamName) => confirm(
    'Delete team',
    `Delete "${teamName}"? This cannot be undone.`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/custom-teams/${teamId}`));
        setTeams(prev => prev.filter(t => t.id !== teamId));
        toast.success('Team deleted');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      closeConfirm();
    }
  );

  const addTeamMember = async (teamId, email) => {
    if (!email?.trim()) return;
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/custom-teams/${teamId}/members`), { user_email: email });
      setTeams(prev => prev.map(t => t.id === teamId ? { ...t, members: [...t.members, res.data] } : t));
      setTeamAddSearch(s => ({ ...s, [teamId]: '' }));
      setTeamAddOpen(s => ({ ...s, [teamId]: false }));
      toast.success('Member added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeTeamMember = (teamId, userId, name) => confirm(
    'Remove member',
    `Remove ${name} from this team?`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/custom-teams/${teamId}/members/${userId}`));
        setTeams(prev => prev.map(t => t.id === teamId ? { ...t, members: t.members.filter(m => m.user_id !== userId) } : t));
        toast.success('Removed');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      closeConfirm();
    }
  );

  // ── Groups ──
  const loadGroups = async () => {
    try {
      const res = await axios.get(getApiUrl(`/organizations/${currentOrg.id}/custom-groups`));
      setGroups(res.data || []);
    } catch (_) {}
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/custom-groups`), {
        name: newGroupName.trim(), description: newGroupDesc.trim() || null,
      });
      setGroups(prev => [...prev, res.data]);
      setNewGroupName(''); setNewGroupDesc(''); setCreateGroupOpen(false);
      toast.success('Group created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create group'); }
  };

  const deleteGroup = (groupId, groupName) => confirm(
    'Delete group',
    `Delete "${groupName}"? This cannot be undone.`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/custom-groups/${groupId}`));
        setGroups(prev => prev.filter(g => g.id !== groupId));
        toast.success('Group deleted');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      closeConfirm();
    }
  );

  const addGroupMember = async (groupId, email) => {
    if (!email?.trim()) return;
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/custom-groups/${groupId}/members`), { user_email: email });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, members: [...g.members, res.data] } : g));
      setGroupAddSearch(s => ({ ...s, [groupId]: '' }));
      setGroupAddOpen(s => ({ ...s, [groupId]: false }));
      toast.success('Member added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeGroupMember = (groupId, userId, name) => confirm(
    'Remove member',
    `Remove ${name} from this group?`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/custom-groups/${groupId}/members/${userId}`));
        setGroups(prev => prev.map(g => g.id === groupId ? { ...g, members: g.members.filter(m => m.user_id !== userId) } : g));
        toast.success('Removed');
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      closeConfirm();
    }
  );

  // Management group = virtual (always current owners + admins)
  const managementGroup = {
    id: '__management__',
    name: 'Management',
    description: 'All workspace Owners and Admins — auto-managed',
    is_default: true,
    members: wsMembers.filter(m => ['owner', 'admin'].includes(m.role?.toLowerCase()))
      .map(m => ({ user_id: m.user_id, full_name: m.full_name, email: m.email })),
  };

  const removeWorkspaceMember = (memberId, memberName) => confirm(
    'Remove workspace member',
    `Remove ${memberName || 'this member'} from the workspace? They will also be removed from all projects.`,
    async () => {
      try {
        await axios.delete(getApiUrl(`/organizations/${currentOrg.id}/members/${memberId}`));
        toast.success('Member removed');
        loadWorkspaceMembers();
        loadProjects();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to remove member');
      }
      closeConfirm();
    }
  );

  const changeWorkspaceRole = async (userId, newRole) => {
    try {
      await axios.patch(getApiUrl(`/organizations/${currentOrg.id}/members/${userId}`), { role: newRole });
      toast.success(`Role changed to ${newRole}`);
      loadWorkspaceMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleWorkspaceInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl(`/organizations/${currentOrg.id}/members`), {
        user_email: inviteEmail.trim(), role: inviteRole,
      });
      toast.success(res.data?.message || 'Invite sent');
      setInviteEmail('');
      setInviteOpen(false);
      loadWorkspaceMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally { setLoading(false); }
  };

  const handleSmartAddToProject = async (e) => {
    e.preventDefault();
    if (!selectedMember || !addProjectId) return;
    setAdding(true);
    try {
      await axios.post(
        getApiUrl(`/organizations/${currentOrg.id}/projects/${addProjectId}/members`),
        { user_email: selectedMember.email, role_in_project: addProjectRole }
      );
      toast.success(`${selectedMember.full_name || selectedMember.email} added to project`);
      const pid = addProjectId;
      setProjSearch(''); setSelectedMember(null); setAddProjectId(''); setAddProjOpen(false);
      loadProjectMembers(pid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to project');
    } finally { setAdding(false); }
  };

  // ── filtered users for search ──
  const allUsers = [
    ...wsMembers.map(m => ({ ...m, status: 'ACTIVE' })),
    ...pendingInvites.map(inv => ({
      user_id: null, full_name: null, email: inv.email,
      role: inv.role, status: 'INVITED', joined_at: inv.invited_at, last_seen: null,
    })),
  ];
  const filteredUsers = userSearch.trim()
    ? allUsers.filter(u =>
        (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
      )
    : allUsers;

  const totalActive  = wsMembers.length;
  const totalInvited = pendingInvites.length;

  return (
    <AppShell header={<DashboardHeader />}>
      <div className="w-full max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Directory</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Find people across your workspace and all projects you manage.
            </p>
          </div>
          {isOwnerOrAdmin && (
            <Button
              onClick={() => setInviteOpen(true)}
              className="bg-[#4B1E87] hover:bg-[#3A1669] text-white self-start sm:self-auto"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Invite users
            </Button>
          )}
        </div>

        {/* ── Stats row ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total users',    value: totalActive + totalInvited },
            { label: 'Active users',   value: totalActive },
            { label: 'Pending invites',value: totalInvited },
            { label: 'Org admins',     value: wsMembers.filter(m => ['owner','admin'].includes(m.role?.toLowerCase())).length },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 shadow-sm">
              <div className="text-2xl font-bold text-foreground">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ═══════════════ USERS ═══════════════ */}
        {section === 'users' && (
          <div className="space-y-4">
            {/* Search + filters row */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input className="pl-9" placeholder="Search by name or email"
                  value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              </div>
            </div>

            {/* Non-admin notice */}
            {!isOwnerOrAdmin && (
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl text-sm text-amber-800 dark:text-amber-300">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                To add or remove workspace members, contact your Admin or Owner.
              </div>
            )}

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm overflow-hidden">
              {/* Scroll wrapper — keeps rounded corners, enables horizontal scroll on mobile */}
              <div className="overflow-x-auto">
                <div className="min-w-[580px]">
                  {/* Table header */}
                  <div className="grid grid-cols-[minmax(160px,1fr)_110px_160px_148px_48px] gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span>User</span>
                    <span>Status</span>
                    <span className="hidden sm:block">Last seen</span>
                    <span>Role</span>
                    <span></span>
                  </div>

                  {/* Rows */}
                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground">
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No users found</p>
                    </div>
                  ) : filteredUsers.map((m, i) => {
                    const isSelf   = m.user_id === user?.id;
                    const canRemove = m.status === 'ACTIVE' && !isSelf && canActOnWsMember(m.role);
                    const initials = (m.full_name || m.email || 'U').charAt(0).toUpperCase();
                    return (
                      <div key={i}
                        className="grid grid-cols-[minmax(160px,1fr)_110px_160px_148px_48px] gap-3 items-center px-5 py-3.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors">
                        {/* User cell */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate text-foreground">
                              {m.full_name || <span className="text-muted-foreground italic">Not registered</span>}
                              {isSelf && <span className="ml-1.5 text-xs text-muted-foreground font-normal">(you)</span>}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                          </div>
                        </div>

                        {/* Status — contained in its own column, never overlaps User */}
                        <div className="shrink-0">
                          <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${STATUS_STYLE[m.status] || ''}`}>
                            {m.status}
                          </span>
                        </div>

                        {/* Last seen */}
                        <div className="hidden sm:block text-sm text-muted-foreground">
                          {m.status === 'INVITED'
                            ? <span className="text-xs">Invited {fmtDate(m.joined_at)}</span>
                            : fmtDate(m.last_seen)}
                        </div>

                        {/* Role */}
                        <div className="shrink-0">
                          {isOwnerOrAdmin && m.status === 'ACTIVE' && !isSelf && m.role?.toLowerCase() !== 'owner' ? (
                            <Select value={m.role?.toLowerCase()} onValueChange={(newRole) => changeWorkspaceRole(m.user_id, newRole)}>
                              <SelectTrigger className="h-7 text-xs w-full border-0 bg-transparent px-2 py-0 focus:ring-1 focus:ring-inset">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WS_ROLE_COLOR[m.role?.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
                                  {WS_ROLE_LABEL[m.role?.toLowerCase()] || m.role}
                                </span>
                              </SelectTrigger>
                              <SelectContent>
                                {userOrgRole === 'OWNER' && <SelectItem value="admin">Admin</SelectItem>}
                                <SelectItem value="member">Member</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${WS_ROLE_COLOR[m.role?.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
                              {WS_ROLE_LABEL[m.role?.toLowerCase()] || m.role}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end shrink-0">
                          {canRemove && (
                            <button
                              onClick={() => removeWorkspaceMember(m.user_id, m.full_name || m.email)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              title="Remove from workspace">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ TEAMS ═══════════════ */}
        {section === 'teams' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{teams.length} {teams.length === 1 ? 'team' : 'teams'}</p>
              {isOwnerOrAdmin && (
                <Button size="sm" onClick={() => setCreateTeamOpen(true)}
                  className="bg-[#4B1E87] hover:bg-[#3A1669] text-white">
                  <Plus className="w-4 h-4 mr-1.5" /> Create team
                </Button>
              )}
            </div>

            {!isOwnerOrAdmin && (
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl text-sm text-amber-800 dark:text-amber-300">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Teams are managed by Admins and Owners. Contact them to be added.
              </div>
            )}

            {teams.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Users2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">No teams yet</p>
                {isOwnerOrAdmin && <p className="text-xs mt-1 opacity-70">Create teams like "AI Team", "Full Stack", "Data Science"</p>}
              </div>
            ) : teams.map(team => {
              const isExpanded = expandedTeam === team.id;
              return (
                <div key={team.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm">
                  {/* Team header */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <button className="flex items-center gap-3 flex-1 text-left"
                      onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                      <div className="w-9 h-9 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-xl flex items-center justify-center shrink-0">
                        <Users2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-foreground">{team.name}</div>
                        {team.description && <div className="text-xs text-muted-foreground truncate">{team.description}</div>}
                      </div>
                      <span className="text-xs text-muted-foreground bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full mr-2">
                        {team.members.length} {team.members.length === 1 ? 'member' : 'members'}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                    </button>
                    {isOwnerOrAdmin && (
                      <button onClick={() => deleteTeam(team.id, team.name)}
                        className="ml-3 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
                        title="Delete team">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-700">
                      {/* Add member search (owner/admin only) */}
                      {isOwnerOrAdmin && (() => {
                        const q = (teamAddSearch[team.id] || '').toLowerCase();
                        const alreadyIn = new Set(team.members.map(m => m.user_id));
                        const matches = q.length >= 1
                          ? wsMembers.filter(m => !alreadyIn.has(m.user_id) && (
                              (m.full_name || '').toLowerCase().includes(q) ||
                              (m.email || '').toLowerCase().includes(q)
                            ))
                          : [];
                        return (
                          <div className="px-5 py-3 bg-slate-50 dark:bg-gray-700/40">
                            <div className="relative">
                              <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              <Input className="pl-9 h-8 text-sm" placeholder="Search by name or email to add…"
                                value={teamAddSearch[team.id] || ''}
                                onChange={e => { setTeamAddSearch(s => ({ ...s, [team.id]: e.target.value })); setTeamAddOpen(s => ({ ...s, [team.id]: true })); }}
                                onFocus={() => setTeamAddOpen(s => ({ ...s, [team.id]: true }))}
                                onBlur={() => setTimeout(() => setTeamAddOpen(s => ({ ...s, [team.id]: false })), 150)}
                                autoComplete="off"
                              />
                              {teamAddOpen[team.id] && q.length >= 1 && (
                                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {matches.length > 0 ? matches.map((m, i) => (
                                    <button key={i} type="button"
                                      onMouseDown={() => addTeamMember(team.id, m.email)}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f3eeff] dark:hover:bg-indigo-900/20 transition-colors text-left">
                                      <div className="w-7 h-7 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                        {(m.full_name || m.email).charAt(0).toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate text-foreground">{m.full_name || 'Unknown'}</div>
                                        <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                                      </div>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${WS_ROLE_COLOR[m.role?.toLowerCase()] || ''}`}>
                                        {WS_ROLE_LABEL[m.role?.toLowerCase()] || m.role}
                                      </span>
                                    </button>
                                  )) : (
                                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">No workspace members match</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {team.members.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">No members yet</p>
                      ) : (
                        <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                          {team.members.map((m, i) => (
                            <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors">
                              <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {(m.full_name || m.email || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate text-foreground">{m.full_name || 'Unknown'}</div>
                                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                              </div>
                              {isOwnerOrAdmin && (
                                <button onClick={() => removeTeamMember(team.id, m.user_id, m.full_name || m.email)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══════════════ GROUPS ═══════════════ */}
        {section === 'groups' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">1 default group · {groups.length} custom {groups.length === 1 ? 'group' : 'groups'}</p>
              {isOwnerOrAdmin && (
                <Button size="sm" onClick={() => setCreateGroupOpen(true)}
                  className="bg-[#4B1E87] hover:bg-[#3A1669] text-white">
                  <Plus className="w-4 h-4 mr-1.5" /> Create group
                </Button>
              )}
            </div>

            {!isOwnerOrAdmin && (
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl text-sm text-amber-800 dark:text-amber-300">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Groups are managed by Admins and Owners. Contact them to be added.
              </div>
            )}

            {/* Default Management group */}
            {[managementGroup, ...groups].map(g => {
              const isDefault  = g.is_default;
              const isExpanded = expandedGroup === g.id;
              return (
                <div key={g.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm">
                  <div className="flex items-center justify-between px-5 py-4">
                    <button className="flex items-center gap-3 flex-1 text-left"
                      onClick={() => setExpandedGroup(isExpanded ? null : g.id)}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isDefault ? 'bg-gradient-to-br from-yellow-400 to-amber-500' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-foreground">{g.name}</span>
                          {isDefault && <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium">DEFAULT</span>}
                        </div>
                        {g.description && <div className="text-xs text-muted-foreground truncate">{g.description}</div>}
                      </div>
                      <span className="text-xs text-muted-foreground bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full mr-2">
                        {g.members.length} {g.members.length === 1 ? 'member' : 'members'}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                    </button>
                    {isOwnerOrAdmin && !isDefault && (
                      <button onClick={() => deleteGroup(g.id, g.name)}
                        className="ml-3 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
                        title="Delete group">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-700">
                      {isOwnerOrAdmin && !isDefault && (() => {
                        const q = (groupAddSearch[g.id] || '').toLowerCase();
                        const alreadyIn = new Set(g.members.map(m => m.user_id));
                        const matches = q.length >= 1
                          ? wsMembers.filter(m => !alreadyIn.has(m.user_id) && (
                              (m.full_name || '').toLowerCase().includes(q) ||
                              (m.email || '').toLowerCase().includes(q)
                            ))
                          : [];
                        return (
                          <div className="px-5 py-3 bg-slate-50 dark:bg-gray-700/40">
                            <div className="relative">
                              <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              <Input className="pl-9 h-8 text-sm" placeholder="Search by name or email to add…"
                                value={groupAddSearch[g.id] || ''}
                                onChange={e => { setGroupAddSearch(s => ({ ...s, [g.id]: e.target.value })); setGroupAddOpen(s => ({ ...s, [g.id]: true })); }}
                                onFocus={() => setGroupAddOpen(s => ({ ...s, [g.id]: true }))}
                                onBlur={() => setTimeout(() => setGroupAddOpen(s => ({ ...s, [g.id]: false })), 150)}
                                autoComplete="off"
                              />
                              {groupAddOpen[g.id] && q.length >= 1 && (
                                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {matches.length > 0 ? matches.map((m, i) => (
                                    <button key={i} type="button"
                                      onMouseDown={() => addGroupMember(g.id, m.email)}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f3eeff] dark:hover:bg-indigo-900/20 transition-colors text-left">
                                      <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                        {(m.full_name || m.email).charAt(0).toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate text-foreground">{m.full_name || 'Unknown'}</div>
                                        <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                                      </div>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${WS_ROLE_COLOR[m.role?.toLowerCase()] || ''}`}>
                                        {WS_ROLE_LABEL[m.role?.toLowerCase()] || m.role}
                                      </span>
                                    </button>
                                  )) : (
                                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">No workspace members match</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {isDefault && (
                        <p className="text-xs text-muted-foreground px-5 py-2 bg-slate-50 dark:bg-gray-700/40 italic">
                          Auto-managed — all workspace Owners and Admins are automatically in this group.
                        </p>
                      )}
                      {g.members.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">No members yet</p>
                      ) : (
                        <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                          {g.members.map((m, i) => (
                            <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors">
                              <div className="w-8 h-8 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {(m.full_name || m.email || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate text-foreground">{m.full_name || 'Unknown'}</div>
                                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                              </div>
                              {m.user_id === user?.id && <span className="text-xs text-muted-foreground shrink-0">(you)</span>}
                              {isOwnerOrAdmin && !isDefault && (
                                <button onClick={() => removeGroupMember(g.id, m.user_id, m.full_name || m.email)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══════════════ ACTIVITY ═══════════════ */}
        {section === 'activity' && (() => {
          const isOnline = (m) => onlineUserIds.has(m.user_id) || m.is_online;

          // Members & tasks scoped to the selected project
          const selectedProject   = projects.find(p => p.id === activityProjectId);
          const projectMemberList = activityProjectId ? (projMembers[activityProjectId] || []) : [];
          // wsMembers enriched with their project role
          const activityMembers   = activityProjectId
            ? wsMembers
                .filter(m => projectMemberList.some(pm => pm.user_id === m.user_id))
                .map(m => ({
                  ...m,
                  projectRole: (projectMemberList.find(pm => pm.user_id === m.user_id) || {}).role_in_project || '',
                }))
            : [];
          const projectTasks = activityProjectId
            ? allTasks.filter(t => t.project_id === activityProjectId)
            : [];

          const onlineCount = activityMembers.filter(isOnline).length;

          return (
            <div className="space-y-5">
              {/* Project selector */}
              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Select Project</label>
                <Select value={activityProjectId} onValueChange={v => setActivityProjectId(v)}>
                  <SelectTrigger className="w-full sm:w-72">
                    <SelectValue placeholder="Choose a project to view activity…" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!activityProjectId ? (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Select a project above to view team activity</p>
                  <p className="text-xs mt-1 opacity-70">See what each member is working on in real time</p>
                </div>
              ) : (
                <>
                  {/* Summary bar */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Online now',       value: onlineCount,                                                     color: 'text-emerald-600' },
                      { label: 'Active tasks',     value: projectTasks.filter(t => t.status === 'in_progress').length,     color: 'text-[#4B1E87]'  },
                      { label: 'In review',        value: projectTasks.filter(t => t.status === 'in_review').length,       color: 'text-amber-600'   },
                      { label: 'Done this sprint', value: projectTasks.filter(t => t.status === 'approved').length,        color: 'text-violet-600'  },
                    ].map(s => (
                      <div key={s.label} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 shadow-sm">
                        <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Per-member cards */}
                  {activityMembers.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground text-sm">No members in this project yet.</div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {activityMembers.map((member) => {
                        const memberTasks = projectTasks.filter(t => t.assignee_id === member.user_id);
                        const inProgress  = memberTasks.filter(t => t.status === 'in_progress');
                        const inReview    = memberTasks.filter(t => t.status === 'in_review');
                        const todo        = memberTasks.filter(t => t.status === 'todo');
                        const done        = memberTasks.filter(t => t.status === 'approved');
                        const activeTask  = inProgress[0] || inReview[0] || null;
                        const online      = isOnline(member);
                        const initials    = (member.full_name || member.email || '?').charAt(0).toUpperCase();
                        const projRole    = member.projectRole?.toUpperCase().replace('_', ' ');

                        return (
                          <div key={member.user_id} className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-3 hover:shadow-md transition-shadow">
                            {/* Header */}
                            <div className="flex items-center gap-3">
                              <div className="relative shrink-0">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4B1E87] to-[#693CB4] flex items-center justify-center text-white text-sm font-bold">
                                  {initials}
                                </div>
                                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${online ? 'bg-emerald-400' : 'bg-gray-300 dark:bg-gray-600'}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{member.full_name || member.email}</p>
                                {projRole && (
                                  <p className="text-[10px] font-medium text-[#4B1E87] dark:text-indigo-400 truncate">{projRole}</p>
                                )}
                              </div>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${online ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                                {online ? 'Online' : member.last_seen ? `Seen ${fmtDate(member.last_seen)}` : 'Offline'}
                              </span>
                            </div>

                            {/* Active task */}
                            {activeTask ? (
                              <div className={`rounded-xl p-3 text-sm space-y-1 ${inProgress.length > 0 ? 'bg-[#f3eeff] dark:bg-indigo-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                                  {inProgress.length > 0
                                    ? <><Zap className="w-3 h-3 text-[#4B1E87]" /><span className="text-[#4B1E87]">In Progress</span></>
                                    : <><Clock className="w-3 h-3 text-amber-600" /><span className="text-amber-600">In Review</span></>}
                                </div>
                                <p className="font-medium text-gray-800 dark:text-gray-100 leading-snug line-clamp-2">{activeTask.title}</p>
                                {activeTask.due_date && (
                                  <p className={`text-xs ${new Date(activeTask.due_date) < new Date() ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
                                    Due {new Date(activeTask.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="rounded-xl p-3 bg-gray-50 dark:bg-gray-800 text-xs text-muted-foreground">
                                No active task
                              </div>
                            )}

                            {/* Task counts */}
                            <div className="grid grid-cols-4 gap-1 text-center">
                              {[
                                { label: 'To Do',  count: todo.length,       color: 'text-gray-500'    },
                                { label: 'Active', count: inProgress.length, color: 'text-[#4B1E87]'  },
                                { label: 'Review', count: inReview.length,   color: 'text-amber-600'   },
                                { label: 'Done',   count: done.length,       color: 'text-emerald-600' },
                              ].map(({ label, count, color }) => (
                                <div key={label} className="rounded-lg bg-gray-50 dark:bg-gray-800 py-2">
                                  <div className={`text-base font-semibold ${color}`}>{count}</div>
                                  <div className="text-[10px] text-muted-foreground">{label}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ═══════════════ INVITE MODAL ═══════════════ */}
        {inviteOpen && isOwnerOrAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) setInviteOpen(false); }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Invite users</h2>
                <button onClick={() => setInviteOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                They'll receive an email invite and automatically join when they register.
              </p>
              <form onSubmit={handleWorkspaceInvite} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Email address</label>
                  <Input type="email" placeholder="teammate@company.com"
                    value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required autoFocus />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Workspace role</label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {userOrgRole === 'OWNER' && <SelectItem value="admin">Admin — can manage projects &amp; members</SelectItem>}
                      <SelectItem value="member">Member — assigned to projects by Admin/Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-1">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setInviteOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-[#4B1E87] hover:bg-[#3A1669] text-white">
                    {loading ? 'Sending…' : 'Send invite'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ═══════════════ ADD TO PROJECT MODAL ═══════════════ */}
        {addProjOpen && isOwnerOrAdmin && (() => {
          const query = projSearch.toLowerCase();
          const wsMatches = query.length >= 1
            ? wsMembers.filter(m =>
                (m.full_name || '').toLowerCase().includes(query) ||
                (m.email || '').toLowerCase().includes(query))
            : [];
          const notInWs  = projSearch.trim().length > 0 && !selectedMember && wsMatches.length === 0;
          const canSubmit = !!selectedMember && !!addProjectId;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
              onClick={e => { if (e.target === e.currentTarget) { setAddProjOpen(false); setSelectedMember(null); setProjSearch(''); } }}>
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">Add member to project</h2>
                  <button onClick={() => { setAddProjOpen(false); setSelectedMember(null); setProjSearch(''); }}
                    className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSmartAddToProject} className="space-y-4">
                  {/* Person */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Person</label>
                    {selectedMember ? (
                      <div className="flex items-center gap-3 p-3 bg-[#f3eeff] dark:bg-indigo-900/20 border border-[#d4b8f0] dark:border-[#4B1E87] rounded-xl">
                        <div className="w-9 h-9 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {(selectedMember.full_name || selectedMember.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{selectedMember.full_name || selectedMember.email}</div>
                          <div className="text-xs text-muted-foreground truncate">{selectedMember.email}</div>
                        </div>
                        <button type="button" onClick={() => { setSelectedMember(null); setProjSearch(''); }}
                          className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input className="pl-9" placeholder="Search workspace members…"
                          value={projSearch}
                          onChange={e => { setProjSearch(e.target.value); setProjSearchOpen(true); }}
                          onFocus={() => setProjSearchOpen(true)}
                          onBlur={() => setTimeout(() => setProjSearchOpen(false), 150)}
                          autoComplete="off" autoFocus />
                        {projSearchOpen && projSearch.length >= 1 && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                            {wsMatches.length > 0 ? wsMatches.map((m, i) => (
                              <button key={i} type="button"
                                onMouseDown={() => { setSelectedMember(m); setProjSearch(''); setProjSearchOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f3eeff] dark:hover:bg-indigo-900/20 transition-colors text-left">
                                <div className="w-8 h-8 bg-gradient-to-br from-[#4B1E87] to-[#693CB4] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                  {(m.full_name || m.email).charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{m.full_name || 'Unknown'}</div>
                                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                                </div>
                              </button>
                            )) : (
                              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No matches</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {notInWs && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Not in workspace. Invite them via <button type="button" className="underline font-semibold" onClick={() => { setAddProjOpen(false); setInviteOpen(true); }}>Invite users</button> first.
                      </p>
                    )}
                  </div>
                  {/* Project */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Project</label>
                    <Select value={addProjectId} onValueChange={setAddProjectId}>
                      <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Role */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Project role</label>
                    <Select value={addProjectRole} onValueChange={setAddProjectRole}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
                        <SelectItem value="SCRUM_MASTER">Scrum Master</SelectItem>
                        <SelectItem value="TEAM_LEAD">Team Lead</SelectItem>
                        <SelectItem value="DEVELOPER">Developer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <Button type="button" variant="outline" className="flex-1"
                      onClick={() => { setAddProjOpen(false); setSelectedMember(null); setProjSearch(''); }}>Cancel</Button>
                    <Button type="submit" disabled={adding || !canSubmit}
                      className="flex-1 bg-[#4B1E87] hover:bg-[#3A1669] text-white">
                      {adding ? 'Adding…' : 'Add to project'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}

        {/* ═══════════════ CREATE TEAM MODAL ═══════════════ */}
        {createTeamOpen && isOwnerOrAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) setCreateTeamOpen(false); }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Create team</h2>
                <button onClick={() => setCreateTeamOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Team name</label>
                  <Input placeholder="e.g. AI Team, Full Stack, Data Science…"
                    value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createTeam()} autoFocus />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Input placeholder="What does this team work on?"
                    value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setCreateTeamOpen(false)}>Cancel</Button>
                <Button className="flex-1 bg-[#4B1E87] hover:bg-[#3A1669] text-white" onClick={createTeam} disabled={!newTeamName.trim()}>
                  Create team
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ CREATE GROUP MODAL ═══════════════ */}
        {createGroupOpen && isOwnerOrAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) setCreateGroupOpen(false); }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Create group</h2>
                <button onClick={() => setCreateGroupOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Group name</label>
                  <Input placeholder="e.g. Business Analysts, DevOps, QA…"
                    value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createGroup()} autoFocus />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Input placeholder="What is this group for?"
                    value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
                <Button className="flex-1 bg-[#4B1E87] hover:bg-[#3A1669] text-white" onClick={createGroup} disabled={!newGroupName.trim()}>
                  Create group
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ CONFIRM MODAL ═══════════════ */}
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Confirm delete"
          onConfirm={confirmModal.onConfirm}
          onCancel={closeConfirm}
        />

      </div>
    </AppShell>
  );
};

// ==================== FEATURES PAGE ====================
// ==================== PRICING PAGE ====================
const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const FREE_PLAN_MEMBER_LIMIT = 10;

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "/month",
      range: `Up to ${FREE_PLAN_MEMBER_LIMIT} members`,
      desc: "Everything your team needs to plan, track, and ship work together — free, no trial, no credit card.",
      color: "border-[#4B1E87]",
      badge: "Current",
      cta: "Get Started",
      features: [
        `Up to ${FREE_PLAN_MEMBER_LIMIT} members`,
        "Unlimited projects & tasks",
        "Kanban board & sprint planning",
        "All project roles (PM, Scrum Master, Team Lead, Developer, Tester, Viewer)",
        "AI Sprint Planning",
        "AI Goal → Task Breakdown",
        "AI Comment Assistant",
        "Analytics dashboard",
        "Real-time collaboration",
        "Two-stage approval workflow",
        "Audit log (Admins & Owners)",
        "CSV export",
        "Email support",
      ],
      missing: [],
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      range: `More than ${FREE_PLAN_MEMBER_LIMIT} members`,
      desc: "Growing past 10 members? Talk to us and we'll put together a plan for your team.",
      color: "border-slate-700",
      badge: null,
      cta: "Contact Sales",
      features: [
        "Unlimited members",
        "Everything in Free",
        "Priority support",
      ],
      missing: [],
    },
  ];

  const faqs = [
    { q: "Is there a free plan?", a: `Yes — SyncWork is free for teams of up to ${FREE_PLAN_MEMBER_LIMIT} members. No trial period, no credit card required to get started.` },
    { q: "What happens when my team grows past 10 members?", a: "Everything keeps working as normal for your existing members. To add more than 10, reach out via Contact Sales and we'll work out a plan sized for your team." },
    { q: "What is the two-stage approval?", a: "Team Lead does the first review (In Review → Approved). Project Manager does the final sign-off (Approved → Done). Every approval logs the approver's name and timestamp." },
    { q: "Is my data isolated from other companies?", a: "Yes. Each workspace is completely isolated. No org can see another org's data — enforced at the database query level." },
    { q: "How do I contact support?", a: "Email us at info@syncwork.pro — we respond within 24 hours." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <MarketingNav activePage="/pricing" />

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-[#0d0618] via-[#1a0833] to-[#2a0f52] text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Simple, Honest Pricing</h1>
          <p className="text-slate-300 text-lg mb-2">Free for teams up to 10 members. No hidden fees, no credit card required.</p>
          <p className="text-indigo-300 text-sm">Growing past 10? Talk to us — every feature is included either way.</p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {plans.map((p, i) => (
              <div key={i} className={`relative bg-white rounded-3xl border-2 ${p.color} p-7 shadow-sm flex flex-col ${i === 1 ? 'bg-[#f3eeff]/40' : ''}`}>
                {p.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="text-white text-xs font-bold px-4 py-1.5 rounded-full bg-emerald-500">{p.badge}</span>
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-0.5">{p.name}</h3>
                  <span className="text-xs font-medium text-[#4B1E87] bg-[#f3eeff] px-2 py-0.5 rounded-full">{p.range}</span>
                  <div className="flex items-baseline gap-1 mt-3 mb-2">
                    <span className="text-4xl font-bold text-gray-900">{p.price}</span>
                    {p.period && <span className="text-gray-400 text-sm">{p.period}</span>}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{p.desc}</p>
                </div>

                <Button
                  onClick={() => navigate(p.name === 'Enterprise' ? '/demo' : '/auth?tab=register')}
                  className={`w-full mb-5 ${i === 1 ? 'bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-white' : ''}`}
                  variant={i === 1 ? 'default' : 'outline'}>
                  {p.cta}
                </Button>

                <div className="space-y-2 flex-1">
                  {p.features.map((f, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      {f}
                    </div>
                  ))}
                  {p.missing.map((f, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm text-gray-400">
                      <X className="w-4 h-4 shrink-0 mt-0.5" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-400 mt-8">
            Need help choosing? Email us at{' '}
            <a href="mailto:info@syncwork.pro" className="text-[#4B1E87] hover:underline">info@syncwork.pro</a>
          </p>
        </div>
      </section>

      {/* Feature comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Full Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm max-w-2xl mx-auto">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 pr-6 font-semibold text-gray-600 w-2/3">Feature</th>
                  <th className="text-center py-3 px-3 font-semibold text-[#4B1E87]">Free<br/><span className="font-normal text-xs text-gray-400">Up to {FREE_PLAN_MEMBER_LIMIT} members</span></th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-700">Enterprise<br/><span className="font-normal text-xs text-gray-400">{FREE_PLAN_MEMBER_LIMIT}+ members</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Price / month",             "$0",                       "Custom"],
                  ["Members",                   String(FREE_PLAN_MEMBER_LIMIT), "Unlimited"],
                  ["Projects & tasks",          "∞",                        "∞"],
                  ["Kanban board & sprints",    "✓",                        "✓"],
                  ["All project roles",         "✓",                        "✓"],
                  ["Real-time collaboration",   "✓",                        "✓"],
                  ["AI Sprint Planning",        "✓",                        "✓"],
                  ["AI Task Breakdown",         "✓",                        "✓"],
                  ["AI Comment Assist",         "✓",                        "✓"],
                  ["Analytics dashboard",       "✓",                        "✓"],
                  ["CSV export",                "✓",                        "✓"],
                  ["Two-stage approval",        "✓",                        "✓"],
                  ["Audit log (Admins/Owners)", "✓",                        "✓"],
                  ["Support",                   "Email",                    "Priority"],
                ].map(([feature, f, e], i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-3 pr-6 text-gray-700">{feature}</td>
                    {[f, e].map((val, j) => (
                      <td key={j} className={`py-3 px-3 text-center ${val === "✓" ? "text-emerald-600 font-bold" : val === "—" ? "text-gray-300" : "text-gray-700 font-medium"}`}>
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{f.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#4B1E87] to-[#693CB4] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Bring Order to Your Team?</h2>
          <p className="text-indigo-100 mb-8">Set up your workspace in minutes. Free for up to {FREE_PLAN_MEMBER_LIMIT} members — no credit card required.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/auth?tab=register')} className="bg-white text-[#4B1E87] hover:bg-[#f3eeff] font-bold px-8">
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button size="lg" onClick={() => navigate('/demo')} className="bg-[#3A1669] hover:bg-[#2d1060] text-white border border-[#4B1E87] px-8">
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center">
            <img src="/syncwork.svg" alt="SyncWork" className="h-12 w-auto object-contain brightness-0 invert" style={{maxWidth:'180px'}} />
          </div>
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/features" className="hover:text-white">Features</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/demo" className="hover:text-white">Contact</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-sm">© 2026 SyncWork. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const FeaturesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const mainFeatures = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "AI Sprint Planning Assistant",
      description: "Intelligent sprint planning that analyzes your backlog, team capacity, and dependencies to create optimal 2-week sprint plans. Our AI considers task complexity, priority, and team velocity to maximize productivity.",
      color: "from-blue-500 to-cyan-400",
      benefits: [
        "Automatic task prioritization",
        "Capacity optimization",
        "Risk identification",
        "Dependency management"
      ]
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Goal → Task Breakdown",
      description: "Transform high-level goals into actionable engineering tasks with estimates, dependencies, and clear acceptance criteria. Perfect for breaking down epics and user stories into sprint-ready tasks.",
      color: "from-emerald-500 to-teal-400",
      benefits: [
        "Intelligent task estimation",
        "Automatic dependency mapping",
        "Acceptance criteria generation",
        "Tag and categorization"
      ]
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Smart Comment Assistant",
      description: "Convert vague comments and requests into structured action items with owners, deadlines, and success metrics. Never lose track of important feedback or requests.",
      color: "from-violet-500 to-purple-400",
      benefits: [
        "Action item extraction",
        "Priority assignment",
        "Owner suggestions",
        "Deadline recommendations"
      ]
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Advanced Analytics Dashboard",
      description: "Track team velocity, AI cost savings, and activity patterns with comprehensive analytics. Export data to CSV for deeper analysis and reporting.",
      color: "from-orange-500 to-red-400",
      benefits: [
        "Velocity tracking",
        "AI savings metrics",
        "Activity heatmaps",
        "CSV export"
      ]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-Tenant Architecture",
      description: "Enterprise-grade multi-tenant system with role-based access control. Secure organization isolation with Owner, Admin, and Member roles plus project-level permissions.",
      color: "from-indigo-500 to-blue-400",
      benefits: [
        "Organization isolation",
        "Role-based access control",
        "Project-level permissions",
        "Member management"
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "AI Result Caching",
      description: "Smart caching system reduces AI API costs by up to 50% while maintaining performance. Similar requests are served from cache instantly.",
      color: "from-yellow-500 to-orange-400",
      benefits: [
        "Cost reduction",
        "Faster responses",
        "Automatic cache management",
        "Force regeneration option"
      ]
    }
  ];

  const securityFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "JWT Authentication",
      description: "Secure token-based authentication with refresh tokens and automatic rotation"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Role-Based Access Control",
      description: "Granular permissions at organization and project levels"
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Tenant Isolation",
      description: "Complete data isolation between organizations"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Industry-standard security practices and encryption"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <MarketingNav activePage="/features" />

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-[#0d0618] via-[#1a0833] to-[#2a0f52] text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Every Feature Your Team Needs
          </h1>
          <p className="text-slate-300 text-lg">AI automation, enterprise RBAC, real-time collaboration, and structured approvals — all in one place.</p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {mainFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border border-gray-100">
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg font-bold text-gray-900 mb-2">{feature.title}</CardTitle>
                  <CardDescription className="text-gray-500 text-sm leading-relaxed">{feature.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security strip */}
      <section className="py-16 bg-gradient-to-r from-[#4B1E87] to-[#693CB4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-10">
          <h2 className="text-3xl font-bold text-white mb-3">Enterprise-Grade Security</h2>
          <p className="text-indigo-100">Every permission enforced at the API. No trust at the UI level.</p>
        </div>
        <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {securityFeatures.map((f, i) => (
            <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-5 text-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 text-white">{f.icon}</div>
              <h3 className="font-semibold text-white text-sm mb-1">{f.title}</h3>
              <p className="text-indigo-200 text-xs">{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-slate-300 mb-8">Per-user pricing. Enterprise-ready from day one.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/auth?tab=register')} className="bg-gradient-to-r from-[#4B1E87] to-[#693CB4] px-8">
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button size="lg" onClick={() => navigate('/pricing')} className="bg-white text-[#4B1E87] hover:bg-[#f3eeff] font-semibold px-8">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center">
              <img src="/footer.svg" alt="SyncWork" className="h-20 w-auto object-contain" style={{maxWidth:'300px'}} />
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <Link to="/features" className="hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/demo" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/auth" className="hover:text-white transition-colors">Sign In</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
            <p className="text-sm">© 2026 SyncWork. All rights reserved. · <a href="mailto:info@syncwork.pro" className="hover:text-white transition-colors">info@syncwork.pro</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== DEMO/CONTACT PAGE ====================
const DemoContactPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(getApiUrl('/contact'), {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
      });
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({ name:'', email:'', company:'', subject:'', message:'' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message. Try info@syncwork.pro directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <MarketingNav activePage="/demo" />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Request a <span className="bg-gradient-to-r from-[#4B1E87] via-[#693CB4] to-[#875AC3] bg-clip-text text-transparent">Demo</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600">
              See SyncWork in action or get in touch with our team. We're here to help!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-[#d4b8f0]">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Why Contact Us?</h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-5">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      Schedule a personalized demo
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      Get pricing information
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      Technical support
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      Enterprise inquiries
                    </li>
                  </ul>
                  <div className="border-t border-[#d4b8f0] pt-4">
                    <p className="text-xs text-gray-500 mb-1">Or email us directly</p>
                    <a href="mailto:info@syncwork.pro"
                      className="text-sm font-semibold text-[#4B1E87] hover:underline break-all">
                      info@syncwork.pro
                    </a>
                    <p className="text-xs text-gray-400 mt-1">We reply within 24 hours</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Us</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you within 24 hours
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                      <p className="text-gray-600 mb-6">
                        We've received your message and will get back to you soon.
                      </p>
                      <Button onClick={() => setSubmitted(false)} variant="outline">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@company.com"
                            required
                            className="text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Company
                        </label>
                        <Input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company Name"
                          className="text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject <span className="text-red-500">*</span>
                        </label>
                        <Select name="subject" value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})} required>
                          <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Select a subject" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="demo">Request a Demo</SelectItem>
                            <SelectItem value="pricing">Pricing Inquiry</SelectItem>
                            <SelectItem value="support">Technical Support</SelectItem>
                            <SelectItem value="enterprise">Enterprise Sales</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help you..."
                          required
                          className="min-h-[120px] text-sm resize-y"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-[#4B1E87] to-[#693CB4] hover:from-[#3A1669] hover:to-[#5a2da5]"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== ONBOARDING WIZARD ====================
const ONBOARDING_KEY = 'syncwork_onboarding_done';

const OnboardingWizard = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const steps = [
    {
      icon: '👋',
      title: 'Welcome to SyncWork!',
      body: 'Your AI-powered project management workspace is ready. Let\'s take 60 seconds to show you around.',
    },
    {
      icon: '📁',
      title: 'Create your first project',
      body: 'Go to Projects → New Project. Give it a name and description. You can have unlimited projects per workspace.',
    },
    {
      icon: '👥',
      title: 'Invite your team',
      body: 'Open Directory → Invite Member. Each person gets a role: Owner, Admin, or Member. You assign their project role (Developer, Team Lead, PM) per project.',
    },
    {
      icon: '📋',
      title: 'Add tasks to the board',
      body: 'Inside a project, click the Kanban board. Use the "+" on any column to create tasks, or go to Assign Tasks for the full form with due dates, estimates, and more.',
    },
    {
      icon: '🤖',
      title: 'Let AI plan your sprint',
      body: 'Once you have tasks, go to AI Tools inside your project. Click "Generate Sprint Plan" and the AI will distribute your backlog across sprints automatically.',
    },
    {
      icon: '✅',
      title: 'You\'re all set!',
      body: 'Explore the sidebar: My Tasks shows everything assigned to you, Approvals shows tasks awaiting your sign-off, and Analytics tracks your team\'s velocity.',
    },
  ];
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const finish = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    onDone();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-1 bg-[#4B1E87] transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="p-8 text-center space-y-4">
          <div className="text-5xl">{current.icon}</div>
          <h2 className="text-xl font-bold text-gray-900">{current.title}</h2>
          <p className="text-sm text-gray-500 leading-relaxed">{current.body}</p>
          <div className="flex items-center justify-between pt-2">
            <button onClick={finish} className="text-xs text-gray-400 hover:text-gray-600">Skip tour</button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Back</button>
              )}
              {isLast ? (
                <button onClick={finish} className="px-5 py-2 text-sm bg-[#4B1E87] hover:bg-[#3A1669] text-white rounded-lg font-medium">
                  Get started!
                </button>
              ) : (
                <button onClick={() => setStep(s => s + 1)} className="px-5 py-2 text-sm bg-[#4B1E87] hover:bg-[#3A1669] text-white rounded-lg font-medium">
                  Next →
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-center gap-1.5 pt-1">
            {steps.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? 'bg-[#4B1E87]' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/guide" element={<UserGuidePage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/create-workspace" element={<CreateWorkspacePage />} />
                  {/* /create-workspace is intentionally outside ProtectedRoute — logged-in users with no org land here */}
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/demo" element={<DemoContactPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/my-tasks" element={
                    <ProtectedRoute><MyTasksPage /></ProtectedRoute>
                  } />
                  <Route path="/approvals" element={
                    <ProtectedRoute><PendingApprovalsPage /></ProtectedRoute>
                  } />
                  <Route path="/audit-log" element={
                    <ProtectedRoute><AuditLogPage /></ProtectedRoute>
                  } />
                  <Route path="/analytics" element={
                    <ProtectedRoute>
                      <AnalyticsLayout />
                    </ProtectedRoute>
                  } />
                  <Route path="/projects" element={
                    <ProtectedRoute>
                      <ProjectsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/projects/:projectId" element={<Navigate to="board" replace />} />
                  <Route path="/projects/:projectId/members" element={
                    <ProtectedRoute><ProjectDetailPage /></ProtectedRoute>
                  } />
                  <Route path="/projects/:projectId/:section" element={
                    <ProtectedRoute><ProjectWorkPage /></ProtectedRoute>
                  } />
                  <Route path="/team" element={<Navigate to="/team/users" replace />} />
                  <Route path="/team/:section" element={
                    <ProtectedRoute>
                      <TeamPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/enterprise" element={
                    <ProtectedRoute>
                      <EnterpriseLayout />
                    </ProtectedRoute>
                  } />
                  <Route path="/billing" element={
                    <ProtectedRoute>
                      <BillingPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/integrations" element={
                    <ProtectedRoute>
                      <IntegrationsLayout />
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <SettingsLayout />
                    </ProtectedRoute>
                  } />
                  <Route path="/help" element={
                    <ProtectedRoute>
                      <HelpSupportPage />
                    </ProtectedRoute>
                  } />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AppErrorBoundary>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
